// src/pages/nafed/pacs/NafedPacsDashboard.js
import React, { useEffect, useMemo, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import {
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { auth, db, storage } from "../../../firebaseConfig";
import PacsDocumentCenter from "./PacsDocumentCenter";
import PacsMouSigning from "./PacsMouSigning";
import PacsLandLeaseCenter from "./PacsLandLeaseCenter";
import PacsRegistrarFileCenter from "./PacsRegistrarFileCenter";
import PacsSubsidyFirstInstallment from "./PacsSubsidyFirstInstallment";
import { downloadOfficialPdf } from "./officialPdf";
import {
  buildFinalSignedDprPdf,
  downloadFile,
} from "../pmc/NafedDprGenerator";

// Original NAFED logo is embedded in this file. No public image path is required.
const ORIGINAL_NAFED_LOGO =
  "data:image/jpeg;base64,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";

const MODULES = [
  [
    "Project Overview",
    "OV",
    "View application status, assigned PMC and project progress",
  ],
  [
    "New Application",
    "AP",
    "Start a new application or view and edit the final submitted application",
  ],
  [
    "Land & Lease",
    "LL",
    "Enter basic land details or generate and upload the registered 20-year lease deed",
  ],
  ["Documents", "DC", "Download PACS, PMC, technical and tender documents"],
  [
    "DPR",
    "DP",
    "View and download the final bank-ready DPR with verified calculations",
  ],
  [
    "Committee Meeting",
    "CM",
    "Download five PDFs, upload manually signed copies, attendance, GPS and photographs",
  ],
  [
    "District Registrar File",
    "RF",
    "Create the complete serial-wise Registrar submission ZIP",
  ],
  ["MOU", "MO", "Complete the secure PMC-PACS two-party MOU process"],
  ["Tender", "TN", "View tender publication, bids and award status"],
  ["Construction", "CN", "Monitor execution, milestones and site progress"],
  [
    "Subsidy – First Instalment",
    "SB",
    "Work-start gated subsidy form, checklist and complete ZIP",
  ],
  ["Bills", "BL", "View RA bills, verification and approval status"],
  ["Payments", "PY", "Track released, pending and approved payments"],
  [
    "Help Center",
    "HP",
    "Contact only the State Branch allocated PMC support team",
  ],
];
const STEPS = [
  "Application",
  "Documents",
  "PMC Verification",
  "MOU Signing",
  "Registrar / DCDC",
  "Tender",
  "Construction",
];
const clean = (value) => String(value || "").trim();
const upper = (value) => clean(value).toUpperCase();
const REMOVED_STATUSES = new Set([
  "DELETED",
  "REMOVED",
  "ARCHIVED",
  "TRASHED",
  "CANCELLED",
  "CANCELED",
]);
const isRemovedRecord = (data = {}) =>
  data.isDeleted === true ||
  data.deleted === true ||
  data.isArchived === true ||
  Boolean(data.deletedAt || data.archivedAt || data.removedAt) ||
  [data.status, data.registrationStatus, data.applicationStatus].some(
    (value) => REMOVED_STATUSES.has(upper(value)),
  );
const isNafedProject = (data = {}) =>
  data.nafedProject === true ||
  upper(data.projectSource) === "NAFED" ||
  upper(data.applicationPortal) === "NAFED_PACS_APPLY";
const timestampMs = (value) => {
  if (!value) return 0;
  const date = value?.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};
const newestRecord = (records = []) =>
  [...records].sort(
    (a, b) =>
      timestampMs(b.updatedAt || b.createdAt || b.appliedAt) -
      timestampMs(a.updatedAt || a.createdAt || a.appliedAt),
  )[0] || null;
const safePhoneHref = (value) => clean(value).replace(/[^+\d]/g, "");
const numberText = (value) => Number(value || 0).toLocaleString("en-IN");
const humanizeStatus = (value, fallback = "Not available") => {
  const text = clean(value || fallback)
    .replaceAll("_", " ")
    .toLowerCase();
  return text.replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const formatDate = (value) => {
  if (!value) return "Not available";
  const date = value?.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

function deriveWorkflow(project = {}, pmcAllocated = false) {
  let stage = project.id ? "Application Submitted" : "Application Pending";
  let progress = project.id ? 12 : 0;
  let step = project.id ? 1 : 0;
  if (
    project.documentStage ||
    project.generatedDocuments ||
    project.signedPacsDocuments
  ) {
    stage = "PACS Documents in Progress";
    progress = 28;
    step = 1;
  }
  if (
    ["SUBMITTED_TO_PMC", "PMC_UNDER_REVIEW", "PMC_QUERY_RAISED"].includes(
      project.documentStage,
    )
  ) {
    stage = "PMC Document Verification";
    progress = 42;
    step = 2;
  }
  if (
    project.pmcVerificationStatus === "APPROVED" ||
    project.documentStage === "PMC_APPROVED" ||
    project.documentStage === "MOU_READY"
  ) {
    stage = "MOU Execution";
    progress = 55;
    step = 3;
  }
  if (
    /SIGNED|EXECUTED|BRANCH/i.test(
      String(project.mouStatus || project.documentStage || ""),
    )
  ) {
    stage = "PMC–PACS MOU Execution";
    progress = 64;
    step = 3;
  }
  if (
    /FULLY_EXECUTED/i.test(
      String(project.mouStatus || project.documentStage || ""),
    )
  ) {
    stage = "PMC–PACS MOU Fully Executed";
    progress = 70;
    step = 3;
  }
  const registrarStage = upper(
    project.registrarWorkflowStage ||
      project.registrarFileStage ||
      project.registrarReviewStatus,
  );
  const districtRegistrarStage = upper(
    project.registrarWorkflow?.district?.status || project.districtRegistrarStatus,
  );
  const stateRegistrarStage = upper(
    project.registrarWorkflow?.state?.status || project.stateRegistrarStatus,
  );
  if (project.registrarPackage?.url || registrarStage) {
    stage = "Registrar Package Ready for PMC";
    progress = 72;
    step = 4;
  }
  if (/PMC_FILE_FORWARDED_TO_DISTRICT|COMPLIANCE_RESUBMITTED_TO_DISTRICT/.test(registrarStage) || project.pmcRegistrarFileForwardedToDistrict) {
    stage = "District Registrar Review";
    progress = 75;
    step = 4;
  }
  if (/QUERY/.test(districtRegistrarStage)) {
    stage = districtRegistrarStage.includes("STATE_QUERY")
      ? "State Query Compliance with PMC / District"
      : "District Registrar Query with PMC";
    progress = 76;
    step = 4;
  }
  if (/FORWARDED_TO_STATE/.test(districtRegistrarStage)) {
    stage = "State Registrar Review";
    progress = 79;
    step = 4;
  }
  if (/QUERY/.test(stateRegistrarStage)) {
    stage = "State Registrar Query under Compliance";
    progress = 80;
    step = 4;
  }
  if (project.stateProjectPermissionGranted || /APPROVED_FOR_DCDC|PERMISSION_GRANTED/.test(stateRegistrarStage)) {
    stage = "State Approved · DCDC Processing";
    progress = 83;
    step = 4;
  }
  if (project.dcdcStatus) {
    stage = `DCDC ${humanizeStatus(project.dcdcStatus)}`;
    progress = upper(project.dcdcStatus) === "APPROVED" ? 86 : 84;
    step = 4;
  }
  if (project.tenderId || project.tenderNumber || project.tenderPublishedAt) {
    stage = "Tender Process";
    progress = 78;
    step = 5;
  }
  if (project.contractorId || project.loaPdfUrl || project.loaGenerateDate) {
    stage = "Contract Awarded";
    progress = 85;
    step = 6;
  }
  if (
    project.workStartedAt ||
    project.workStartDate ||
    project.workStatus === "STARTED"
  ) {
    stage = "Construction in Progress";
    progress = Math.max(
      86,
      Number(project.constructionProgress || project.progress || 0),
    );
    step = 6;
  }
  if (
    /COMPLETED|FULLY_COMPLETED/i.test(
      String(project.workStatus || project.status || ""),
    ) ||
    project.completionCertificateUrl
  ) {
    stage = "Project Completed";
    progress = 100;
    step = 6;
  }
  if (!pmcAllocated && project.id && progress > 28)
    stage += " · PMC Allocation Pending";
  return {
    stage: registrarStage ? stage : project.currentStage || stage,
    progress: Math.max(0, Math.min(100, Number(project.progress ?? progress))),
    step: Math.max(
      0,
      Math.min(STEPS.length - 1, Number(project.completedStep ?? step)),
    ),
  };
}

function collectDocuments(project = {}) {
  const records = [];
  const add = (source, fallbackName, category = "PACS") => {
    if (!source) return;
    if (Array.isArray(source))
      return source.forEach((item) => add(item, fallbackName, category));
    if (typeof source === "string")
      return records.push({
        name: fallbackName,
        category,
        url: source,
        status: "Available",
      });
    if (typeof source !== "object") return;
    const url =
      source.url ||
      source.downloadUrl ||
      source.fileUrl ||
      source.pdfUrl ||
      source.documentUrl;
    if (url)
      records.push({
        name: source.name || source.title || source.fileName || fallbackName,
        category: source.category || category,
        code: source.code || source.type || "DOCUMENT",
        url,
        status: source.status || "Available",
        date: source.uploadedAt || source.createdAt || source.updatedAt,
      });
  };
  const addMap = (map, fallbackName, category) => {
    if (!map || Array.isArray(map) || typeof map !== "object") return;
    Object.entries(map).forEach(([key, value]) =>
      add(
        value,
        value?.title || key.replace(/_/g, " ") || fallbackName,
        category,
      ),
    );
  };

  add(project.documents, "Application Document", "PACS");
  if (
    project.documents &&
    !Array.isArray(project.documents) &&
    typeof project.documents === "object"
  ) {
    Object.entries(project.documents).forEach(([name, value]) =>
      add(value, name.replace(/([A-Z])/g, " $1"), "PACS"),
    );
  }
  add(
    project.generatedDocuments,
    "Generated PACS Document",
    "System Generated",
  );
  addMap(
    project.generatedDocuments,
    "Generated PACS Document",
    "System Generated",
  );
  addMap(project.signedPacsDocuments, "Signed PACS Document", "PACS");
  addMap(project.landLeaseDocuments, "Land / Lease Document", "PACS");
  addMap(project.registrarFileDocuments, "Registrar Source Document", "PACS");
  addMap(project.pmcTechnicalDocuments, "PMC Technical Document", "PMC");
  add(
    project.applicationPdfUrl,
    "Application Acknowledgement",
    "System Generated",
  );
  add(
    project.dprUrl || project.dprPdfUrl,
    "Detailed Project Report (DPR)",
    "PMC",
  );
  add(project.dprDocuments, "Detailed Project Report (DPR)", "PMC");
  add(
    project.surveyReportUrl || project.surveyPdfUrl,
    "Site Survey Report",
    "PMC",
  );
  add(project.surveyDocuments, "Site Survey Report", "PMC");
  add(
    project.soilTestReportUrl || project.soilTestPdfUrl,
    "Soil Test Report",
    "PMC",
  );
  add(project.soilTestDocuments, "Soil Test Report", "PMC");
  add(
    project.designUrl || project.designPdfUrl,
    "Approved Design and Drawings",
    "PMC",
  );
  add(project.designDocuments, "Approved Design and Drawings", "PMC");
  add(project.estimateUrl || project.estimatePdfUrl, "Project Estimate", "PMC");
  add(project.estimateDocuments, "Project Estimate", "PMC");
  add(project.boqUrl || project.boqPdfUrl, "Bill of Quantities (BOQ)", "PMC");
  add(project.boqDocuments, "Bill of Quantities (BOQ)", "PMC");
  add(project.tenderCopyUrl || project.tenderPdfUrl, "Tender Document", "PMC");
  add(project.tenderDocuments, "Tender Document", "PMC");
  add(
    project.loaPdfUrl || project.loaUrl,
    "Letter of Award / Work Order",
    "PMC",
  );
  add(project.progressDocuments, "Construction Progress Report", "PMC");
  add(project.raBillDocuments, "RA Bill Document", "PMC");
  add(project.completionCertificateUrl, "Completion Certificate", "PMC");
  Object.entries(project.technicalWorkflow || {}).forEach(
    ([stageKey, work]) => {
      const stageName = stageKey.replaceAll("_", " ");
      add(work?.documents || work?.files, stageName, "PMC");
    },
  );
  add(
    project.compiledDocumentUrl || project.completeDocumentFileUrl,
    "Complete Project Document File",
    "Complete File",
  );

  return records.filter(
    (item, index, list) =>
      item.url && list.findIndex((entry) => entry.url === item.url) === index,
  );
}

async function queryFirst(collectionName, field, value, filter) {
  if (!value) return null;
  try {
    const snap = await getDocs(
      query(collection(db, collectionName), where(field, "==", value)),
    );
    const item = filter
      ? snap.docs.find((entry) => filter(entry.data()))
      : snap.docs[0];
    return item ? { id: item.id, ...item.data() } : null;
  } catch (error) {
    console.warn(
      `${collectionName} lookup unavailable`,
      error?.code || error?.message,
    );
    return null;
  }
}

export default function NafedPacsDashboard() {
  const [profile, setProfile] = useState(null);
  const [profileSource, setProfileSource] = useState({
    collectionName: "",
    documentId: "",
  });
  const [project, setProject] = useState({});
  const [pmc, setPmc] = useState(null);
  const [active, setActive] = useState("Dashboard");
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState("");
  const [now, setNow] = useState(new Date());
  const [lastSyncedAt, setLastSyncedAt] = useState(null);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    let profileUnsubscribe = () => {};
    let projectUnsubscribe = () => {};
    let activeRequest = true;

    const authUnsubscribe = onAuthStateChanged(auth, async (user) => {
        if (!user) return window.location.replace("/nafed");
        try {
          let profileRecord = null;
          let profileCollection = "nafed_users";
          const direct = await getDoc(doc(db, "nafed_users", user.uid));
          if (direct.exists())
            profileRecord = { id: direct.id, ...direct.data() };
          if (!profileRecord && user.email)
            profileRecord = await queryFirst(
              "nafed_users",
              "email",
              user.email,
            );
          if (!profileRecord) {
            const legacy = await getDoc(doc(db, "pacs_users", user.uid));
            if (legacy.exists()) {
              profileCollection = "pacs_users";
              profileRecord = {
                id: legacy.id,
                ...legacy.data(),
                role: legacy.data().role || "PACS",
              };
            }
          }
          if (!profileRecord && user.email) {
            profileCollection = "pacs_users";
            profileRecord = await queryFirst("pacs_users", "email", user.email);
          }
          if (!profileRecord) {
            const common = await getDoc(doc(db, "users", user.uid));
            if (
              common.exists() &&
              clean(common.data().role).toUpperCase().includes("PACS")
            ) {
              profileCollection = "users";
              profileRecord = { id: common.id, ...common.data() };
            }
          }

          if (!profileRecord) {
            setNotice(
              "PACS profile could not be linked with this login. Please contact the portal administrator.",
            );
            profileRecord = {
              id: user.uid,
              name: user.displayName || "PACS Administrator",
              email: user.email || "",
              role: "PACS",
            };
            profileCollection = "";
          }
          const role = clean(profileRecord.role).toUpperCase();
          if (
            isRemovedRecord(profileRecord) ||
            !["PACS", "PACS / SOCIETY", "PACS_SOCIETY"].includes(role)
          ) {
            await signOut(auth);
            return window.location.replace("/nafed");
          }
          if (!activeRequest) return;
          setProfile(profileRecord);
          setProfileSource({
            collectionName: profileCollection,
            documentId: profileRecord.id || user.uid,
          });

          // Keep the PACS allocation profile live. State Branch allocation,
          // edit or removal is reflected without a browser refresh.
          if (profileRecord.id && profileCollection) {
            profileUnsubscribe = onSnapshot(
              doc(db, profileCollection, profileRecord.id),
              (snapshot) => {
                if (!snapshot.exists() || isRemovedRecord(snapshot.data())) {
                  setProfile(null);
                  setPmc(null);
                  setNotice("This PACS profile is no longer active.");
                  return;
                }
                setProfile({ id: snapshot.id, ...snapshot.data() });
                setLastSyncedAt(new Date());
              },
              (error) =>
                console.warn("PACS live profile unavailable", error?.code),
            );
          }

          const matchingProjects = [];
          for (const field of [
            "pacsUserId",
            "createdByUid",
            "pacsId",
            "pacsUid",
            "applicantUid",
          ]) {
            const snap = await getDocs(
              query(
                collection(db, "godown_projects"),
                where(field, "==", user.uid),
              ),
            );
            snap.docs.forEach((entry) => {
              const data = entry.data();
              if (!isRemovedRecord(data))
                matchingProjects.push({ id: entry.id, ...data });
            });
          }
          let foundProject =
            newestRecord(matchingProjects.filter(isNafedProject)) ||
            newestRecord(matchingProjects);
          // Legacy applications without UID fields are linked safely through registered email/name.
          if (!foundProject) {
            const allProjects = await getDocs(
              collection(db, "godown_projects"),
            );
            const profileName = clean(
              profileRecord.organizationName ||
                profileRecord.societyName ||
                profileRecord.pacsName,
            ).toLowerCase();
            const profileEmail = clean(
              profileRecord.email || user.email,
            ).toLowerCase();
            const legacyMatches = allProjects.docs
              .map((entry) => ({ id: entry.id, ...entry.data() }))
              .filter((data) => {
              const projectName = clean(
                data.societyName || data.pacsName || data.organizationName,
              ).toLowerCase();
              const projectEmail = clean(
                data.email || data.pacsEmail || data.createdByEmail,
              ).toLowerCase();
              return (
                isNafedProject(data) &&
                !isRemovedRecord(data) &&
                ((profileEmail && projectEmail === profileEmail) ||
                  (profileName && projectName === profileName))
              );
            });
            foundProject = newestRecord(legacyMatches);
          }
          if (foundProject) {
            setProject(foundProject);
            projectUnsubscribe = onSnapshot(
              doc(db, "godown_projects", foundProject.id),
              (snapshot) => {
                if (!snapshot.exists() || isRemovedRecord(snapshot.data())) {
                  setProject({});
                } else {
                  setProject({ id: snapshot.id, ...snapshot.data() });
                }
                setLastSyncedAt(new Date());
              },
              (error) =>
                console.warn("Project live update unavailable", error?.code),
            );
          } else {
            setProject({});
            // A PACS may submit its first application while this dashboard is
            // already open. Listen on supported ownership fields so it appears
            // automatically without logout or refresh.
            const liveProjects = new Map();
            const stops = [];
            const identifiers = [user.uid, profileRecord.uid, profileRecord.id]
              .map(clean)
              .filter((value, index, list) => value && list.indexOf(value) === index);
            for (const field of [
              "pacsUserId",
              "createdByUid",
              "pacsId",
              "pacsUid",
              "applicantUid",
            ]) {
              for (const identifier of identifiers) {
                stops.push(
                  onSnapshot(
                    query(
                      collection(db, "godown_projects"),
                      where(field, "==", identifier),
                    ),
                    (snapshot) => {
                      snapshot.docChanges().forEach((change) => {
                        const entry = change.doc;
                        const data = entry.data();
                        if (change.type === "removed" || isRemovedRecord(data))
                          liveProjects.delete(entry.id);
                        else liveProjects.set(entry.id, { id: entry.id, ...data });
                      });
                      const records = [...liveProjects.values()];
                      const latest =
                        newestRecord(records.filter(isNafedProject)) ||
                        newestRecord(records);
                      if (latest) setProject(latest);
                      setLastSyncedAt(new Date());
                    },
                    (error) =>
                      console.warn("New project live lookup unavailable", error?.code),
                  ),
                );
              }
            }
            projectUnsubscribe = () => stops.forEach((stop) => stop());
          }
        } catch (error) {
          console.error("PACS dashboard loading failed", error);
          setNotice(error.message || "The dashboard could not be loaded.");
        } finally {
          setLoading(false);
        }
      });

    return () => {
      activeRequest = false;
      authUnsubscribe();
      profileUnsubscribe();
      projectUnsubscribe();
    };
  }, []);

  // The PACS profile is authoritative for PMC allocation. When State Branch
  // unallocates the PMC, stale project contact data is deliberately ignored.
  const allocationStatus = upper(profile?.pmcAllocationStatus);
  const allocatedPmcUid =
    allocationStatus === "UNALLOCATED"
      ? ""
      : clean(profile?.allocatedPmcUid || profile?.allocatedPmcId);

  useEffect(() => {
    setPmc(null);
    if (!allocatedPmcUid) return undefined;
    return onSnapshot(
      doc(db, "nafed_users", allocatedPmcUid),
      (snapshot) => {
        if (!snapshot.exists() || isRemovedRecord(snapshot.data())) {
          setPmc(null);
          return;
        }
        const data = snapshot.data();
        const role = upper(data.role);
        if (
          !["PMC", "PMC_AGENCY", "PMC AGENCY"].includes(role) ||
          data.isActive === false
        ) {
          setPmc(null);
          return;
        }
        setPmc({ id: snapshot.id, ...data });
        setLastSyncedAt(new Date());
      },
      (error) => console.warn("Allocated PMC live lookup unavailable", error?.code),
    );
  }, [allocatedPmcUid]);

  const documents = useMemo(() => collectDocuments(project), [project]);
  const societyName =
    project.societyName ||
    project.pacsName ||
    profile?.organizationName ||
    "Registered PACS / Society";
  const projectId =
    project.applicationId ||
    project.projectId ||
    project.id ||
    "Application not submitted";
  const district = project.district || profile?.district || "Not available";
  const pmcAllocated =
    allocationStatus !== "UNALLOCATED" && Boolean(allocatedPmcUid);
  const pmcName = pmcAllocated
    ? pmc?.organizationName ||
      pmc?.name ||
      pmc?.pmcName ||
      profile?.allocatedPmcName ||
      "Allocated PMC"
    : "Allocation pending";
  const pmcPhone = pmcAllocated
    ? clean(
        pmc?.mobile ||
          pmc?.phone ||
          pmc?.contactPhone ||
          pmc?.mobileNumber ||
          profile?.allocatedPmcMobile,
      )
    : "";
  const pmcEmail = pmcAllocated
    ? clean(pmc?.email || pmc?.officialEmail || profile?.allocatedPmcEmail)
    : "";
  const workStarted = Boolean(
    project.workStartedAt ||
      project.workStartDate ||
      project.workStatus === "STARTED",
  );
  const workflow = deriveWorkflow(project, pmcAllocated);
  const progress = workflow.progress;
  const currentStep = workflow.step;
  const initials = clean(profile?.name || "PACS Administrator")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((item) => item[0])
    .join("")
    .toUpperCase();

  const choosePage = (page) => {
    if (page === "New Application" && !project.id)
      return window.location.assign("/nafed/apply-godown");
    setActive(page === "Project Overview" ? "Overview" : page);
  };
  const logout = async () => {
    await signOut(auth);
    window.location.replace("/nafed");
  };
  const openFile = (url) => window.open(url, "_blank", "noopener,noreferrer");

  if (loading)
    return (
      <div className="loading">
        <span />
        <h2>Loading PACS Digital Workspace</h2>
        <p>Verifying profile, project and PMC allocation.</p>
        <style>{CSS}</style>
      </div>
    );

  return (
    <div className={active === "Dashboard" ? "portal portal-home" : "portal"}>
      <style>{CSS}</style>
      <header className="header">
        <div className="brand">
          <NafedLogo compact />
          <div>
            <b>National PACS Portal</b>
            <span>World's Largest Grain Storage Plan</span>
          </div>
        </div>
        <div className="header-right">
          <div className="sync-state">
            <i /> Live Sync
            <span>
              {lastSyncedAt
                ? `Updated ${lastSyncedAt.toLocaleTimeString("en-IN", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}`
                : "Connecting"}
            </span>
          </div>
          <div className="live">
            <b>
              {now.toLocaleTimeString("en-IN", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </b>
            <span>
              {now.toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </span>
          </div>
          <div className="identity">
            <div className="avatar">{initials || "PA"}</div>
            <div>
              <b>{profile?.name || "PACS Administrator"}</b>
              <span>{societyName}</span>
            </div>
            <button onClick={logout}>Logout</button>
          </div>
        </div>
      </header>

      <main
        className={active === "Dashboard" ? "content home-content" : "content"}
      >
        {active !== "Dashboard" && (
          <div className="module-bar">
            <button onClick={() => setActive("Dashboard")}>
              Back to Dashboard
            </button>
            <div>
              <small>WORLD'S LARGEST GRAIN STORAGE PLAN</small>
              <h1>{active}</h1>
              <p>
                <b>{societyName}</b>
                <i />
                Project ID: <strong>{projectId}</strong>
              </p>
            </div>
            <div
              className={
                pmcAllocated
                  ? "allocation-status assigned"
                  : "allocation-status"
              }
            >
              {pmcAllocated ? "PMC Allocated" : "PMC Allocation Pending"}
            </div>
          </div>
        )}
        {notice && active !== "Dashboard" && (
          <div className="notice">
            Information: {notice}
            <button onClick={() => setNotice("")}>Close</button>
          </div>
        )}

        {active === "Dashboard" ? (
          <PortalHome
            modules={MODULES}
            choosePage={choosePage}
            profile={profile}
            societyName={societyName}
            projectId={projectId}
            project={project}
            currentStage={workflow.stage}
            pmcName={pmcName}
            pmcPhone={pmcPhone}
            pmcEmail={pmcEmail}
            pmcAllocated={pmcAllocated}
            progress={progress}
          />
        ) : active === "New Application" ? (
          <PacsSubmittedApplicationPage
            profile={profile || {}}
            profileSource={profileSource}
            project={project || {}}
            pmc={pmc || {}}
            onSaved={(changes) => {
              setProject((old) => ({ ...(old || {}), ...changes }));
              setLastSyncedAt(new Date());
            }}
          />
        ) : active === "Land & Lease" ? (
          <div className="module">
            <PacsLandLeaseCenter
              profile={profile || {}}
              project={project || {}}
              onSaved={(changes) => {
                setProject((old) => ({ ...(old || {}), ...changes }));
                setLastSyncedAt(new Date());
              }}
            />
          </div>
        ) : active === "Overview" ? (
          <Dashboard
            societyName={societyName}
            profile={profile}
            project={project}
            documents={documents}
            district={district}
            pmc={pmc}
            pmcName={pmcName}
            pmcPhone={pmcPhone}
            pmcEmail={pmcEmail}
            pmcAllocated={pmcAllocated}
            workStarted={workStarted}
            progress={progress}
            currentStep={currentStep}
            openFile={openFile}
            setActive={setActive}
          />
        ) : active === "Documents" ? (
          <DocumentsPage
            documents={documents}
            openFile={openFile}
            project={project}
          />
        ) : active === "DPR" ? (
          <PacsDprPage
            profile={profile || {}}
            project={project || {}}
            onSaved={(changes) => {
              setProject((old) => ({ ...(old || {}), ...changes }));
              setLastSyncedAt(new Date());
            }}
          />
        ) : active === "Committee Meeting" ? (
          <div className="module">
            <div className="intro">
              <small>MEETING DAY MODULE</small>
              <h2>Committee Meeting and GPS Evidence</h2>
              <p>
                Download all five print-ready PDFs, sign and seal them manually,
                upload the scanned signed PDFs, record attendance and capture
                geo-tagged meeting evidence.
              </p>
            </div>
            <PacsDocumentCenter
              mode="committee"
              profile={profile || {}}
              project={project || {}}
              onSaved={(changes) =>
                setProject((old) => ({ ...old, ...changes }))
              }
            />
          </div>
        ) : active === "District Registrar File" ? (
          <div className="module">
            <PacsRegistrarFileCenter
              profile={profile || {}}
              project={project || {}}
              onSaved={(changes) => {
                setProject((old) => ({ ...(old || {}), ...changes }));
                setLastSyncedAt(new Date());
              }}
            />
          </div>
        ) : active === "MOU" ? (
          <div className="module">
            <PacsMouSigning
              key={project?.id}
              profile={profile || {}}
              profileSource={profileSource}
              project={project || {}}
              onSaved={(changes) =>
                setProject((old) => ({ ...old, ...changes }))
              }
            />
          </div>
        ) : active === "Subsidy – First Instalment" ? (
          <div className="module">
            <PacsSubsidyFirstInstallment
              profile={profile || {}}
              project={project || {}}
              onSaved={(changes) => {
                setProject((old) => ({ ...(old || {}), ...changes }));
                setLastSyncedAt(new Date());
              }}
            />
          </div>
        ) : active === "Help Center" ? (
          <HelpCenterPage
            societyName={societyName}
            pmc={pmc}
            pmcName={pmcName}
            pmcPhone={pmcPhone}
            pmcEmail={pmcEmail}
            pmcAllocated={pmcAllocated}
          />
        ) : (
          <OperationalPage
            title={active}
            project={project}
            documents={documents}
            openFile={openFile}
            pmcAllocated={pmcAllocated}
            workStarted={workStarted}
          />
        )}
      </main>
    </div>
  );
}

function PortalHome({
  modules,
  choosePage,
  profile,
  societyName,
  projectId,
  project,
  currentStage,
  pmcName,
  pmcPhone,
  pmcEmail,
  pmcAllocated,
  progress,
}) {
  return (
    <section className="launcher">
      <div className="launcher-heading">
        <div className="nafed-seal">
          <NafedLogo />
        </div>
        <small>WORLD'S LARGEST GRAIN STORAGE PLAN</small>
        <h1>PACS Digital Project Workspace</h1>
        <p>{societyName}</p>
      </div>
      <div className="launcher-summary">
        <span>
          Project ID <b>{projectId}</b>
        </span>
        <span>
          Current Stage <b>{currentStage}</b>
        </span>
        <span>
          Assigned PMC <b>{pmcAllocated ? pmcName : "Allocation Pending"}</b>
        </span>
        <span>
          Overall Progress <b>{progress}%</b>
        </span>
      </div>
      <RegistrarLiveProgress project={project} compact />
      <div className="module-grid">
        {modules.map(([title, code, description], index) => (
          <button
            key={title}
            style={{ "--delay": `${index * 55}ms` }}
            onClick={() => choosePage(title)}
          >
            <ModuleIcon code={code} />
            <b>{title}</b>
            <span>{description}</span>
            <footer>
              <em>Open Module</em>
              <strong>
                {moduleStatus(title, project, pmcAllocated, profile)}
              </strong>
            </footer>
          </button>
        ))}
      </div>
      <div className="launcher-note">
        Secure access for the authorised PACS project administrator
        {pmcAllocated && (pmcPhone || pmcEmail)
          ? ` · Allocated PMC help desk: ${pmcPhone || pmcEmail}`
          : ""}
      </div>
    </section>
  );
}

function NafedLogo({ compact = false }) {
  return (
    <img
      className={compact ? "official-logo compact" : "official-logo"}
      src={ORIGINAL_NAFED_LOGO}
      alt="NAFED – A Farmers' Cooperative"
    />
  );
}

function ModuleIcon({ code }) {
  const paths = {
    OV: (
      <>
        <path d="M4 13h6V4H4v9Zm10 7h6V11h-6v9ZM4 20h6v-3H4v3Zm10-13h6V4h-6v3Z" />
      </>
    ),
    AP: (
      <>
        <path d="M12 5v14M5 12h14" />
        <circle cx="12" cy="12" r="9" />
      </>
    ),
    LL: (
      <>
        <path d="M3 20h18M5 20V8l7-5 7 5v12" />
        <path d="M9 20v-6h6v6M7 10h10" />
        <path d="M16 6 20 2M18 2h2v2" />
      </>
    ),
    DC: (
      <>
        <path d="M6 2h9l5 5v15H6z" />
        <path d="M14 2v6h6M9 13h8M9 17h8" />
      </>
    ),
    CM: (
      <>
        <circle cx="8" cy="8" r="3" />
        <circle cx="17" cy="8" r="3" />
        <path d="M2 20c0-4 2-7 6-7s6 3 6 7M13 14c1-.7 2.2-1 4-1 3.3 0 5 2.8 5 7" />
      </>
    ),
    SG: (
      <>
        <path d="M4 3h10v14H4zM7 7h4M7 11h4" />
        <path d="m14 14 5-5 2 2-5 5-3 1 1-3Z" />
        <circle cx="18" cy="18" r="4" />
      </>
    ),
    RF: (
      <>
        <path d="M5 3h10l4 4v14H5zM14 3v5h5" />
        <path d="M8 12h8M8 16h8" />
        <path d="m9 19 1.5 1.5L14 17" />
      </>
    ),
    MO: (
      <>
        <path d="M5 3h14v18H5zM8 8h8M8 12h8" />
        <path d="m9 17 2 2 5-5" />
      </>
    ),
    TN: (
      <>
        <path d="m14 4 6 6M12 6l6 6M4 20l8-8M3 21l4-1-3-3-1 4Z" />
        <path d="M13 3 3 13M21 11 11 21" />
      </>
    ),
    CN: (
      <>
        <path d="M3 21h18M5 21V9l7-5 7 5v12M9 21v-6h6v6M8 11h2M14 11h2" />
      </>
    ),
    SB: (
      <>
        <rect x="3" y="4" width="18" height="16" rx="2" />
        <path d="M3 9h18M7 14h4M16 12v5M13.5 14.5h5" />
      </>
    ),
    BL: (
      <>
        <path d="M6 2h12v20l-3-2-3 2-3-2-3 2V2Z" />
        <path d="M9 7h6M9 11h6M9 15h4" />
      </>
    ),
    PY: (
      <>
        <rect x="2" y="5" width="20" height="14" rx="2" />
        <path d="M2 10h20M6 15h4" />
      </>
    ),
    HP: (
      <>
        <path d="M4 13v-2a8 8 0 0 1 16 0v2" />
        <path d="M4 13h3v6H5a1 1 0 0 1-1-1v-5ZM20 13h-3v6h2a1 1 0 0 0 1-1v-5Z" />
        <path d="M17 19c0 2-2 3-5 3" />
      </>
    ),
  };
  return (
    <div className="module-icon">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {paths[code]}
      </svg>
    </div>
  );
}

function moduleStatus(title, project, pmcAllocated, profile = {}) {
  if (title === "New Application")
    return project.id ? "View / Edit" : "Start Application";
  if (title === "Land & Lease") {
    if (!project.id) return "Application Pending";
    if (["LEASE_DOCUMENTS_COMPLETE", "SIGNED_LEASE_UPLOADED"].includes(project.landDocumentStage))
      return "Lease Documents Complete";
    if (["REGISTERED_LEASE_UPLOADED_LAND_DOCUMENT_PENDING", "NOTARISED_LEASE_UPLOADED_LAND_DOCUMENT_PENDING"].includes(project.landDocumentStage))
      return "Land Document Pending";
    if (["LAND_DOCUMENT_UPLOADED_REGISTERED_LEASE_PENDING", "LAND_DOCUMENT_UPLOADED_NOTARISED_LEASE_PENDING"].includes(project.landDocumentStage))
      return "Registered Lease Pending";
    if (project.landLeaseDocuments?.LAND_RECORD?.url) return "Land Document Ready";
    return project.landDocumentStage || "Details Pending";
  }
  if (title === "Documents") return project.id ? "Live" : "Waiting";
  if (title === "Committee Meeting")
    return project.meetingCapturedAt ? "Recorded" : "Pending";
  if (title === "District Registrar File")
    return project.registrarPackage?.url
      ? humanizeStatus(
          project.registrarWorkflowStage ||
            project.pmcRegistrarStatus ||
            `ZIP V${project.registrarPackage.version || 1}`,
        )
      : "Checklist Pending";
  if (title === "MOU") return project.mouStatus || "Pending";
  if (title === "Tender") return project.tenderStatus || "Pending";
  if (title === "Construction") return project.workStatus || "Pending";
  if (title === "Subsidy – First Instalment") {
    const started = Boolean(
      project.workStartedAt ||
        project.workStartedAtIso ||
        project.workStartDate ||
        project.constructionStartedAt ||
        project.workStarted === true ||
        ["STARTED", "IN_PROGRESS", "CONSTRUCTION_STARTED"].includes(
          upper(project.workStatus || project.constructionStatus),
        ),
    );
    if (!started) return "Locked – Work Not Started";
    return project.subsidyFirstInstallment?.package?.url
      ? `ZIP V${project.subsidyFirstInstallment.package.version || 1}`
      : project.subsidyFirstInstallmentStage || "First Instalment Pending";
  }
  if (title === "Bills")
    return `${(project.raBills || project.bills || []).length || 0} Records`;
  if (title === "Payments")
    return `${(project.payments || []).length || 0} Records`;
  if (title === "Help Center")
    return pmcAllocated ? "PMC Support" : "Allocation Pending";
  return pmcAllocated ? "Active" : "View";
}

function RegistrarLiveProgress({ project = {}, compact = false }) {
  const packageReady = Boolean(
    project.registrarPackage?.url || project.registrarPackageUrl,
  );
  const pmcStatus = upper(
    project.registrarWorkflow?.pmc?.status || project.pmcRegistrarStatus,
  );
  const districtStatus = upper(
    project.registrarWorkflow?.district?.status || project.districtRegistrarStatus,
  );
  const stateStatus = upper(
    project.registrarWorkflow?.state?.status || project.stateRegistrarStatus,
  );
  const dcdcStatus = upper(project.dcdcStatus);
  let current = packageReady ? 0 : -1;
  if (
    project.pmcRegistrarFileForwardedToDistrict ||
    /FORWARDED_TO_DISTRICT|COMPLIANCE_RESUBMITTED/.test(pmcStatus)
  )
    current = 1;
  if (districtStatus && !/WAITING_FOR_PMC/.test(districtStatus)) current = 2;
  if (
    project.districtPhysicalFileForwardedToState ||
    /FORWARDED_TO_STATE|STATE_QUERY/.test(districtStatus) ||
    (stateStatus && !/WAITING_FOR_DISTRICT/.test(stateStatus))
  )
    current = 3;
  if (
    project.stateProjectPermissionGranted ||
    /PERMISSION_GRANTED|APPROVED_FOR_DCDC/.test(stateStatus) ||
    project.dcdcMeetingDate ||
    dcdcStatus
  )
    current = 4;
  if (["APPROVED", "REJECTED"].includes(dcdcStatus)) current = 5;
  const steps = [
    "PACS Package",
    "PMC Forward",
    "District Review",
    "State Review",
    "DCDC",
    "Complete",
  ];
  const query =
    (/QUERY/.test(stateStatus) && project.stateRegistrarRemarks) ||
    (/QUERY/.test(districtStatus) && project.districtRegistrarRemarks) ||
    "";
  const status =
    dcdcStatus ||
    stateStatus ||
    districtStatus ||
    pmcStatus ||
    (packageReady ? "READY_FOR_PMC_FORWARD" : "PACKAGE_PENDING");
  return (
    <section className={`pacs-reg-live ${compact ? "compact" : ""}`}>
      <style>{`.pacs-reg-live{margin:18px 0;padding:16px;border:1px solid #cad8e4;border-top:4px solid #1b7a49;border-radius:13px;background:#fff;color:#153654}.pacs-reg-title{display:flex;justify-content:space-between;gap:15px;align-items:center}.pacs-reg-title small{display:block;color:#1a7949;font-size:9px;font-weight:950;letter-spacing:.09em}.pacs-reg-title h3{margin:4px 0 0;font-size:18px}.pacs-reg-title b{padding:7px 10px;border-radius:999px;background:#eef5fb;color:#174b7b;font-size:9px}.pacs-reg-flow{display:grid;grid-template-columns:repeat(6,1fr);gap:7px;margin-top:14px}.pacs-reg-flow div{position:relative;padding:11px 8px;border:1px solid #d6e1ea;border-radius:8px;background:#f7f9fb}.pacs-reg-flow div.done{border-color:#8ac7a4;background:#eff9f3}.pacs-reg-flow div.current{border-color:#e2b357;background:#fff7e6}.pacs-reg-flow i,.pacs-reg-flow span{display:block}.pacs-reg-flow i{width:23px;height:23px;display:grid;place-items:center;border-radius:50%;background:#dfe8f0;color:#39556f;font-size:8px;font-style:normal;font-weight:950}.pacs-reg-flow .done i{background:#17864e;color:#fff}.pacs-reg-flow span{margin-top:7px;font-size:9px;font-weight:900}.pacs-reg-query{margin-top:10px;padding:10px;border-left:4px solid #d49322;background:#fff7e7;color:#724d0d;font-size:10px}.pacs-reg-live.compact{margin:12px 0}.pacs-reg-live.compact .pacs-reg-title h3{font-size:15px}@media(max-width:800px){.pacs-reg-flow{grid-template-columns:repeat(3,1fr)}}@media(max-width:480px){.pacs-reg-flow{grid-template-columns:repeat(2,1fr)}.pacs-reg-title{display:block}.pacs-reg-title>b{display:inline-block;margin-top:8px}}`}</style>
      <div className="pacs-reg-title"><div><small>LIVE REGISTRAR FILE TRACKING</small><h3>PMC → District → State → DCDC</h3></div><b>{humanizeStatus(status)}</b></div>
      <div className="pacs-reg-flow">{steps.map((label, index)=><div key={label} className={index < current ? "done" : index === current ? "current" : ""}><i>{index < current ? "✓" : index + 1}</i><span>{label}</span></div>)}</div>
      {query && <div className="pacs-reg-query"><b>Official query:</b> {query}</div>}
    </section>
  );
}

function Dashboard({
  profile,
  project,
  documents,
  district,
  pmc,
  pmcName,
  pmcPhone,
  pmcEmail,
  pmcAllocated,
  workStarted,
  progress,
  currentStep,
  openFile,
  setActive,
}) {
  return (
    <>
      <section className="welcome">
        <div>
          <small>PACS DIGITAL WORKSPACE</small>
          <h2>Welcome, {profile?.name || "PACS Administrator"}</h2>
          <p>
            Track the application, assigned PMC, official documents, MOU and
            execution progress.
          </p>
        </div>
        <b>{project.currentStage || "Application Stage"}</b>
      </section>
      <section className="kpis">
        <Kpi
          title="Project Status"
          value={humanizeStatus(
            project.status || (project.id ? "APPLIED" : "NOT APPLIED"),
          )}
          note={humanizeStatus(project.currentStage || "Application stage")}
        />
        <Kpi
          title="Proposed Capacity"
          value={`${numberText(project.proposedCapacityMt || project.capacityMt || project.capacity)} MT`}
          note="Grain storage capacity"
        />
        <Kpi
          title="Official Documents"
          value={`${documents.length} Files`}
          note="PACS and PMC records"
        />
        <Kpi
          title="Overall Progress"
          value={`${progress}%`}
          note="Project completion"
        />
      </section>
      <section className="pmc-card">
        <div>
          <small>PACS-WISE PMC ALLOCATION</small>
          <h2>{pmcName}</h2>
          <p>
            {pmcAllocated
              ? "This PMC has been allocated directly to your PACS by the NAFED State Branch. Technical and tender records will update here automatically."
              : "The State Branch will allocate your PACS to an approved PMC of the same State."}
          </p>
        </div>
        <div className="pmc-grid">
          <Info label="Project District" value={district} />
          <Info
            label="PMC Contact"
            value={pmcAllocated ? pmcPhone : "Allocation pending"}
          />
          <Info
            label="PMC Email"
            value={pmcAllocated ? pmcEmail : "Allocation pending"}
          />
          <Info
            label="Work Status"
            value={
              workStarted
                ? "Work Started"
                : pmcAllocated
                  ? "Awaiting Work Start"
                  : "Awaiting Allocation"
            }
          />
        </div>
      </section>
      <section className="steps">
        {STEPS.map((step, index) => (
          <React.Fragment key={step}>
            <div
              className={
                index < currentStep
                  ? "step done"
                  : index === currentStep
                    ? "step current"
                    : "step"
              }
            >
              <span>{index < currentStep ? "OK" : index + 1}</span>
              <b>{step}</b>
              <small>
                {index < currentStep
                  ? "Completed"
                  : index === currentStep
                    ? "In Progress"
                    : "Pending"}
              </small>
            </div>
            {index < STEPS.length - 1 && (
              <i className={index < currentStep ? "done" : ""} />
            )}
          </React.Fragment>
        ))}
      </section>
      <RegistrarLiveProgress project={project} />
      <section className="dashboard-grid">
        <Panel
          title="Allocated PMC Help Center"
          action="Open Help Center"
          click={() => setActive("Help Center")}
        >
          <div className="help-preview">
            <strong>{pmcAllocated ? pmcName : "PMC Allocation Pending"}</strong>
            <p>
              {pmcAllocated
                ? "Only your State Branch allocated PMC support details are shown here."
                : "Help desk contact will unlock automatically after State Branch allocation."}
            </p>
            {pmcAllocated && pmcPhone && (
              <a href={`tel:${safePhoneHref(pmcPhone)}`}>{pmcPhone}</a>
            )}
          </div>
        </Panel>
        <Panel
          title="Official Document Repository"
          action="Open Documents"
          click={() => setActive("Documents")}
        >
          <FileList files={documents.slice(0, 4)} openFile={openFile} />
          {!documents.length && (
            <Empty text="PACS and PMC documents will appear automatically after upload." />
          )}
        </Panel>
        <Panel
          title="Committee Meeting"
          action="Open Meeting Module"
          click={() => setActive("Committee Meeting")}
        >
          <div className="meeting-summary">
            <div className="meeting-photo">
              {project.committeePhotoUrl ? (
                <img src={project.committeePhotoUrl} alt="Committee meeting" />
              ) : (
                "Meeting photo not captured"
              )}
            </div>
            <div>
              <Info
                label="Meeting Date"
                value={formatDate(project.meetingCapturedAt)}
              />
              <Info
                label="GPS Location"
                value={
                  project.latitude && project.longitude
                    ? `${project.latitude}, ${project.longitude}`
                    : "Not captured"
                }
              />
              <Info
                label="Verification"
                value={project.committeeVerificationStatus || "Pending"}
              />
            </div>
          </div>
        </Panel>
      </section>
    </>
  );
}

const appField = (name, label, options = {}) => ({
  name,
  label,
  ...options,
});

const YES_NO_OPTIONS = ["", "YES", "NO"];
const PACS_APPLICATION_SECTIONS = [
  {
    title: "Society Registration and Statutory Details",
    note: "Complete registered identity and statutory particulars of the PACS",
    fields: [
      appField("societyName", "Society / PACS Name", {
        required: true,
        wide: true,
      }),
      appField("registrationNo", "Registration Number", { required: true }),
      appField("registrationDate", "Registration Date", { type: "date" }),
      appField("registrationAct", "Registration Act / Rules"),
      appField("societyType", "Society Type / Category"),
      appField("establishmentYear", "Establishment Year", {
        type: "number",
        min: "1900",
      }),
      appField("societyCode", "PACS / Society Code"),
      appField("panNumber", "PAN Number"),
      appField("gstNumber", "GST / GSTIN Number"),
      appField("auditClass", "Latest Audit Class"),
      appField("lastAuditYear", "Latest Audit Financial Year"),
    ],
  },
  {
    title: "Office, Location and Communication",
    note: "Registered office and official application contact details",
    fields: [
      appField("state", "State", { required: true }),
      appField("district", "District", { required: true }),
      appField("taluka", "Taluka / Block", { required: true }),
      appField("village", "Village", { required: true }),
      appField("address", "Registered Office Address", {
        type: "textarea",
        wide: true,
      }),
      appField("pinCode", "PIN Code", { inputMode: "numeric" }),
      appField("officeLandline", "Office Landline"),
      appField("website", "Website"),
      appField("email", "Official Email", { type: "email" }),
      appField("mobile", "Primary Mobile Number", {
        inputMode: "numeric",
      }),
    ],
  },
  {
    title: "Management, Membership and Authorised Contact",
    note: "Office bearers and responsible person named in the application",
    fields: [
      appField("chairmanName", "Chairman / Chairperson Name"),
      appField("chairmanMobile", "Chairman Mobile", {
        inputMode: "numeric",
      }),
      appField("secretaryName", "Secretary / Chief Executive Name"),
      appField("secretaryMobile", "Secretary Mobile", {
        inputMode: "numeric",
      }),
      appField("contactPerson", "Application Contact Person"),
      appField("contactDesignation", "Contact Person Designation"),
      appField("totalMembers", "Total Society Members", {
        type: "number",
        min: "0",
      }),
      appField("activeMembers", "Active Members", {
        type: "number",
        min: "0",
      }),
      appField("farmerMembers", "Farmer Members", {
        type: "number",
        min: "0",
      }),
      appField("womenMembers", "Women Members", {
        type: "number",
        min: "0",
      }),
    ],
  },
  {
    title: "Financial Position and Funds",
    note: "Reserve fund, Makan fund and last three financial years",
    fields: [
      appField("reserveFund", "Reserve Fund (₹)", {
        type: "number",
        min: "0",
      }),
      appField("buildingFund", "Building / Makan Fund (₹)", {
        type: "number",
        min: "0",
      }),
      appField("shareCapital", "Paid-up Share Capital (₹)", {
        type: "number",
        min: "0",
      }),
      appField("workingCapital", "Working Capital (₹)", {
        type: "number",
        min: "0",
      }),
      appField("profitYear1Label", "Financial Year 1"),
      appField("profitYear1", "Profit / Loss – Year 1 (₹)", {
        type: "number",
      }),
      appField("profitYear2Label", "Financial Year 2"),
      appField("profitYear2", "Profit / Loss – Year 2 (₹)", {
        type: "number",
      }),
      appField("profitYear3Label", "Financial Year 3"),
      appField("profitYear3", "Profit / Loss – Year 3 (₹)", {
        type: "number",
      }),
      appField("annualTurnover", "Latest Annual Turnover (₹)", {
        type: "number",
        min: "0",
      }),
      appField("netWorth", "Net Worth (₹)", { type: "number" }),
      appField("outstandingLoan", "Outstanding Loan (₹)", {
        type: "number",
        min: "0",
      }),
      appField("financialRemarks", "Financial Remarks", {
        type: "textarea",
        wide: true,
      }),
    ],
  },
  {
    title: "Bank and Credit Details",
    note: "Society bank account and credit linkage information",
    fields: [
      appField("bankName", "Bank Name", { wide: true }),
      appField("bankBranch", "Bank Branch"),
      appField("accountHolderName", "Account Holder Name"),
      appField("accountNumber", "Bank Account Number"),
      appField("accountType", "Account Type", {
        type: "select",
        options: ["", "CURRENT", "SAVINGS", "CASH_CREDIT", "OTHER"],
      }),
      appField("ifscCode", "IFSC Code"),
      appField("bankConsentStatus", "Bank Consent / Finance Status", {
        type: "select",
        options: ["", "PENDING", "AVAILABLE", "APPROVED", "NOT_REQUIRED"],
      }),
      appField("creditLimit", "Sanctioned Credit Limit (₹)", {
        type: "number",
        min: "0",
      }),
      appField("bankManagerName", "Bank Manager / Contact Person"),
      appField("bankContactMobile", "Bank Contact Mobile", {
        inputMode: "numeric",
      }),
    ],
  },
  {
    title: "Land Ownership, Lease and Revenue Record",
    note: "Land title, survey, lease and site accessibility details",
    fields: [
      appField("landOwnership", "Land Ownership / Possession", {
        type: "select",
        options: ["", "OWNED", "LEASED", "GOVERNMENT_ALLOTTED", "OTHER"],
      }),
      appField("landOwnerName", "Land Owner / Lessor Name"),
      appField("surveyNo", "Survey / Block Number"),
      appField("khasraNo", "Khasra / Revenue Record Number"),
      appField("landArea", "Available Land Area"),
      appField("landAreaUnit", "Land Area Unit", {
        type: "select",
        options: ["", "SQ_METER", "SQ_FEET", "HECTARE", "ACRE", "BIGHA"],
      }),
      appField("plotLengthM", "Available Plot Length (Meter)", {
        type: "number",
        min: "0",
      }),
      appField("plotWidthM", "Available Plot Width (Meter)", {
        type: "number",
        min: "0",
      }),
      appField("leaseAgreementNo", "Lease Agreement Number"),
      appField("leaseAgreementDate", "Lease Agreement Date", {
        type: "date",
      }),
      appField("leaseExpiryDate", "Lease Expiry Date", { type: "date" }),
      appField("leaseYears", "Registered Lease Period (Years)", {
        type: "number",
        min: "20",
      }),
      appField("titleClear", "Clear and Marketable Title", {
        type: "select",
        options: ["", "YES", "NO", "UNDER_VERIFICATION"],
      }),
      appField("encumbranceFree", "Land Free from Encumbrance", {
        type: "select",
        options: ["", "YES", "NO", "UNDER_VERIFICATION"],
      }),
      appField("approachRoadAvailable", "Approach Road Available", {
        type: "select",
        options: ["", "YES", "NO", "PROPOSED"],
      }),
      appField("approachRoadWidthM", "Approach Road Width (Meter)", {
        type: "number",
        min: "0",
      }),
      appField("siteAddress", "Proposed Godown Site Address", {
        type: "textarea",
        wide: true,
      }),
      appField("latitude", "Site Latitude", {
        type: "number",
        step: "any",
      }),
      appField("longitude", "Site Longitude", {
        type: "number",
        step: "any",
      }),
    ],
  },
  {
    title: "Godown, Capacity and Technical Proposal",
    note: "Storage capacity, dimensions, utilities and project cost",
    fields: [
      appField("projectName", "Project / Godown Name", { wide: true }),
      appField("capacityMt", "Proposed Capacity (MT)", {
        type: "number",
        min: "1",
        required: true,
      }),
      appField("existingCapacityMt", "Existing Capacity (MT)", {
        type: "number",
        min: "0",
      }),
      appField("numberOfGodowns", "Number of Proposed Godowns", {
        type: "number",
        min: "1",
      }),
      appField("godownType", "Proposed Godown Type"),
      appField("constructionType", "Construction Type", {
        type: "select",
        options: ["", "PEB", "RCC", "CONVENTIONAL", "HYBRID", "OTHER"],
      }),
      appField("proposedLengthM", "Godown Length (Meter)", {
        type: "number",
        min: "0",
      }),
      appField("proposedWidthM", "Godown Width (Meter)", {
        type: "number",
        min: "0",
      }),
      appField("clearHeightM", "Clear Height (Meter)", {
        type: "number",
        min: "0",
      }),
      appField("estimatedProjectCost", "Estimated Project Cost (₹)", {
        type: "number",
        min: "0",
      }),
      appField("societyContribution", "Society Contribution (₹)", {
        type: "number",
        min: "0",
      }),
      appField("storageCommodity", "Proposed Commodities", { wide: true }),
      appField("electricityAvailable", "Electricity Available", {
        type: "select",
        options: ["", "YES", "NO", "PROPOSED"],
      }),
      appField("waterAvailable", "Water Available", {
        type: "select",
        options: ["", "YES", "NO", "PROPOSED"],
      }),
      appField("drainageAvailable", "Drainage Available", {
        type: "select",
        options: ["", "YES", "NO", "PROPOSED"],
      }),
      appField("siteReady", "Site Readiness", {
        type: "select",
        options: ["", "READY", "PARTLY_READY", "NOT_READY"],
      }),
      appField("expectedCompletionMonths", "Expected Completion (Months)", {
        type: "number",
        min: "1",
      }),
    ],
  },
  {
    title: "Committee Resolution and DCDC Particulars",
    note: "Managing Committee, General Body and DCDC information",
    fields: [
      appField("resolutionNo", "Managing Committee Resolution Number"),
      appField("resolutionDate", "Managing Committee Resolution Date", {
        type: "date",
      }),
      appField("generalBodyResolutionNo", "General Body Resolution Number"),
      appField("generalBodyResolutionDate", "General Body Resolution Date", {
        type: "date",
      }),
      appField("committeeMeetingDate", "Committee Meeting Date", {
        type: "date",
      }),
      appField("quorumPresent", "Required Quorum Present", {
        type: "select",
        options: YES_NO_OPTIONS,
      }),
      appField("dcdcApprovalStatus", "DCDC Approval Status", {
        type: "select",
        options: ["", "NOT_APPLIED", "PENDING", "APPROVED", "REJECTED"],
      }),
      appField("dcdcApprovalNo", "DCDC Approval / Reference Number"),
      appField("dcdcApprovalDate", "DCDC Approval Date", { type: "date" }),
      appField(
        "technicalFileSubmissionDays",
        "Technical File Submission Period (Days)",
        { type: "number", min: "1" },
      ),
      appField("resolutionRemarks", "Resolution / DCDC Remarks", {
        type: "textarea",
        wide: true,
      }),
    ],
  },
  {
    title: "NAFED Assurance, Scheme and Implementation",
    note: "Hiring assurance and linked scheme particulars",
    fields: [
      appField("schemeName", "Scheme / Programme Name", { wide: true }),
      appField("nafedBranchName", "NAFED State Branch", { wide: true }),
      appField("nafedAssuranceStatus", "NAFED Hiring Assurance Status", {
        type: "select",
        options: ["", "AFTER_DCDC", "PENDING", "RECEIVED", "APPROVED"],
      }),
      appField("nafedAssuranceNo", "NAFED Assurance Letter Number"),
      appField("nafedAssuranceDate", "NAFED Assurance Date", {
        type: "date",
      }),
      appField("subsidyScheme", "Proposed Subsidy / Assistance Scheme", {
        wide: true,
      }),
      appField("proposedPmcName", "Proposed / Allocated PMC", {
        wide: true,
      }),
    ],
  },
  {
    title: "Declaration and Application Remarks",
    note: "Authorised applicant and submitted confirmations",
    fields: [
      appField("applicantName", "Authorised Applicant Name"),
      appField("applicantDesignation", "Applicant Designation"),
      appField("declarationAccepted", "Application Declaration Accepted", {
        type: "select",
        options: YES_NO_OPTIONS,
      }),
      appField(
        "undertakingAccepted",
        "DCDC / Technical Undertaking Accepted",
        { type: "select", options: YES_NO_OPTIONS },
      ),
      appField("dataAccuracyConfirmed", "Information Accuracy Confirmed", {
        type: "select",
        options: YES_NO_OPTIONS,
      }),
      appField("remarks", "Application Remarks", {
        type: "textarea",
        wide: true,
      }),
    ],
  },
];

const PACS_APPLICATION_NUMERIC_FIELDS = new Set([
  "establishmentYear",
  "totalMembers",
  "activeMembers",
  "farmerMembers",
  "womenMembers",
  "reserveFund",
  "buildingFund",
  "shareCapital",
  "workingCapital",
  "profitYear1",
  "profitYear2",
  "profitYear3",
  "annualTurnover",
  "netWorth",
  "outstandingLoan",
  "creditLimit",
  "plotLengthM",
  "plotWidthM",
  "approachRoadWidthM",
  "leaseYears",
  "latitude",
  "longitude",
  "capacityMt",
  "existingCapacityMt",
  "numberOfGodowns",
  "proposedLengthM",
  "proposedWidthM",
  "clearHeightM",
  "estimatedProjectCost",
  "societyContribution",
  "expectedCompletionMonths",
  "technicalFileSubmissionDays",
]);

const PACS_APPLICATION_MOBILE_FIELDS = new Set([
  "mobile",
  "chairmanMobile",
  "secretaryMobile",
  "bankContactMobile",
]);

const applicationDateInput = (value) => {
  if (!value) return "";
  const date = value?.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
};

const applicationSources = (project = {}, profile = {}) =>
  [
    project,
    project.applicationData,
    project.applicationDetails,
    project.formData,
    project.pacsApplicationDetails,
    project.pacsDetails,
    project.societyDetails,
    project.registrationDetails,
    project.contactDetails,
    project.membershipDetails,
    project.financialDetails,
    project.bankDetails,
    project.landDetails,
    project.siteDetails,
    project.godownDetails,
    project.projectDetails,
    project.committeeDetails,
    project.nafedDetails,
    project.declarationDetails,
    profile,
  ].filter((source) => source && typeof source === "object");

const applicationSourceValue = (sources, ...keys) => {
  for (const source of sources) {
    for (const key of keys) {
      const value = key
        .split(".")
        .reduce((current, part) => current?.[part], source);
      if (value !== undefined && value !== null && clean(value)) return value;
    }
  }
  return "";
};

const applicationFormFrom = (project = {}, profile = {}) => {
  const sources = applicationSources(project, profile);
  const pick = (...keys) => clean(applicationSourceValue(sources, ...keys));
  const pickDate = (...keys) =>
    applicationDateInput(applicationSourceValue(sources, ...keys));

  return {
  societyName: pick(
    "societyName",
    "pacsName",
    "organizationName",
    "cooperativeName",
  ),
  registrationNo: pick(
    "pacsRegistrationNo",
    "registrationNumber",
    "registrationNo",
  ),
  registrationDate: pickDate(
    "registrationDate",
    "pacsRegistrationDate",
    "registeredAt",
    "societyRegistrationDate",
  ),
  societyType: pick("societyType", "pacsType", "cooperativeType"),
  projectName: pick(
    "projectName",
    "godownName",
    "warehouseName",
    "schemeName",
  ),
  state: pick("state", "stateName"),
  district: pick("district", "districtName"),
  taluka: pick("taluka", "talukaName", "block"),
  village: pick("village", "villageName", "city"),
  address: pick(
    "address",
    "registeredAddress",
    "officeAddress",
    "societyAddress",
    "pacsAddress",
  ),
  pinCode: pick("pinCode", "pincode", "postalCode", "zipCode"),
  capacityMt: pick(
    "proposedCapacityMt",
    "proposedCapacityMT",
    "capacityMt",
    "capacityMT",
    "storageCapacityMt",
    "godownCapacityMt",
    "selectedCapacity",
    "capacity",
  ),
  existingCapacityMt: pick(
    "existingCapacityMt",
    "existingCapacityMT",
    "currentCapacityMt",
    "existingStorageCapacity",
  ),
  surveyNo: pick(
    "surveyNo",
    "landSurveyNo",
    "surveyNumber",
    "blockNo",
    "revenueSurveyNo",
  ),
  landArea: pick(
    "landArea",
    "landAreaSqFt",
    "landAreaSqM",
    "plotArea",
    "availableLandArea",
  ),
  landOwnership: pick(
    "landOwnership",
    "landOwnershipType",
    "ownershipType",
    "landStatus",
  ),
  godownType: pick("godownType", "warehouseType", "storageType"),
  latitude: pick(
    "siteLatitude",
    "latitude",
    "location.latitude",
    "geoLocation.latitude",
  ),
  longitude: pick(
    "siteLongitude",
    "longitude",
    "location.longitude",
    "geoLocation.longitude",
  ),
  contactPerson: pick(
    "contactPerson",
    "contactPersonName",
    "authorizedPersonName",
    "applicantName",
    "name",
  ),
  mobile: pick("mobile", "pacsMobile", "mobileNumber", "phoneNumber"),
  email: pick("email", "pacsEmail", "officialEmail", "createdByEmail"),
  registrationAct: pick(
    "registrationAct",
    "registeredUnderAct",
    "cooperativeAct",
  ),
  establishmentYear: pick(
    "establishmentYear",
    "yearOfEstablishment",
    "foundedYear",
  ),
  societyCode: pick("societyCode", "pacsCode", "cooperativeCode"),
  panNumber: pick("panNumber", "pan", "pacsPan"),
  gstNumber: pick("gstNumber", "gstin", "gstNo"),
  auditClass: pick("auditClass", "auditGrade", "latestAuditClass"),
  lastAuditYear: pick(
    "lastAuditYear",
    "latestAuditYear",
    "auditedFinancialYear",
  ),
  officeLandline: pick("officeLandline", "landline", "officePhone"),
  website: pick("website", "websiteUrl"),
  chairmanName: pick(
    "chairmanName",
    "chairpersonName",
    "presidentName",
    "pacsChairmanName",
  ),
  chairmanMobile: pick(
    "chairmanMobile",
    "chairpersonMobile",
    "presidentMobile",
  ),
  secretaryName: pick(
    "secretaryName",
    "pacsSecretaryName",
    "chiefExecutiveName",
    "ministerName",
  ),
  secretaryMobile: pick(
    "secretaryMobile",
    "pacsSecretaryMobile",
    "chiefExecutiveMobile",
  ),
  contactDesignation: pick(
    "contactDesignation",
    "contactPersonDesignation",
    "authorizedPersonDesignation",
  ),
  totalMembers: pick("totalMembers", "memberCount", "totalMemberCount"),
  activeMembers: pick("activeMembers", "activeMemberCount"),
  farmerMembers: pick("farmerMembers", "farmerMemberCount"),
  womenMembers: pick("womenMembers", "womenMemberCount"),
  reserveFund: pick("reserveFund", "reserveFundAmount"),
  buildingFund: pick(
    "buildingFund",
    "buildingFundAmount",
    "makanFund",
    "makanFundAmount",
  ),
  shareCapital: pick("shareCapital", "paidUpShareCapital"),
  workingCapital: pick("workingCapital", "workingCapitalAmount"),
  profitYear1Label: pick(
    "profitYear1Label",
    "financialYear1",
    "profitFinancialYear1",
  ),
  profitYear1: pick("profitYear1", "profitAmountYear1", "lastYearProfit"),
  profitYear2Label: pick(
    "profitYear2Label",
    "financialYear2",
    "profitFinancialYear2",
  ),
  profitYear2: pick(
    "profitYear2",
    "profitAmountYear2",
    "secondLastYearProfit",
  ),
  profitYear3Label: pick(
    "profitYear3Label",
    "financialYear3",
    "profitFinancialYear3",
  ),
  profitYear3: pick(
    "profitYear3",
    "profitAmountYear3",
    "thirdLastYearProfit",
  ),
  annualTurnover: pick("annualTurnover", "latestAnnualTurnover", "turnover"),
  netWorth: pick("netWorth", "societyNetWorth"),
  outstandingLoan: pick(
    "outstandingLoan",
    "outstandingLoanAmount",
    "totalBorrowings",
  ),
  financialRemarks: pick("financialRemarks", "financeRemarks"),
  bankName: pick("bankName", "financingBankName", "bank.bankName"),
  bankBranch: pick("bankBranch", "bankBranchName", "bank.branchName"),
  accountHolderName: pick("accountHolderName", "bankAccountHolderName"),
  accountNumber: pick("accountNumber", "bankAccountNumber"),
  accountType: pick("accountType", "bankAccountType"),
  ifscCode: pick("ifscCode", "ifsc", "bankIFSC"),
  bankConsentStatus: pick(
    "bankConsentStatus",
    "bankFinanceStatus",
    "loanConsentStatus",
  ),
  creditLimit: pick("creditLimit", "sanctionedCreditLimit"),
  bankManagerName: pick("bankManagerName", "bankContactPerson"),
  bankContactMobile: pick("bankContactMobile", "bankManagerMobile"),
  landOwnerName: pick("landOwnerName", "lessorName", "propertyOwnerName"),
  khasraNo: pick("khasraNo", "khasraNumber", "revenueRecordNo"),
  landAreaUnit: pick("landAreaUnit", "plotAreaUnit", "areaUnit"),
  plotLengthM: pick(
    "plotLengthM",
    "landLengthM",
    "plotLength",
    "landLength",
  ),
  plotWidthM: pick(
    "plotWidthM",
    "landWidthM",
    "plotWidth",
    "landWidth",
  ),
  leaseAgreementNo: pick(
    "leaseAgreementNo",
    "leaseAgreementNumber",
    "registeredLeaseNo",
  ),
  leaseAgreementDate: pickDate(
    "leaseAgreementDate",
    "registeredLeaseDate",
  ),
  leaseExpiryDate: pickDate("leaseExpiryDate", "leaseValidTill"),
  leaseYears: pick("leaseYears", "registeredLeaseYears", "leasePeriodYears"),
  titleClear: pick("titleClear", "clearTitle", "landTitleStatus"),
  encumbranceFree: pick(
    "encumbranceFree",
    "isEncumbranceFree",
    "encumbranceStatus",
  ),
  approachRoadAvailable: pick(
    "approachRoadAvailable",
    "roadAccessAvailable",
    "hasApproachRoad",
  ),
  approachRoadWidthM: pick(
    "approachRoadWidthM",
    "approachRoadWidth",
    "roadWidthM",
  ),
  siteAddress: pick(
    "siteAddress",
    "godownSiteAddress",
    "projectSiteAddress",
  ),
  numberOfGodowns: pick(
    "numberOfGodowns",
    "proposedGodownCount",
    "godownCount",
  ),
  constructionType: pick(
    "constructionType",
    "structureType",
    "buildingType",
  ),
  proposedLengthM: pick(
    "proposedLengthM",
    "godownLengthM",
    "godownLength",
  ),
  proposedWidthM: pick(
    "proposedWidthM",
    "godownWidthM",
    "godownWidth",
  ),
  clearHeightM: pick("clearHeightM", "godownClearHeightM", "clearHeight"),
  estimatedProjectCost: pick(
    "estimatedProjectCost",
    "projectCost",
    "estimatedCost",
  ),
  societyContribution: pick(
    "societyContribution",
    "pacsContribution",
    "beneficiaryContribution",
  ),
  storageCommodity: pick(
    "storageCommodity",
    "proposedCommodities",
    "commodityNames",
  ),
  electricityAvailable: pick(
    "electricityAvailable",
    "powerAvailable",
    "electricityStatus",
  ),
  waterAvailable: pick(
    "waterAvailable",
    "waterFacilityAvailable",
    "waterStatus",
  ),
  drainageAvailable: pick(
    "drainageAvailable",
    "drainageFacilityAvailable",
    "drainageStatus",
  ),
  siteReady: pick("siteReady", "siteReadiness", "siteStatus"),
  expectedCompletionMonths: pick(
    "expectedCompletionMonths",
    "projectDurationMonths",
  ),
  resolutionNo: pick(
    "committeeResolutionNo",
    "managingCommitteeResolutionNo",
    "resolutionNo",
    "boardResolutionNo",
  ),
  resolutionDate: pickDate(
    "committeeResolutionDate",
    "managingCommitteeResolutionDate",
    "resolutionDate",
    "boardResolutionDate",
  ),
  generalBodyResolutionNo: pick(
    "generalBodyResolutionNo",
    "generalMeetingResolutionNo",
  ),
  generalBodyResolutionDate: pickDate(
    "generalBodyResolutionDate",
    "generalMeetingResolutionDate",
  ),
  committeeMeetingDate: pickDate(
    "committeeMeetingDate",
    "meetingDate",
    "meetingCapturedAt",
  ),
  quorumPresent: pick("quorumPresent", "committeeQuorumPresent"),
  dcdcApprovalStatus: pick(
    "dcdcApprovalStatus",
    "dcdcStatus",
    "committeeApprovalStatus",
  ),
  dcdcApprovalNo: pick(
    "dcdcApprovalNo",
    "dcdcApprovalNumber",
    "dcdcReferenceNo",
  ),
  dcdcApprovalDate: pickDate("dcdcApprovalDate", "dcdcApprovedAt"),
  technicalFileSubmissionDays: pick(
    "technicalFileSubmissionDays",
    "dprSubmissionDays",
    "complianceDays",
  ),
  resolutionRemarks: pick(
    "resolutionRemarks",
    "committeeRemarks",
    "dcdcRemarks",
  ),
  schemeName: pick("schemeName", "programmeName", "programName"),
  nafedBranchName: pick(
    "nafedBranchName",
    "stateBranchName",
    "branchName",
  ),
  nafedAssuranceStatus: pick(
    "nafedAssuranceStatus",
    "hiringAssuranceStatus",
    "assuranceStatus",
  ),
  nafedAssuranceNo: pick(
    "nafedAssuranceNo",
    "nafedHiringAssuranceNo",
    "hiringAssuranceNo",
    "assuranceLetterNo",
  ),
  nafedAssuranceDate: pickDate(
    "nafedAssuranceDate",
    "nafedHiringAssuranceDate",
    "hiringAssuranceDate",
    "assuranceLetterDate",
  ),
  subsidyScheme: pick(
    "subsidyScheme",
    "proposedSubsidyScheme",
    "assistanceScheme",
  ),
  proposedPmcName: pick(
    "proposedPmcName",
    "allocatedPmcName",
    "pmcName",
    "pmcAgencyName",
  ),
  applicantName: pick(
    "applicantName",
    "authorizedPersonName",
    "contactPerson",
    "name",
  ),
  applicantDesignation: pick(
    "applicantDesignation",
    "authorizedPersonDesignation",
    "designation",
  ),
  declarationAccepted: pick(
    "declarationAccepted",
    "applicationDeclarationAccepted",
    "declarationConfirmed",
  ),
  undertakingAccepted: pick(
    "undertakingAccepted",
    "dcdcUndertakingAccepted",
    "technicalUndertakingAccepted",
  ),
  dataAccuracyConfirmed: pick(
    "dataAccuracyConfirmed",
    "informationConfirmed",
    "detailsVerified",
  ),
  remarks: pick(
    "applicationRemarks",
    "remarks",
    "pacsRemarks",
    "declarationRemarks",
  ),
  };
};

const pacsReportFirst = (...values) => {
  for (const value of values) {
    const normalized = clean(value);
    if (normalized) return normalized;
  }
  return "";
};

const pacsReportFirstDate = (...values) => {
  for (const value of values) {
    if (
      value &&
      (typeof value?.toDate === "function" ||
        Number.isFinite(value?.seconds))
    )
      return value;
    if (clean(value)) return value;
  }
  return "";
};

const pacsReportToDate = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value === "object" && Number.isFinite(value.seconds))
    return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const pacsReportDate = (value) => {
  const date = pacsReportToDate(value);
  if (!date) return "________________";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const pacsReportEscape = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const pacsReportSafe = (value, fallback = "________________") =>
  pacsReportEscape(clean(value) || fallback);

const pacsReportFileSlug = (value) =>
  clean(value || "PACS")
    .replace(/[^a-zA-Z0-9\u0A80-\u0AFF_-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, 80) || "PACS";

const pacsReportCapacity = (value) => {
  const number = Number(String(value || "").replace(/,/g, ""));
  return Number.isFinite(number) && number > 0
    ? `${number.toLocaleString("en-IN")} MT`
    : "________ MT";
};

const pacsReportAsset = (
  url,
  alt,
  manualText,
  extraClass = "",
) => {
  void url;
  void alt;
  void extraClass;
  return `<div class="signature-asset-empty">${pacsReportEscape(
    manualText,
  )}</div>`;
};

const buildPacsRegistrationReportData = ({
  form = {},
  project = {},
  profile = {},
  pmc = {},
}) => {
  const assuranceStatus = upper(
    pacsReportFirst(
      form.nafedAssuranceStatus,
      project.nafedAssuranceStatus,
      project.hiringAssuranceStatus,
    ),
  );
  const assuranceNo = pacsReportFirst(
    form.nafedAssuranceNo,
    project.nafedAssuranceNo,
    project.nafedHiringAssuranceNo,
    project.hiringAssuranceNo,
  );
  const leaseAgreementNo = pacsReportFirst(
    form.leaseAgreementNo,
    project.leaseAgreementNo,
    project.leaseAgreementNumber,
  );
  const landOwnership = upper(
    pacsReportFirst(
      form.landOwnership,
      project.landOwnership,
      project.landOwnershipType,
    ),
  );

  return {
    societyName: pacsReportFirst(
      form.societyName,
      project.societyName,
      project.pacsName,
      profile.organizationName,
      profile.societyName,
    ),
    registrationNo: pacsReportFirst(
      form.registrationNo,
      project.pacsRegistrationNo,
      project.registrationNumber,
      profile.registrationNumber,
      profile.pacsRegistrationNo,
    ),
    registrationDate: pacsReportFirstDate(
      form.registrationDate,
      project.registrationDate,
      profile.registrationDate,
    ),
    address: pacsReportFirst(
      form.address,
      project.registeredAddress,
      project.address,
      profile.address,
    ),
    village: pacsReportFirst(form.village, project.village, profile.village),
    taluka: pacsReportFirst(
      form.taluka,
      project.taluka,
      project.block,
      profile.taluka,
    ),
    district: pacsReportFirst(
      form.district,
      project.district,
      profile.district,
    ),
    state: pacsReportFirst(form.state, project.state, profile.state),
    pinCode: pacsReportFirst(
      form.pinCode,
      project.pinCode,
      project.pincode,
      profile.pinCode,
    ),
    applicationId: pacsReportFirst(
      project.applicationId,
      project.projectId,
      project.id,
    ),
    applicationDate: pacsReportFirstDate(
      project.finalSubmittedAt,
      project.submittedAt,
      project.applicationSubmittedAt,
      project.createdAt,
    ),
    projectName: pacsReportFirst(
      form.projectName,
      project.projectName,
      project.schemeName,
      "NAFED-linked Grain Storage Project",
    ),
    capacityMt: pacsReportFirst(
      form.capacityMt,
      project.proposedCapacityMt,
      project.capacityMt,
      project.capacity,
    ),
    surveyNo: pacsReportFirst(
      form.surveyNo,
      project.surveyNo,
      project.landSurveyNo,
      project.blockNo,
    ),
    landArea: pacsReportFirst(
      form.landArea,
      project.landArea,
      project.plotArea,
    ),
    resolutionNo: pacsReportFirst(
      form.resolutionNo,
      project.committeeResolutionNo,
      project.managingCommitteeResolutionNo,
      project.resolutionNo,
    ),
    resolutionDate: pacsReportFirstDate(
      form.resolutionDate,
      project.committeeResolutionDate,
      project.managingCommitteeResolutionDate,
      project.resolutionDate,
    ),
    reportNo: pacsReportFirst(
      project.dcdcUndertakingNo,
      project.undertakingLetterNo,
      project.letterNo,
      project.referenceNo,
    ),
    reportDate: new Date(),
    complianceDays: pacsReportFirst(
      form.technicalFileSubmissionDays,
      project.technicalFileSubmissionDays,
      project.dprSubmissionDays,
      project.complianceDays,
      "60",
    ),
    recipientOffice: "The District Registrar, Cooperative Societies",
    stateRegistrarOffice: "The State Registrar, Cooperative Societies",
    nafedBranchName: pacsReportFirst(
      project.nafedBranchName,
      project.stateBranchName,
      profile.nafedBranchName,
      form.state ? `NAFED ${form.state} State Branch` : "",
      "NAFED State Branch",
    ),
    pmcName: pacsReportFirst(
      pmc.organizationName,
      pmc.agencyName,
      pmc.name,
      project.allocatedPmcName,
      project.pmcName,
      profile.allocatedPmcName,
    ),
    assuranceMode:
      assuranceNo ||
      assuranceStatus.includes("ISSUED") ||
      assuranceStatus.includes("RECEIVED") ||
      assuranceStatus.includes("APPROVED")
        ? "RECEIVED"
        : "AFTER_DCDC",
    assuranceNo,
    assuranceDate: pacsReportFirstDate(
      form.nafedAssuranceDate,
      project.nafedAssuranceDate,
      project.nafedHiringAssuranceDate,
      project.hiringAssuranceDate,
    ),
    leaseMode: leaseAgreementNo
      ? "EXECUTED"
      : landOwnership.includes("OWN")
        ? "OWNED"
        : "AFTER_DCDC",
    leaseAgreementNo,
    leaseAgreementDate: pacsReportFirstDate(
      form.leaseAgreementDate,
      project.leaseAgreementDate,
      project.registeredLeaseDate,
    ),
    landOwnerName: pacsReportFirst(
      form.landOwnerName,
      project.landOwnerName,
      project.lessorName,
    ),
    chairmanName: pacsReportFirst(
      form.chairmanName,
      project.chairmanName,
      project.pacsChairmanName,
      profile.chairmanName,
      profile.pacsChairmanName,
    ),
    secretaryName: pacsReportFirst(
      form.secretaryName,
      project.secretaryName,
      project.pacsSecretaryName,
      profile.secretaryName,
      profile.pacsSecretaryName,
    ),
    chairmanSignatureUrl: pacsReportFirst(
      project.chairmanSignatureUrl,
      project.authorisedSignatures?.chairman?.url,
      project.pacsSignatures?.chairman?.url,
      profile.chairmanSignatureUrl,
      profile.authorisedSignatures?.chairman?.url,
    ),
    secretarySignatureUrl: pacsReportFirst(
      project.secretarySignatureUrl,
      project.authorisedSignatures?.secretary?.url,
      project.pacsSignatures?.secretary?.url,
      profile.secretarySignatureUrl,
      profile.authorisedSignatures?.secretary?.url,
    ),
    societySealUrl: pacsReportFirst(
      project.societySealUrl,
      project.officialSealUrl,
      project.authorisedSignatures?.seal?.url,
      profile.societySealUrl,
      profile.officialSealUrl,
      profile.authorisedSignatures?.seal?.url,
    ),
    contactMobile: pacsReportFirst(
      form.mobile,
      project.pacsMobile,
      project.mobile,
      profile.mobile,
    ),
    contactEmail: pacsReportFirst(
      form.email,
      project.pacsEmail,
      project.email,
      profile.email,
    ),
  };
};

const pacsReportAssuranceEnglish = (data) =>
  data.assuranceMode === "RECEIVED"
    ? `NAFED's written Hiring Assurance has been received${
        data.assuranceNo ? ` vide No. ${pacsReportSafe(data.assuranceNo)}` : ""
      }${
        data.assuranceDate
          ? ` dated ${pacsReportSafe(pacsReportDate(data.assuranceDate))}`
          : ""
      }. The final MoU/Hiring Agreement shall be executed in accordance with the approved terms and the competent authority's directions.`
    : "NAFED shall issue the written Hiring Assurance and the proposed MoU/Hiring Agreement shall be executed after the project receives conditional/in-principle DCDC approval and after completion of the prescribed eligibility formalities.";

const pacsReportAssuranceGujarati = (data) =>
  data.assuranceMode === "RECEIVED"
    ? `NAFED તરફથી લેખિત Hiring Assurance પ્રાપ્ત થયેલ છે${
        data.assuranceNo
          ? `, પત્ર ક્રમાંક ${pacsReportSafe(data.assuranceNo)}`
          : ""
      }${
        data.assuranceDate
          ? `, તારીખ ${pacsReportSafe(pacsReportDate(data.assuranceDate))}`
          : ""
      }. અંતિમ MoU/Hiring Agreement મંજૂર શરતો તથા સક્ષમ સત્તાની સૂચનાઓ મુજબ કરવામાં આવશે.`
    : "પ્રોજેક્ટને DCDCની શરતી/સૈદ્ધાંતિક મંજૂરી મળ્યા બાદ તથા નિયત પાત્રતા પ્રક્રિયા પૂર્ણ થયા પછી NAFED તરફથી લેખિત Hiring Assurance આપવામાં આવશે અને NAFED સાથેનો પ્રસ્તાવિત MoU/Hiring Agreement પૂર્ણ કરવામાં આવશે.";

const pacsReportLeaseEnglish = (data) => {
  if (data.leaseMode === "OWNED")
    return "The project land is stated to be owned/held by the Society. Updated ownership, title and possession documents shall be produced before final technical/administrative approval.";
  if (data.leaseMode === "EXECUTED")
    return `The registered Lease Agreement is stated to have been executed${
      data.leaseAgreementNo
        ? ` vide No. ${pacsReportSafe(data.leaseAgreementNo)}`
        : ""
    }${
      data.leaseAgreementDate
        ? ` dated ${pacsReportSafe(
            pacsReportDate(data.leaseAgreementDate),
          )}`
        : ""
    }. The original/verified copy shall be submitted whenever called for.`;
  return "Where the project land is to be taken on lease, the registered Lease Agreement and related title/NOC documents shall be executed and submitted after DCDC approval but before final technical/administrative approval, tender publication, work order or commencement of construction.";
};

const pacsReportLeaseGujarati = (data) => {
  if (data.leaseMode === "OWNED")
    return "પ્રોજેક્ટ માટેની જમીન મંડળીની માલિકી/કબજામાં હોવાનું દર્શાવેલ છે. અંતિમ તકનિકી/વહીવટી મંજૂરી પહેલાં અદ્યતન માલિકી, હક્ક અને કબજાના દસ્તાવેજો રજૂ કરવામાં આવશે.";
  if (data.leaseMode === "EXECUTED")
    return `રજિસ્ટર્ડ Lease Agreement કરવામાં આવેલ હોવાનું દર્શાવેલ છે${
      data.leaseAgreementNo
        ? `, દસ્તાવેજ ક્રમાંક ${pacsReportSafe(data.leaseAgreementNo)}`
        : ""
    }${
      data.leaseAgreementDate
        ? `, તારીખ ${pacsReportSafe(
            pacsReportDate(data.leaseAgreementDate),
          )}`
        : ""
    }. માંગણી થયે તેની મૂળ/પ્રમાણિત નકલ રજૂ કરવામાં આવશે.`;
  return "પ્રોજેક્ટ માટે જમીન લીઝ ઉપર લેવાની હોય તો DCDC મંજૂરી મળ્યા બાદ, પરંતુ અંતિમ તકનિકી/વહીવટી મંજૂરી, ટેન્ડર, વર્ક ઓર્ડર અથવા બાંધકામ શરૂ કરતાં પહેલાં, રજિસ્ટર્ડ Lease Agreement તથા સંબંધિત માલિકી/NOC દસ્તાવેજો પૂર્ણ કરીને રજૂ કરવામાં આવશે.";
};

const pacsReportLetterhead = (data, language) => {
  const gujarati = language === "GUJARATI";
  const location = [data.village, data.taluka, data.district, data.state]
    .filter(Boolean)
    .join(", ");
  const contact = [data.contactMobile, data.contactEmail]
    .filter(Boolean)
    .join(" | ");
  return `
    <header class="letterhead ${gujarati ? "gujarati" : ""}">
      <div class="program-tag">NAFED-LINKED GRAIN STORAGE PROJECT · PACS UNDERTAKING</div>
      <h1>${pacsReportSafe(data.societyName, "PACS / SOCIETY NAME")}</h1>
      <h2>${
        gujarati
          ? "પ્રાથમિક કૃષિ ધિરાણ સહકારી મંડળી (PACS)"
          : "Primary Agricultural Credit Cooperative Society (PACS)"
      }</h2>
      <p><b>${gujarati ? "નોંધણી ક્રમાંક" : "Registration No."}:</b> ${pacsReportSafe(
        data.registrationNo,
      )}</p>
      <p>${pacsReportSafe(data.address || location)}</p>
      ${contact ? `<p>${pacsReportEscape(contact)}</p>` : ""}
    </header>`;
};

const pacsReportSignatureGrid = (data, language) => {
  const gujarati = language === "GUJARATI";
  return `
    <div class="signature-grid">
      <div class="signature-card">
        <div class="signature-asset-frame">${pacsReportAsset(
          data.chairmanSignatureUrl,
          gujarati ? "ચેરમેનશ્રીની સહી" : "Chairman signature",
          gujarati ? "અહીં હાથથી સહી કરો" : "Sign here manually",
        )}</div>
        <span>${pacsReportSafe(data.chairmanName, gujarati ? "નામ" : "Name")}</span>
        <b>${gujarati ? "ચેરમેનશ્રી" : "Chairman / Chairperson"}</b>
        <small>${gujarati ? "અધિકૃત સહી" : "Authorised Signatory"}</small>
      </div>
      <div class="signature-card">
        <div class="signature-asset-frame">${pacsReportAsset(
          data.secretarySignatureUrl,
          gujarati ? "મંત્રી / સેક્રેટરીશ્રીની સહી" : "Secretary signature",
          gujarati ? "અહીં હાથથી સહી કરો" : "Sign here manually",
        )}</div>
        <span>${pacsReportSafe(data.secretaryName, gujarati ? "નામ" : "Name")}</span>
        <b>${gujarati ? "મંત્રી / સેક્રેટરીશ્રી" : "Secretary / Chief Executive"}</b>
        <small>${gujarati ? "અધિકૃત સહી" : "Authorised Signatory"}</small>
      </div>
      <div class="signature-card seal-card">
        <div class="signature-asset-frame seal-frame">${pacsReportAsset(
          data.societySealUrl,
          gujarati ? "મંડળીનો સત્તાવાર સિક્કો" : "Society official seal",
          gujarati ? "અહીં મંડળીનો સિક્કો કરો" : "Affix physical society seal here",
          "seal-image",
        )}</div>
        <span>${pacsReportSafe(data.societyName, gujarati ? "મંડળી / PACS" : "PACS / Society")}</span>
        <b>${gujarati ? "મંડળીનો સત્તાવાર સિક્કો" : "Society Official Seal"}</b>
        <small>${gujarati ? "અધિકૃત ગોળ સિક્કો" : "Official Round Seal"}</small>
      </div>
    </div>`;
};

const pacsEnglishRegistrationReport = (data) => {
  const location = [data.village, data.taluka, data.district, data.state]
    .filter(Boolean)
    .join(", ");
  const applicationReference = data.applicationId
    ? `${pacsReportSafe(data.applicationId)}${
        data.applicationDate
          ? ` dated ${pacsReportSafe(pacsReportDate(data.applicationDate))}`
          : ""
      }`
    : "________________";
  const resolutionReference = data.resolutionNo
    ? `${pacsReportSafe(data.resolutionNo)}${
        data.resolutionDate
          ? ` dated ${pacsReportSafe(pacsReportDate(data.resolutionDate))}`
          : ""
      }`
    : "________________";
  const days = pacsReportSafe(data.complianceDays || "60");
  const capacity = pacsReportEscape(pacsReportCapacity(data.capacityMt));
  const assurance = pacsReportAssuranceEnglish(data);
  const lease = pacsReportLeaseEnglish(data);

  return `
  <section class="page report-page english-page">
    ${pacsReportLetterhead(data, "ENGLISH")}
    <div class="letter-meta"><span><b>Ref. No.:</b> ${pacsReportSafe(
      data.reportNo,
    )}</span><span><b>Date:</b> ${pacsReportSafe(
      pacsReportDate(data.reportDate),
    )}</span></div>
    <div class="recipient"><b>To,</b><br>${pacsReportSafe(
      data.recipientOffice,
      "The District Registrar, Cooperative Societies",
    )}<br>${pacsReportSafe(data.district)} District, ${pacsReportSafe(
      data.state,
    )}</div>
    <div class="subject"><b>Subject:</b> Request to accept the Society's undertaking in lieu of the site-specific DPR, drawings and PWD/R&amp;B SOR-based detailed estimate at the preliminary DCDC approval stage, and to forward the proposal with positive recommendation to the State Registrar/competent authority.</div>
    <div class="references"><b>Reference:</b><ol>
      <li>PACS application No. ${applicationReference}.</li>
      <li>Managing Committee Resolution No. ${resolutionReference}.</li>
      <li>Requirement of “Technical File: Site-specific DPR, drawings and estimates as per PWD SOR”.</li>
      <li>Proposed NAFED Hiring Assurance, MoU/Hiring arrangement and land/lease compliance.</li>
    </ol></div>
    <p>Sir/Madam,</p>
    <p>We, <b>${pacsReportSafe(
      data.societyName,
    )}</b>, a registered Primary Agricultural Credit Cooperative Society bearing Registration No. <b>${pacsReportSafe(
      data.registrationNo,
    )}</b>, respectfully submit that the Society proposes to establish a <b>${capacity}</b> storage godown at ${pacsReportSafe(
      location,
    )}${data.surveyNo ? `, Survey/Block No. <b>${pacsReportSafe(data.surveyNo)}</b>` : ""}, under the NAFED-linked grain storage project.</p>
    <p>The proposal is presently under preliminary scrutiny and is required to be forwarded by the District Registrar to the State Registrar/competent authority for consideration and conditional/in-principle DCDC approval. The checklist requires a complete site-specific DPR, detailed survey, soil investigation, architectural and structural drawings, BOQ, quantity calculations, rate analysis and a detailed estimate based on the prevailing PWD/R&amp;B SOR.</p>
    <p>Preparation of the above technical file before project selection/conditional approval involves substantial and largely non-recoverable expenditure. If the project is not approved, such expenditure would unnecessarily burden the Society and its members' funds and may also invite an audit objection. The Society therefore seeks permission to incur this expenditure only after the project receives conditional/in-principle approval.</p>
    <table class="facts-table"><tbody>
      <tr><th>Society / PACS</th><td>${pacsReportSafe(data.societyName)}</td><th>Registration No.</th><td>${pacsReportSafe(data.registrationNo)}</td></tr>
      <tr><th>Application</th><td>${applicationReference}</td><th>Proposed Capacity</th><td>${capacity}</td></tr>
      <tr><th>Location</th><td>${pacsReportSafe(location)}</td><th>Survey / Block No.</th><td>${pacsReportSafe(data.surveyNo)}</td></tr>
      <tr><th>PMC</th><td>${pacsReportSafe(data.pmcName, "To be appointed/confirmed")}</td><th>Current Stage</th><td>Conditional / In-principle DCDC approval awaited</td></tr>
      <tr><th>NAFED State Branch</th><td>${pacsReportSafe(data.nafedBranchName)}</td><th>Compliance Period</th><td>${days} days after the triggering approvals</td></tr>
    </tbody></table>
    <div class="status-note"><b>NAFED position:</b> ${assurance}</div>
    <div class="status-note"><b>Land / lease position:</b> ${lease}</div>
    <h3 class="section-heading">UNDERTAKING OF THE SOCIETY</h3>
    <p>Through the undersigned authorised office-bearers and pursuant to the Managing Committee Resolution referred to above, the Society solemnly undertakes as follows:</p>
    <ol class="undertaking-list">
      <li>Upon receipt of conditional/in-principle DCDC approval and NAFED's written Hiring Assurance, whichever is later, the Society shall appoint a qualified registered/empanelled Civil Engineer or authorised PMC.</li>
      <li>Within <b>${days} days</b> from the later of the above events, or within the period prescribed by the competent authority, the Society shall prepare and submit the complete site-specific technical file.</li>
      <li>The technical file shall include the detailed DPR, site and level survey, soil/geotechnical investigation wherever required, site/layout plan, architectural and structural drawings, structural design calculations, detailed BOQ, quantity calculations, prevailing PWD/R&amp;B SOR-based estimate, rate analysis for Non-SOR items, abstract cost and all other prescribed technical documents.</li>
      <li>The final design, quantities and cost shall be based on actual site conditions, sanctioned capacity, soil-test results, applicable codes and the PWD/R&amp;B SOR prevailing on the date of preparation.</li>
      <li>${assurance}</li>
      <li>${lease}</li>
      <li>No tender shall be published, no work order shall be issued and no construction shall commence until all NAFED, land/lease, technical, financial and administrative requirements are completed.</li>
      <li>If the Society fails to submit the required documents within the stipulated period, the competent authority may cancel the conditional approval or take action under the applicable rules.</li>
      <li>If the project is not selected/approved, the Society shall not be compelled to incur the complete DPR and drawing expenditure merely because a preliminary application was filed.</li>
      <li>All particulars stated in this undertaking and application are true to the best of our knowledge, and the Society accepts responsibility for compliance.</li>
    </ol>
    <h3 class="section-heading">PRAYER / REQUEST</h3>
    <p>In view of the above facts and undertaking, the Society requests that this undertaking be accepted as <b>Post-Approval Compliance</b> in place of the complete technical file at the present preliminary selection/DCDC approval stage, and that the proposal be forwarded with a positive recommendation for conditional/in-principle approval.</p>
    <div class="approval-condition"><b>Suggested condition in the approval order:</b><br>“The Society shall submit the site-specific DPR, drawings, detailed BOQ, prevailing PWD/R&amp;B SOR-based estimate, NAFED MoU/Hiring Agreement and required registered Lease Agreement/land documents within ${days} days after conditional/in-principle DCDC approval and NAFED's written Hiring Assurance, whichever is later. No tender, work order or construction shall be undertaken before such compliance and final approval.”</div>
    <p class="closing">Yours faithfully,<br><b>For ${pacsReportSafe(
      data.societyName,
    )}</b></p>
    ${pacsReportSignatureGrid(data, "ENGLISH")}
    <div class="enclosures"><b>Enclosures:</b> Certified Managing Committee Resolution; PACS registration certificate; available land ownership/possession document; preliminary site details and photographs; NAFED assurance/correspondence, if received; and other supporting records.</div>
    <footer>System-generated draft from the PACS Digital Dashboard. Verify it and submit it on the PACS's official letterhead with authorised signatures and seal.</footer>
  </section>`;
};

const pacsGujaratiRegistrationReport = (data) => {
  const location = [data.village, data.taluka, data.district, data.state]
    .filter(Boolean)
    .join(", ");
  const applicationReference = data.applicationId
    ? `${pacsReportSafe(data.applicationId)}${
        data.applicationDate
          ? `, તારીખ ${pacsReportSafe(pacsReportDate(data.applicationDate))}`
          : ""
      }`
    : "________________";
  const resolutionReference = data.resolutionNo
    ? `${pacsReportSafe(data.resolutionNo)}${
        data.resolutionDate
          ? `, તારીખ ${pacsReportSafe(pacsReportDate(data.resolutionDate))}`
          : ""
      }`
    : "________________";
  const days = pacsReportSafe(data.complianceDays || "60");
  const capacity = pacsReportEscape(pacsReportCapacity(data.capacityMt));
  const assurance = pacsReportAssuranceGujarati(data);
  const lease = pacsReportLeaseGujarati(data);

  return `
  <section class="page report-page gujarati-page" lang="gu">
    ${pacsReportLetterhead(data, "GUJARATI")}
    <div class="letter-meta"><span><b>પત્ર ક્રમાંક:</b> ${pacsReportSafe(
      data.reportNo,
    )}</span><span><b>તારીખ:</b> ${pacsReportSafe(
      pacsReportDate(data.reportDate),
    )}</span></div>
    <div class="recipient"><b>પ્રતિ,</b><br>જિલ્લા રજિસ્ટ્રારશ્રી,<br>સહકારી મંડળીઓ, ${pacsReportSafe(
      data.district,
    )} જિલ્લો, ${pacsReportSafe(data.state)}.</div>
    <div class="subject"><b>વિષય:</b> પ્રાથમિક DCDC મંજૂરીના તબક્કે સ્થળ-વિશિષ્ટ DPR, નકશા તથા PWD/R&amp;B SOR મુજબના વિગતવાર અંદાજના સ્થાને મંડળીની બાંહેધરી સ્વીકારી, પ્રસ્તાવને હકારાત્મક ભલામણ સાથે રાજ્ય રજિસ્ટ્રારશ્રી/સક્ષમ સત્તા સમક્ષ મોકલવા બાબત.</div>
    <div class="references"><b>સંદર્ભ:</b><ol>
      <li>PACS અરજી ક્રમાંક ${applicationReference}.</li>
      <li>વ્યવસ્થાપક સમિતિનો ઠરાવ ક્રમાંક ${resolutionReference}.</li>
      <li>“ટેક્નિકલ ફાઇલ: સાઇટ સ્પેસિફિક DPR, નકશા અને PWD SOR મુજબના અંદાજો” રજૂ કરવાની જરૂરિયાત.</li>
      <li>NAFED તરફથી પ્રસ્તાવિત Hiring Assurance, MoU/Hiring વ્યવસ્થા તથા જમીન/લીઝ સંબંધિત અનુપાલન.</li>
    </ol></div>
    <p>માનનીય સાહેબશ્રી,</p>
    <p>આથી અમે <b>${pacsReportSafe(
      data.societyName,
    )}</b>, નોંધણી ક્રમાંક <b>${pacsReportSafe(
      data.registrationNo,
    )}</b> ધરાવતી પ્રાથમિક કૃષિ ધિરાણ સહકારી મંડળી (PACS), સવિનય રજૂઆત કરીએ છીએ કે મંડળી દ્વારા NAFED સાથે સંકળાયેલા અનાજ સંગ્રહ પ્રોજેક્ટ હેઠળ ${pacsReportSafe(
      location,
    )}${data.surveyNo ? ` ખાતે સર્વે/બ્લોક નં. <b>${pacsReportSafe(data.surveyNo)}</b>` : ""} ઉપર <b>${capacity}</b> ક્ષમતાનું ગોડાઉન બનાવવાનો પ્રસ્તાવ કરવામાં આવેલ છે.</p>
    <p>હાલ પ્રસ્તાવ પ્રાથમિક ચકાસણી હેઠળ છે અને જિલ્લા રજિસ્ટ્રારશ્રી મારફતે રાજ્ય રજિસ્ટ્રારશ્રી/સક્ષમ સત્તા સમક્ષ શરતી/સૈદ્ધાંતિક DCDC મંજૂરી માટે મોકલવાનો છે. ચેકલિસ્ટ મુજબ સ્થળ-વિશિષ્ટ વિગતવાર DPR, સર્વે, સોઇલ ઇન્વેસ્ટિગેશન, આર્કિટેક્ચરલ તથા સ્ટ્રક્ચરલ નકશા, BOQ, ક્વોન્ટિટી કેલ્ક્યુલેશન, રેટ એનાલિસિસ અને પ્રવર્તમાન PWD/R&amp;B SOR મુજબનો વિગતવાર અંદાજ માંગવામાં આવેલ છે.</p>
    <p>પ્રોજેક્ટની પસંદગી/શરતી મંજૂરી પહેલાં ઉપરોક્ત સંપૂર્ણ ટેક્નિકલ ફાઇલ તૈયાર કરાવવામાં નોંધપાત્ર અને મોટાભાગે પરત ન મળી શકે એવો ખર્ચ થાય છે. જો પ્રોજેક્ટને મંજૂરી ન મળે તો મંડળી તથા તેના સભાસદોના ભંડોળ ઉપર બિનજરૂરી નાણાકીય બોજ પડે અને ભવિષ્યમાં ઓડિટ વાંધો ઊભો થવાની શક્યતા રહે. તેથી મંડળી આ ખર્ચ DCDCની શરતી/સૈદ્ધાંતિક મંજૂરી મળ્યા બાદ જ કરવા માટે પરવાનગી માંગે છે.</p>
    <table class="facts-table"><tbody>
      <tr><th>મંડળી / PACS</th><td>${pacsReportSafe(data.societyName)}</td><th>નોંધણી ક્રમાંક</th><td>${pacsReportSafe(data.registrationNo)}</td></tr>
      <tr><th>અરજી</th><td>${applicationReference}</td><th>પ્રસ્તાવિત ક્ષમતા</th><td>${capacity}</td></tr>
      <tr><th>સ્થળ</th><td>${pacsReportSafe(location)}</td><th>સર્વે / બ્લોક નં.</th><td>${pacsReportSafe(data.surveyNo)}</td></tr>
      <tr><th>PMC</th><td>${pacsReportSafe(data.pmcName, "નિમણૂક/પુષ્ટિ બાકી")}</td><th>હાલનો તબક્કો</th><td>શરતી / સૈદ્ધાંતિક DCDC મંજૂરી બાકી</td></tr>
      <tr><th>NAFED State Branch</th><td>${pacsReportSafe(data.nafedBranchName)}</td><th>અનુપાલન સમયમર્યાદા</th><td>મંજૂરીની છેલ્લી ઘટનાથી ${days} દિવસ</td></tr>
    </tbody></table>
    <div class="status-note"><b>NAFED સંબંધિત સ્થિતિ:</b> ${assurance}</div>
    <div class="status-note"><b>જમીન / લીઝ સંબંધિત સ્થિતિ:</b> ${lease}</div>
    <h3 class="section-heading">મંડળીની બાંહેધરી</h3>
    <p>ઉપરોક્ત ઠરાવના આધારે મંડળીના અધિકૃત હોદ્દેદારો મારફતે નીચે મુજબની સ્પષ્ટ અને બિનશરતી બાંહેધરી આપવામાં આવે છે:</p>
    <ol class="undertaking-list">
      <li>DCDCની શરતી/સૈદ્ધાંતિક મંજૂરી તથા NAFEDનું લેખિત Hiring Assurance—બંનેમાંથી જે તારીખ છેલ્લી હોય ત્યારબાદ મંડળી લાયકાતપ્રાપ્ત રજિસ્ટર્ડ/એમ્પેનલ્ડ સિવિલ એન્જિનિયર અથવા અધિકૃત PMCની નિમણૂક કરશે.</li>
      <li>ઉપરોક્ત બંનેમાંથી છેલ્લી ઘટનાની તારીખથી <b>${days} દિવસની અંદર</b> અથવા સક્ષમ સત્તા દ્વારા નક્કી કરવામાં આવેલી સમયમર્યાદામાં મંડળી સંપૂર્ણ સ્થળ-વિશિષ્ટ ટેક્નિકલ ફાઇલ તૈયાર કરાવી રજૂ કરશે.</li>
      <li>ટેક્નિકલ ફાઇલમાં વિગતવાર DPR, સાઇટ તથા લેવલ સર્વે, જરૂરી સોઇલ/જિયોટેક્નિકલ તપાસ, સાઇટ/લેઆઉટ પ્લાન, આર્કિટેક્ચરલ અને સ્ટ્રક્ચરલ નકશા, સ્ટ્રક્ચરલ ગણતરી, વિગતવાર BOQ, ક્વોન્ટિટી કેલ્ક્યુલેશન, પ્રવર્તમાન PWD/R&amp;B SOR મુજબનો અંદાજ, Non-SOR વસ્તુઓ માટે રેટ એનાલિસિસ, એબ્સ્ટ્રેક્ટ કોસ્ટ અને અન્ય તમામ નિયત દસ્તાવેજો સામેલ રહેશે.</li>
      <li>અંતિમ ડિઝાઇન, માત્રાઓ તથા ખર્ચ સ્થળની વાસ્તવિક સ્થિતિ, મંજૂર ક્ષમતા, સોઇલ ટેસ્ટના પરિણામો, લાગુ કોડ/ધોરણો અને તૈયારીની તારીખે પ્રવર્તમાન PWD/R&amp;B SORના આધારે નક્કી કરવામાં આવશે.</li>
      <li>${assurance}</li>
      <li>${lease}</li>
      <li>NAFED સંબંધિત દસ્તાવેજો, જમીન/લીઝ અનુપાલન, સ્થળ-વિશિષ્ટ ટેક્નિકલ ફાઇલ તથા જરૂરી તકનિકી, નાણાકીય અને વહીવટી મંજૂરીઓ પૂર્ણ થયા પહેલાં કોઈ ટેન્ડર પ્રસિદ્ધ કરવામાં આવશે નહીં, વર્ક ઓર્ડર આપવામાં આવશે નહીં અને બાંધકામ શરૂ કરવામાં આવશે નહીં.</li>
      <li>નિયત સમયમર્યાદામાં જરૂરી દસ્તાવેજો રજૂ ન કરવામાં આવે તો સક્ષમ સત્તા શરતી મંજૂરી રદ કરી શકે અથવા લાગુ નિયમો મુજબ અન્ય કાર્યવાહી કરી શકે; એવો નિર્ણય મંડળીને બંધનકર્તા રહેશે.</li>
      <li>જો પ્રોજેક્ટની પસંદગી/મંજૂરી ન થાય તો માત્ર પ્રાથમિક અરજીના કારણે મંડળીને સંપૂર્ણ DPR અને નકશાનો ખર્ચ કરવા માટે ફરજ પાડવામાં આવશે નહીં.</li>
      <li>આ બાંહેધરી તથા અરજીમાં દર્શાવેલ તમામ વિગતો અમારી જાણ અને માન્યતા મુજબ સાચી છે અને તેના અનુપાલનની સંપૂર્ણ જવાબદારી મંડળી સ્વીકારે છે.</li>
    </ol>
    <h3 class="section-heading">વિનંતી</h3>
    <p>ઉપરોક્ત હકીકતો અને બાંહેધરીને ધ્યાને લઈ, હાલના પ્રાથમિક પસંદગી/DCDC મંજૂરીના તબક્કે સંપૂર્ણ ટેક્નિકલ ફાઇલના સ્થાને આ બાંહેધરીને <b>Post-Approval Compliance</b> તરીકે સ્વીકારવા વિનંતી છે. મંડળીના પ્રસ્તાવને હકારાત્મક અભિપ્રાય અને ભલામણ સાથે રાજ્ય રજિસ્ટ્રારશ્રી/સક્ષમ સત્તા સમક્ષ મોકલી શરતી/સૈદ્ધાંતિક DCDC મંજૂરી માટે રજૂ કરવા વિનંતી છે.</p>
    <div class="approval-condition"><b>મંજૂરી હુકમમાં સમાવવાની સૂચિત શરત:</b><br>“મંડળીએ DCDCની શરતી/સૈદ્ધાંતિક મંજૂરી તથા NAFEDનું લેખિત Hiring Assurance મળ્યા બાદ ${days} દિવસની અંદર અથવા નિયત સમયમર્યાદામાં સ્થળ-વિશિષ્ટ DPR, નકશા, વિગતવાર BOQ, પ્રવર્તમાન PWD/R&amp;B SOR મુજબનો અંદાજ, NAFED સાથેનો MoU/Hiring Agreement તથા જરૂરી રજિસ્ટર્ડ Lease Agreement/જમીન દસ્તાવેજો રજૂ કરવાના રહેશે. ઉપરોક્ત અનુપાલન અને અંતિમ મંજૂરી પહેલાં ટેન્ડર, વર્ક ઓર્ડર કે બાંધકામ હાથ ધરી શકાશે નહીં.”</div>
    <p class="closing">આપનો વિશ્વાસુ,<br><b>${pacsReportSafe(
      data.societyName,
    )} વતી</b></p>
    ${pacsReportSignatureGrid(data, "GUJARATI")}
    <div class="enclosures"><b>બિડાણ:</b> વ્યવસ્થાપક સમિતિના ઠરાવની પ્રમાણિત નકલ; PACSનું નોંધણી પ્રમાણપત્ર; ઉપલબ્ધ જમીન માલિકી/કબજા દસ્તાવેજ; સાઇટની પ્રાથમિક વિગતો તથા ફોટોગ્રાફ્સ; NAFEDનું Assurance/પત્રવ્યવહાર જો પ્રાપ્ત થયેલ હોય; તથા અન્ય આધાર દસ્તાવેજો.</div>
    <footer>આ ડ્રાફ્ટ PACS Digital Dashboardમાંથી સિસ્ટમ દ્વારા તૈયાર થયેલ છે. રજૂ કરતાં પહેલાં તેની ચકાસણી કરી PACSના સત્તાવાર લેટરહેડ ઉપર અધિકૃત સહી અને સિક્કા સાથે રજૂ કરવો.</footer>
  </section>`;
};

const buildPacsRegistrationReportHtml = (
  data,
  outputLanguage = "BILINGUAL",
  autoPrint = false,
) => {
  const pages = [];
  if (outputLanguage === "GUJARATI" || outputLanguage === "BILINGUAL")
    pages.push(pacsGujaratiRegistrationReport(data));
  if (outputLanguage === "ENGLISH" || outputLanguage === "BILINGUAL")
    pages.push(pacsEnglishRegistrationReport(data));
  const languageLabel =
    outputLanguage === "GUJARATI"
      ? "Gujarati"
      : outputLanguage === "ENGLISH"
        ? "English"
        : "Gujarati + English";

  return `<!doctype html>
  <html lang="en">
    <head>
      <meta charset="utf-8" />
      <meta name="viewport" content="width=device-width,initial-scale=1" />
      <title>${pacsReportEscape(
        data.societyName,
      )} - ${languageLabel} DCDC Undertaking</title>
      <style>
        @page{size:A4;margin:12mm 13mm 14mm}
        *{box-sizing:border-box}
        body{margin:0;background:#e9eef3;color:#111827;font-family:Arial,"Nirmala UI","Noto Sans Gujarati",sans-serif;-webkit-print-color-adjust:exact;print-color-adjust:exact}
        .report-page{width:210mm;min-height:297mm;margin:14px auto;padding:13mm 14mm 15mm;background:#fff;box-shadow:0 10px 35px rgba(15,23,42,.18);font-size:10.5pt;line-height:1.58;page-break-after:always}
        .report-page:last-child{page-break-after:auto}.gujarati-page{font-family:"Nirmala UI","Noto Sans Gujarati","Shruti",Arial,sans-serif;font-size:10.6pt;line-height:1.72}
        .letterhead{text-align:center;border-bottom:2.5px solid #0d613e;padding-bottom:9px;margin-bottom:10px}.letterhead .program-tag{display:inline-block;margin-bottom:6px;padding:4px 9px;border-radius:999px;background:#e8f5ed;color:#0d613e;font-size:7.5pt;font-weight:800;letter-spacing:.04em}
        .letterhead h1{margin:0;color:#082b55;font-size:16.5pt;line-height:1.25;text-transform:uppercase}.letterhead.gujarati h1{text-transform:none}.letterhead h2{margin:4px 0;color:#155f40;font-size:10pt}.letterhead p{margin:2px 0;font-size:8.5pt;line-height:1.35}
        .letter-meta{display:flex;justify-content:space-between;gap:20px;margin:9px 0;font-size:9.5pt}.recipient{margin:8px 0 11px;line-height:1.55}
        .subject,.references{margin:8px 0;padding:8px 9px;border:1px solid #9aa9b8;background:#f5f7f9;text-align:justify}.subject{border-left:4px solid #0d613e}.references ol{margin:5px 0 0;padding-left:20px}.references li{margin:2px 0}
        p{margin:7px 0;text-align:justify}.facts-table{width:100%;margin:10px 0;border-collapse:collapse;font-size:8.7pt}.facts-table th,.facts-table td{border:1px solid #4b5563;padding:6px 7px;text-align:left;vertical-align:top}.facts-table th{width:17%;background:#e8eef4;color:#153450}
        .status-note{margin:7px 0;padding:7px 9px;border-left:4px solid #c58413;background:#fff8e8;text-align:justify}.section-heading{margin:13px 0 6px;padding:6px 9px;border-left:5px solid #0d613e;background:#eaf4ee;color:#0d4a32;font-size:10.5pt;letter-spacing:.02em}
        .undertaking-list{margin:6px 0 10px;padding-left:23px}.undertaking-list li{margin:5px 0;text-align:justify;padding-left:2px}.approval-condition{margin:10px 0;padding:9px 10px;border:1.5px solid #0d613e;background:#f0f8f3;text-align:justify}.closing{margin-top:16px;text-align:left}
        .signature-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-top:28px;align-items:end}.signature-grid>div{min-height:132px;padding:7px 7px 6px;border-top:1px solid #263645;text-align:center}.signature-grid span,.signature-grid b,.signature-grid small{display:block}.signature-grid span{min-height:19px;margin-top:4px}.signature-grid b{margin-top:3px;font-size:8.5pt}.signature-grid small{margin-top:4px;color:#596579;font-size:7.5pt}
        .signature-asset-frame{height:70px;display:grid;place-items:center;margin-bottom:3px;overflow:hidden}.signature-asset{display:block;width:100%;height:100%;object-fit:contain}.signature-asset-empty{width:100%;padding:15px 5px;border:1px dashed #9aa6b2;background:#f8fafc;color:#788493;font-size:7.5pt}.signature-grid .seal-card{border:1px dashed #6b7280;border-top:1px dashed #6b7280;border-radius:50% 50% 12px 12px;padding:7px 12px 6px}.signature-grid .seal-frame{height:75px}.signature-grid .seal-image{max-width:105px;border-radius:50%}
        .enclosures{margin-top:14px;padding-top:8px;border-top:1px solid #8b96a3;font-size:8.5pt;text-align:justify}footer{margin-top:12px;padding-top:6px;border-top:1px solid #c6cdd4;color:#667085;font-size:7.2pt;text-align:center}
        @media print{body{background:#fff}.report-page{width:auto;min-height:auto;margin:0;padding:0;box-shadow:none}.report-page:not(:last-child){page-break-after:always}}
      </style>
    </head>
    <body>
      ${pages.join("\n")}
      ${
        autoPrint
          ? `<script>
              window.onload=function(){
                var printed=false;
                var printNow=function(){
                  if(printed)return;
                  printed=true;
                  setTimeout(function(){window.print();},250);
                };
                var images=Array.prototype.slice.call(document.images||[]);
                var pending=images.filter(function(image){return !image.complete;});
                if(!pending.length){printNow();return;}
                var completed=0;
                pending.forEach(function(image){
                  var done=function(){
                    completed+=1;
                    if(completed>=pending.length)printNow();
                  };
                  image.addEventListener("load",done,{once:true});
                  image.addEventListener("error",done,{once:true});
                });
                setTimeout(printNow,2500);
              };
            <\/script>`
          : ""
      }
    </body>
  </html>`;
};

const openPacsRegistrationReport = (
  data,
  outputLanguage,
  autoPrint = false,
) => {
  const reportWindow = window.open(
    "",
    "_blank",
    "width=1120,height=900,scrollbars=yes,resizable=yes",
  );
  if (!reportWindow) {
    window.alert(
      "Report window was blocked. Please allow pop-ups for this portal and try again.",
    );
    return;
  }
  reportWindow.document.open();
  reportWindow.document.write(
    buildPacsRegistrationReportHtml(data, outputLanguage, autoPrint),
  );
  reportWindow.document.close();
};

function PacsSubmittedApplicationPage({
  profile = {},
  profileSource = {},
  project = {},
  pmc = {},
  onSaved,
}) {
  const [form, setForm] = useState(() =>
    applicationFormFrom(project, profile),
  );
  const [baseline, setBaseline] = useState(() =>
    applicationFormFrom(project, profile),
  );
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (editing) return;
    const next = applicationFormFrom(project, profile);
    setForm(next);
    setBaseline(next);
  }, [project, profile, editing]);

  if (!project.id) {
    return (
      <section className="application-empty">
        <div className="application-empty-icon">AP</div>
        <small>NEW NAFED APPLICATION</small>
        <h2>No submitted application found</h2>
        <p>
          Complete and finally submit the grain storage application. The full
          application will then appear automatically in this Dashboard module.
        </p>
        <button onClick={() => window.location.assign("/nafed/apply-godown")}>
          Start New Application
        </button>
      </section>
    );
  }

  const applicationId =
    project.applicationId || project.projectId || project.id;
  const submittedAt =
    project.finalSubmittedAt ||
    project.submittedAt ||
    project.appliedAt ||
    project.createdAt;
  const workflow = deriveWorkflow(project, true);
  const applicationDocuments = collectDocuments(project);
  const applicationFieldCount = PACS_APPLICATION_SECTIONS.reduce(
    (total, section) => total + section.fields.length,
    0,
  );
  const editVersion = Number(
    project.pacsApplicationEditCount || project.applicationEditVersion || 0,
  );
  const changedKeys = Object.keys(form).filter(
    (key) => clean(form[key]) !== clean(baseline[key]),
  );
  const changedLabels = changedKeys.map((key) => {
    for (const section of PACS_APPLICATION_SECTIONS) {
      const field = section.fields.find((item) => item.name === key);
      if (field) return field.label;
    }
    return key;
  });
  const registrationReportData = buildPacsRegistrationReportData({
    form,
    project,
    profile,
    pmc,
  });
  const registrationReportMissing = [
    ["Society name", registrationReportData.societyName],
    ["Registration number", registrationReportData.registrationNo],
    ["District", registrationReportData.district],
    ["Application ID", registrationReportData.applicationId],
    ["Proposed capacity", registrationReportData.capacityMt],
    ["Survey / Block number", registrationReportData.surveyNo],
    ["Committee resolution number", registrationReportData.resolutionNo],
    ["Chairman name", registrationReportData.chairmanName],
    ["Secretary name", registrationReportData.secretaryName],
  ]
    .filter(([, value]) => !clean(value))
    .map(([label]) => label);
  const registrationReportVariants = [
    {
      language: "GUJARATI",
      number: "01",
      title: "Gujarati Application",
      subtitle: "ગુજરાતી અરજી અને મંડળીની બાંહેધરી",
    },
    {
      language: "ENGLISH",
      number: "02",
      title: "English Application",
      subtitle: "English application and Society undertaking",
    },
    {
      language: "BILINGUAL",
      number: "03",
      title: "Gujarati + English",
      subtitle: "Both language pages in one document",
    },
  ];

  const updateField = (name, value) => {
    setForm((old) => ({ ...old, [name]: value }));
    if (message) setMessage(null);
  };

  const startEditing = () => {
    const next = applicationFormFrom(project, profile);
    setForm(next);
    setBaseline(next);
    setMessage(null);
    setEditing(true);
  };

  const cancelEditing = () => {
    setForm(baseline);
    setMessage(null);
    setEditing(false);
  };

  const runRegistrationReport = async (language, action) => {
    if (editing) {
      setMessage({
        type: "info",
        text: "Save or cancel application editing before downloading the official report.",
      });
      return;
    }
    if (action === "DOWNLOAD") {
      try {
        const languageLabel =
          language === "GUJARATI"
            ? "Gujarati"
            : language === "ENGLISH"
              ? "English"
              : "Gujarati_English";
        await downloadOfficialPdf(
          buildPacsRegistrationReportHtml(
            registrationReportData,
            language,
            false,
          ),
          `${pacsReportFileSlug(
            registrationReportData.societyName,
          )}_DCDC_Undertaking_${languageLabel}.pdf`,
        );
      } catch (error) {
        setMessage({
          type: "error",
          text: error.message || "Application PDF could not be generated.",
        });
      }
    } else
      openPacsRegistrationReport(
        registrationReportData,
        language,
        action === "PRINT",
      );
  };

  const validate = () => {
    const required = [
      ["societyName", "Society / PACS name"],
      ["registrationNo", "Registration number"],
      ["state", "State"],
      ["district", "District"],
      ["taluka", "Taluka / Block"],
      ["village", "Village"],
      ["capacityMt", "Proposed capacity"],
    ];
    const missing = required.find(([key]) => !clean(form[key]));
    if (missing) return `${missing[1]} is required.`;
    if (!Number.isFinite(Number(form.capacityMt)) || Number(form.capacityMt) <= 0)
      return "Proposed capacity must be greater than zero.";
    if (
      form.existingCapacityMt &&
      (!Number.isFinite(Number(form.existingCapacityMt)) ||
        Number(form.existingCapacityMt) < 0)
    )
      return "Existing capacity cannot be negative.";
    if (form.pinCode && !/^\d{6}$/.test(clean(form.pinCode)))
      return "PIN code must contain exactly 6 digits.";
    for (const fieldName of PACS_APPLICATION_MOBILE_FIELDS) {
      if (
        form[fieldName] &&
        !/^\d{10}$/.test(clean(form[fieldName]).replace(/\D/g, ""))
      )
        return `${changedLabels.find((label, index) => changedKeys[index] === fieldName) || fieldName} must contain exactly 10 digits.`;
    }
    if (
      form.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean(form.email))
    )
      return "Please enter a valid official email address.";
    if (
      form.ifscCode &&
      !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(upper(form.ifscCode))
    )
      return "Please enter a valid 11-character IFSC code.";
    if (
      form.panNumber &&
      !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(upper(form.panNumber))
    )
      return "Please enter a valid PAN number.";
    if (
      form.gstNumber &&
      !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][A-Z0-9]Z[A-Z0-9]$/.test(
        upper(form.gstNumber),
      )
    )
      return "Please enter a valid GSTIN number.";
    if (
      form.activeMembers &&
      form.totalMembers &&
      Number(form.activeMembers) > Number(form.totalMembers)
    )
      return "Active members cannot exceed total members.";
    for (const section of PACS_APPLICATION_SECTIONS) {
      for (const field of section.fields) {
        if (field.type !== "number" || !clean(form[field.name])) continue;
        const numericValue = Number(form[field.name]);
        if (!Number.isFinite(numericValue))
          return `${field.label} must be a valid number.`;
        if (field.min !== undefined && numericValue < Number(field.min))
          return `${field.label} cannot be less than ${field.min}.`;
      }
    }
    if (
      form.latitude &&
      (!Number.isFinite(Number(form.latitude)) ||
        Number(form.latitude) < -90 ||
        Number(form.latitude) > 90)
    )
      return "Latitude must be between -90 and 90.";
    if (
      form.longitude &&
      (!Number.isFinite(Number(form.longitude)) ||
        Number(form.longitude) < -180 ||
        Number(form.longitude) > 180)
    )
      return "Longitude must be between -180 and 180.";
    return "";
  };

  const saveApplication = async () => {
    const validationError = validate();
    if (validationError) {
      setMessage({ type: "error", text: validationError });
      return;
    }
    if (!changedKeys.length) {
      setMessage({ type: "info", text: "No changes were made." });
      return;
    }

    setSaving(true);
    setMessage(null);
    try {
      const capacityMt = Number(form.capacityMt);
      const existingCapacityMt = form.existingCapacityMt
        ? Number(form.existingCapacityMt)
        : 0;
      const mobile = clean(form.mobile).replace(/\D/g, "");
      const latitude = form.latitude ? Number(form.latitude) : "";
      const longitude = form.longitude ? Number(form.longitude) : "";
      const nextVersion = editVersion + 1;
      const localEditedAt = new Date().toISOString();
      const editorUid = clean(auth.currentUser?.uid);
      const editorEmail = clean(
        auth.currentUser?.email || profile.email || form.email,
      );
      const ownerProfileId = clean(
        profileSource.documentId || profile.id || editorUid,
      );
      const ownerProfileCollection = clean(profileSource.collectionName);
      if (!ownerProfileCollection || !ownerProfileId) {
        throw new Error(
          "PACS login profile could not be linked. Please logout and login once.",
        );
      }
      if (
        clean(project.pacsOwnerAuthUid) &&
        clean(project.pacsOwnerAuthUid) !== editorUid
      ) {
        throw new Error("This application is linked with another PACS login.");
      }

      // One-time secure owner binding for legacy projects. After this small
      // write, application and MOU updates use the immutable Firebase Auth UID
      // and no longer depend on legacy state/district/profile formatting.
      if (clean(project.pacsOwnerAuthUid) !== editorUid) {
        await updateDoc(doc(db, "godown_projects", project.id), {
          pacsOwnerAuthUid: editorUid,
          pacsOwnerProfileId: ownerProfileId,
          pacsOwnerProfileCollection: ownerProfileCollection,
          pacsOwnerBoundAt: serverTimestamp(),
          pacsOwnerBoundAtIso: localEditedAt,
        });
      }
      const normalizedApplication = Object.fromEntries(
        Object.entries(form).map(([key, value]) => {
          if (PACS_APPLICATION_MOBILE_FIELDS.has(key))
            return [key, clean(value).replace(/\D/g, "")];
          if (["panNumber", "gstNumber", "ifscCode"].includes(key))
            return [key, upper(value)];
          if (PACS_APPLICATION_NUMERIC_FIELDS.has(key))
            return [key, clean(value) ? Number(value) : ""];
          return [key, clean(value)];
        }),
      );

      // Application identity fields are updated with supported aliases so the
      // existing PACS, State Branch and report screens remain in sync.
      // Workflow, approval, MOU, tender and construction fields are untouched.
      const payload = {
        ...normalizedApplication,
        // Secure bridge for legacy PACS profiles whose Firestore document ID
        // differs from the Firebase Auth UID. Rules verify this document's
        // email, PACS role and stored society identity before allowing writes.
        pacsOwnerAuthUid: editorUid,
        pacsOwnerProfileId: ownerProfileId,
        pacsOwnerProfileCollection: ownerProfileCollection,
        pacsApplicationDetails: normalizedApplication,
        societyName: clean(form.societyName),
        pacsName: clean(form.societyName),
        organizationName: clean(form.societyName),
        pacsRegistrationNo: clean(form.registrationNo),
        registrationNumber: clean(form.registrationNo),
        registrationNo: clean(form.registrationNo),
        registrationDate: clean(form.registrationDate),
        societyType: clean(form.societyType),
        projectName: clean(form.projectName),
        godownName: clean(form.projectName),
        state: clean(form.state),
        stateName: clean(form.state),
        district: clean(form.district),
        districtName: clean(form.district),
        taluka: clean(form.taluka),
        talukaName: clean(form.taluka),
        block: clean(form.taluka),
        village: clean(form.village),
        villageName: clean(form.village),
        address: clean(form.address),
        registeredAddress: clean(form.address),
        officeAddress: clean(form.address),
        siteAddress: clean(form.siteAddress || form.address),
        godownSiteAddress: clean(form.siteAddress || form.address),
        pinCode: clean(form.pinCode),
        pincode: clean(form.pinCode),
        proposedCapacityMt: capacityMt,
        proposedCapacityMT: capacityMt,
        capacityMt,
        capacityMT: capacityMt,
        capacity: capacityMt,
        existingCapacityMt,
        existingCapacityMT: existingCapacityMt,
        surveyNo: clean(form.surveyNo),
        landSurveyNo: clean(form.surveyNo),
        landArea: clean(form.landArea),
        landOwnership: clean(form.landOwnership),
        landOwnershipType: clean(form.landOwnership),
        ownershipType: clean(form.landOwnership),
        godownType: clean(form.godownType),
        warehouseType: clean(form.godownType),
        siteLatitude: latitude,
        siteLongitude: longitude,
        latitude,
        longitude,
        contactPerson: clean(form.contactPerson),
        contactPersonName: clean(form.contactPerson),
        authorizedPersonName: clean(form.applicantName || form.contactPerson),
        authorizedPersonDesignation: clean(
          form.applicantDesignation || form.contactDesignation,
        ),
        mobile,
        pacsMobile: mobile,
        mobileNumber: mobile,
        email: clean(form.email),
        pacsEmail: clean(form.email),
        officialEmail: clean(form.email),
        pan: upper(form.panNumber),
        gstin: upper(form.gstNumber),
        ifsc: upper(form.ifscCode),
        bankIFSC: upper(form.ifscCode),
        makanFund: normalizedApplication.buildingFund,
        makanFundAmount: normalizedApplication.buildingFund,
        reserveFundAmount: normalizedApplication.reserveFund,
        paidUpShareCapital: normalizedApplication.shareCapital,
        lastYearProfit: normalizedApplication.profitYear1,
        secondLastYearProfit: normalizedApplication.profitYear2,
        thirdLastYearProfit: normalizedApplication.profitYear3,
        committeeResolutionNo: clean(form.resolutionNo),
        managingCommitteeResolutionNo: clean(form.resolutionNo),
        resolutionNumber: clean(form.resolutionNo),
        resolutionNo: clean(form.resolutionNo),
        committeeResolutionDate: clean(form.resolutionDate),
        managingCommitteeResolutionDate: clean(form.resolutionDate),
        resolutionDate: clean(form.resolutionDate),
        leaseYears: normalizedApplication.leaseYears,
        dprSubmissionDays: normalizedApplication.technicalFileSubmissionDays,
        complianceDays: normalizedApplication.technicalFileSubmissionDays,
        nafedHiringAssuranceNo: clean(form.nafedAssuranceNo),
        hiringAssuranceNo: clean(form.nafedAssuranceNo),
        nafedHiringAssuranceDate: clean(form.nafedAssuranceDate),
        hiringAssuranceDate: clean(form.nafedAssuranceDate),
        hiringAssuranceStatus: clean(form.nafedAssuranceStatus),
        applicationRemarks: clean(form.remarks),
        remarks: clean(form.remarks),
        pacsApplicationEditCount: nextVersion,
        applicationEditVersion: nextVersion,
        applicationLastEditedAt: serverTimestamp(),
        applicationLastEditedByUid: editorUid,
        applicationLastEditedByEmail: editorEmail,
        updatedAt: serverTimestamp(),
        applicationEditHistory: arrayUnion({
          version: nextVersion,
          changedFields: changedLabels,
          editedAt: localEditedAt,
          editedByUid: editorUid,
          editedByEmail: editorEmail,
          source: "PACS_DASHBOARD",
        }),
      };

      await setDoc(doc(db, "godown_projects", project.id), payload, {
        merge: true,
      });
      const localChanges = {
        ...payload,
        applicationLastEditedAt: localEditedAt,
        updatedAt: localEditedAt,
      };
      delete localChanges.applicationEditHistory;
      onSaved?.(localChanges);
      setBaseline({ ...form });
      setEditing(false);
      setMessage({
        type: "success",
        text: `Application updated successfully. Edit version ${nextVersion} was recorded.`,
      });
    } catch (error) {
      console.error("PACS application update failed", error);
      setMessage({
        type: "error",
        text:
          error?.code === "permission-denied"
            ? "Update permission was denied. Please allow PACS users to update their own godown_projects record in Firestore Rules."
            : error?.message || "Application could not be updated.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="application-workspace">
      <header className="application-hero">
        <div>
          <small>FINAL SUBMITTED APPLICATION</small>
          <h2>{form.societyName || "PACS Grain Storage Application"}</h2>
          <p>
            All {applicationFieldCount} submitted application particulars and
            linked documents are shown here. PACS can correct details while
            every edit is versioned and the approval workflow remains
            unchanged.
          </p>
        </div>
        <div className="application-hero-actions">
          <span className="submitted-badge">Final Submitted</span>
          {!editing ? (
            <button className="application-edit-btn" onClick={startEditing}>
              Edit Application
            </button>
          ) : (
            <span className="editing-badge">Editing Draft</span>
          )}
        </div>
      </header>

      {message && (
        <div className={`application-message ${message.type}`}>
          {message.text}
          <button onClick={() => setMessage(null)}>Close</button>
        </div>
      )}

      <div className="application-summary">
        <Info label="Application ID" value={applicationId} />
        <Info label="Final Submitted On" value={formatDate(submittedAt)} />
        <Info
          label="Application Status"
          value={humanizeStatus(project.status || "SUBMITTED")}
        />
        <Info label="Current Workflow" value={workflow.stage} />
        <Info
          label="Proposed Capacity"
          value={`${numberText(form.capacityMt)} MT`}
        />
        <Info
          label="Edit Version"
          value={editVersion ? `Version ${editVersion}` : "Original submission"}
        />
      </div>

      <section className="pacs-application-report-center">
        <header>
          <div>
            <small>DIRECT PACS DOWNLOAD · NAFED REGISTRATION REPORT</small>
            <h3>Gujarati & English Application Report</h3>
            <p>
              The same State Branch Registration MIS application is now
              generated directly from this final PACS application. Signature
              and seal areas remain blank for manual signing after printing.
            </p>
          </div>
          <b className={registrationReportMissing.length ? "incomplete" : "ready"}>
            {registrationReportMissing.length
              ? `${registrationReportMissing.length} Detail(s) Pending`
              : "Report Ready"}
          </b>
        </header>

        <div className="pacs-report-download-grid">
          {registrationReportVariants.map((variant) => (
            <article key={variant.language}>
              <div className="pacs-report-language">
                <i>{variant.number}</i>
                <div>
                  <h4>{variant.title}</h4>
                  <span>{variant.subtitle}</span>
                </div>
              </div>
              <div className="pacs-report-actions">
                <button
                  type="button"
                  className="report-download-word"
                  onClick={() =>
                    runRegistrationReport(variant.language, "DOWNLOAD")
                  }
                  disabled={editing}
                >
                  Download PDF
                </button>
                <button
                  type="button"
                  className="report-print-pdf"
                  onClick={() =>
                    runRegistrationReport(variant.language, "PRINT")
                  }
                  disabled={editing}
                >
                  Print / Save PDF
                </button>
                <button
                  type="button"
                  className="report-preview"
                  onClick={() =>
                    runRegistrationReport(variant.language, "PREVIEW")
                  }
                  disabled={editing}
                >
                  Preview
                </button>
              </div>
            </article>
          ))}
        </div>

        {registrationReportMissing.length ? (
          <div className="pacs-report-warning">
            <b>Complete these details for the official draft:</b>
            <span>{registrationReportMissing.join(" · ")}</span>
          </div>
        ) : (
          <div className="pacs-report-complete">
            Draft ready. Download/print it, sign manually and affix the physical
            society seal before submission.
          </div>
        )}
      </section>

      {editing && (
        <div className="application-edit-notice">
          <b>Safe edit mode</b>
          <span>
            Only application particulars below will change. Status, State
            Branch approvals, PMC allocation, documents, MOU, tender and
            construction progress will remain protected.
          </span>
        </div>
      )}

      <div className={editing ? "application-form editing" : "application-form"}>
        {PACS_APPLICATION_SECTIONS.map((section, sectionIndex) => (
          <section className="application-section" key={section.title}>
            <header>
              <span>{String(sectionIndex + 1).padStart(2, "0")}</span>
              <div>
                <h3>{section.title}</h3>
                <p>{section.note}</p>
              </div>
            </header>
            <div className="application-fields">
              {section.fields.map((field) => (
                <label
                  className={field.wide ? "application-field wide" : "application-field"}
                  key={field.name}
                >
                  <span>
                    {field.label}
                    {field.required && <em>*</em>}
                  </span>
                  {editing ? (
                    field.type === "textarea" ? (
                      <textarea
                        value={form[field.name]}
                        onChange={(event) =>
                          updateField(field.name, event.target.value)
                        }
                        rows="3"
                      />
                    ) : field.type === "select" ? (
                      <select
                        value={form[field.name]}
                        onChange={(event) =>
                          updateField(field.name, event.target.value)
                        }
                      >
                        {(field.options || []).map((option) => (
                          <option value={option} key={option || "blank"}>
                            {option
                              ? humanizeStatus(option)
                              : "Select / Not provided"}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={field.type || "text"}
                        value={form[field.name]}
                        min={field.min}
                        step={field.step}
                        inputMode={field.inputMode}
                        onChange={(event) =>
                          updateField(field.name, event.target.value)
                        }
                      />
                    )
                  ) : (
                    <b>
                      {clean(form[field.name])
                        ? field.type === "select"
                          ? humanizeStatus(form[field.name])
                          : clean(form[field.name])
                        : "Not provided"}
                    </b>
                  )}
                </label>
              ))}
            </div>
          </section>
        ))}
      </div>

      {editing && (
        <footer className="application-save-bar">
          <div>
            <b>{changedKeys.length} field(s) changed</b>
            <span>
              {changedLabels.length
                ? changedLabels.join(" · ")
                : "Make a correction and save the application."}
            </span>
          </div>
          <button
            className="application-cancel-btn"
            onClick={cancelEditing}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className="application-save-btn"
            onClick={saveApplication}
            disabled={saving || !changedKeys.length}
          >
            {saving ? "Saving Changes..." : "Save Application Changes"}
          </button>
        </footer>
      )}

      <section className="application-document-register">
        <header>
          <div>
            <small>SUBMITTED DOCUMENT REGISTER</small>
            <h3>Application Documents and Supporting Evidence</h3>
            <p>
              Registration, land, resolution, financial, bank and technical
              files linked with this project are retained here.
            </p>
          </div>
          <b>{applicationDocuments.length} Files</b>
        </header>
        {applicationDocuments.length ? (
          <FileList
            files={applicationDocuments}
            openFile={(url) =>
              window.open(url, "_blank", "noopener,noreferrer")
            }
          />
        ) : (
          <Empty text="No supporting document is linked with this application yet. Use the Documents module to upload the official files." />
        )}
      </section>

      <div className="application-audit">
        <div>
          <small>RECORD CONTROL</small>
          <b>Original final submission is retained with edit history</b>
        </div>
        <Info
          label="Last Edited"
          value={formatDate(project.applicationLastEditedAt)}
        />
        <Info
          label="Last Edited By"
          value={
            project.applicationLastEditedByEmail ||
            project.applicationLastEditedByUid ||
            "No PACS edits recorded"
          }
        />
      </div>
    </section>
  );
}

function DocumentsPage({ documents, openFile, project }) {
  const pacs = documents.filter((item) => item.category !== "PMC");
  const pmc = documents.filter((item) => item.category === "PMC");
  const technicalUnlocked = Boolean(
    project.workStartedAt ||
      project.workStartDate ||
      project.workStarted === true ||
      ["STARTED", "IN_PROGRESS", "CONSTRUCTION_STARTED"].includes(
        String(project.workStatus || project.constructionStatus || "").toUpperCase(),
      ),
  );
  return (
    <div className="module">
      <div className="intro">
        <small>OFFICIAL PROJECT RECORDS</small>
        <h2>Document Repository</h2>
        <p>
          PACS documents and all technical records uploaded by the assigned PMC
          are available here automatically.
        </p>
      </div>
      <div className="document-stats">
        <Info label="PACS / Generated Files" value={`${pacs.length} Files`} />
        <Info label="PMC Technical Files" value={`${pmc.length} Files`} />
        <Info
          label="Last Project Update"
          value={formatDate(project.updatedAt)}
        />
      </div>
      <Panel title="PACS and System Generated Documents">
        <FileList files={pacs} openFile={openFile} />
        {!pacs.length && (
          <Empty text="Application and generated PACS documents are not available yet." />
        )}
      </Panel>
      <Panel title="PMC Technical and Tender Documents">
        {!technicalUnlocked && (
          <div className="technical-lock">
            <b>Technical Documents Securely Locked</b>
            <p>
              Survey, Soil Test, Design, Estimate and DPR files will become
              downloadable automatically after the PMC records Work Start and
              uploads the controlled files.
            </p>
          </div>
        )}
        <FileList files={pmc} openFile={openFile} locked={!technicalUnlocked} />
        {!pmc.length && (
          <Empty text="DPR, Survey Report, Soil Test, Design, Estimate, BOQ, Tender Copy and other PMC records will appear here after upload." />
        )}
      </Panel>
    </div>
  );
}

function PacsDprPage({ profile = {}, project = {}, onSaved }) {
  const [dpr, setDpr] = useState(null);
  const [loadingDpr, setLoadingDpr] = useState(true);
  const [signatureFile, setSignatureFile] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState("");
  const [signerName, setSignerName] = useState("");
  const [signerDesignation, setSignerDesignation] = useState(
    "Chairperson / Authorised Signatory",
  );
  const [confirmed, setConfirmed] = useState(false);
  const [signing, setSigning] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    setSignerName(
      clean(
        project.dprPacsSignerName ||
          project.chairmanName ||
          project.pacsChairmanName ||
          profile.chairmanName ||
          profile.name ||
          profile.fullName,
      ),
    );
    setSignerDesignation(
      clean(
        project.dprPacsSignerDesignation ||
          profile.designation ||
          "Chairperson / Authorised Signatory",
      ),
    );
  }, [project.id, project.dprPacsSignerName, project.chairmanName, profile]);

  useEffect(() => {
    if (!project?.id) {
      setDpr(null);
      setLoadingDpr(false);
      return undefined;
    }

    /*
      Backward-compatible project fallback.

      Some PMC deployments publish the manual DPR directly on the existing
      godown_projects record, while older deployments also create the
      published_dprs/{projectId} mirror. PACS must show the same uploaded PDF
      in either case, without changing the rest of the dashboard workflow.
    */
    const projectPdfUrl =
      project.dprOriginalPdfUrl || project.dprPdfUrl || project.dprUrl || "";
    const projectDpr = projectPdfUrl
      ? {
          id: project.id,
          projectId: project.id,
          applicationId:
            project.applicationId || project.projectId || project.id,
          societyName:
            project.societyName || project.pacsName || profile.societyName || "",
          pacsUid:
            project.pacsUid ||
            project.submittedByUid ||
            project.pacsUserId ||
            project.pacsId ||
            project.applicantUid ||
            "",
          originalPdfUrl: projectPdfUrl,
          originalPdfStoragePath: project.dprOriginalStoragePath || "",
          currentVersion: project.dprVersion || "V001",
          versionNumber: Number(project.dprVersionNumber || 1),
          fileName: project.dprFileName || "Detailed Project Report.pdf",
          fileSize: Number(project.dprFileSize || 0),
          mimeType: project.dprMimeType || "application/pdf",
          status: project.dprStatus || "PMC_PUBLISHED",
          sourceType: project.dprSourceType || "PMC_MANUAL_PDF",
          pmcSignatureIncludedInPdf:
            project.dprPmcSignatureIncludedInPdf !== false,
          finalSignedDprUrl:
            project.finalSignedDprUrl || project.finalDprUrl || "",
          publishedAt: project.dprPublishedAt,
          publishedAtIso: project.dprPublishedAtIso || "",
        }
      : null;

    setDpr(projectDpr);
    setLoadingDpr(true);
    return onSnapshot(
      doc(db, "published_dprs", project.id),
      (snapshot) => {
        const data = snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
        setDpr(
          data?.originalPdfUrl || data?.dprPdfUrl
            ? { ...(projectDpr || {}), ...data }
            : projectDpr,
        );
        setLoadingDpr(false);
      },
      (error) => {
        console.error("Manual DPR live listener failed:", error);
        setDpr(projectDpr);
        setLoadingDpr(false);
        if (!projectDpr) {
          setMessage({ type: "error", text: "Published DPR could not be loaded." });
        }
      },
    );
  }, [
    profile.societyName,
    project?.id,
    project?.applicationId,
    project?.projectId,
    project?.societyName,
    project?.pacsName,
    project?.pacsUid,
    project?.submittedByUid,
    project?.pacsUserId,
    project?.pacsId,
    project?.applicantUid,
    project?.dprOriginalPdfUrl,
    project?.dprPdfUrl,
    project?.dprUrl,
    project?.dprOriginalStoragePath,
    project?.dprVersion,
    project?.dprVersionNumber,
    project?.dprFileName,
    project?.dprFileSize,
    project?.dprMimeType,
    project?.dprStatus,
    project?.dprSourceType,
    project?.dprPmcSignatureIncludedInPdf,
    project?.finalSignedDprUrl,
    project?.finalDprUrl,
    project?.dprPublishedAt,
    project?.dprPublishedAtIso,
  ]);

  useEffect(() => {
    if (!signatureFile) {
      setSignaturePreview("");
      return undefined;
    }
    const url = URL.createObjectURL(signatureFile);
    setSignaturePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [signatureFile]);

  if (loadingDpr) {
    return <div className="module"><div className="empty">Loading PMC DPR…</div></div>;
  }

  if (!dpr) {
    return (
      <div className="module pacs-manual-dpr">
        <style>{PACS_DPR_STYLE}</style>
        <div className="pacs-dpr-hero waiting">
          <div><small>MANUAL DPR WORKFLOW</small><h2>Detailed Project Report</h2><p>The allocated PMC will upload the complete DPR PDF manually. It will appear here automatically for PACS download and signature.</p></div>
          <span>AWAITING PMC UPLOAD</span>
        </div>
        <div className="pacs-dpr-waiting">
          <b>PMC DPR Not Published Yet</b>
          <p>No auto-generated calculation or draft is shown. After the PMC publishes its complete signed PDF, PACS can preview, download and add only the PACS acceptance signature.</p>
        </div>
      </div>
    );
  }

  const originalPdfUrl =
    dpr.originalPdfUrl ||
    dpr.dprPdfUrl ||
    project.dprOriginalPdfUrl ||
    project.dprPdfUrl ||
    project.dprUrl;
  const finalSignedDprUrl =
    project.finalSignedDprUrl ||
    project.finalDprUrl ||
    dpr.finalSignedDprUrl;
  const version = dpr.currentVersion || project.dprVersion || "V001";
  const societyName =
    dpr.societyName ||
    project.societyName ||
    project.pacsName ||
    profile.societyName ||
    "PACS / Society";
  const appId = dpr.applicationId || project.applicationId || project.projectId || project.id;

  async function finaliseDpr() {
    setMessage(null);
    const currentUser = auth.currentUser;
    if (!currentUser?.uid) {
      setMessage({ type: "error", text: "Please sign in again before applying the PACS signature." });
      return;
    }
    if (!originalPdfUrl) {
      setMessage({ type: "error", text: "The PMC-uploaded source DPR is missing." });
      return;
    }
    if (!signatureFile) {
      setMessage({ type: "error", text: "Select the PACS signature PNG/JPG." });
      return;
    }
    if (!["image/png", "image/jpeg", "image/jpg"].includes(signatureFile.type)) {
      setMessage({ type: "error", text: "PACS signature must be a PNG or JPG image." });
      return;
    }
    if (signatureFile.size > 5 * 1024 * 1024) {
      setMessage({ type: "error", text: "PACS signature must be smaller than 5 MB." });
      return;
    }
    if (!clean(signerName) || !clean(signerDesignation)) {
      setMessage({ type: "error", text: "Enter the PACS signatory name and designation." });
      return;
    }
    if (!confirmed) {
      setMessage({ type: "error", text: "Confirm the DPR review declaration." });
      return;
    }
    if (finalSignedDprUrl && !window.confirm("Replace the existing PACS execution signature and regenerate the final DPR?")) return;

    setSigning(true);
    try {
      const output = await buildFinalSignedDprPdf({
        sourcePdfUrl: originalPdfUrl,
        pacsSignatureFile: signatureFile,
        dpr,
        project,
        pacsSignerName: clean(signerName),
        pacsSignerDesignation: clean(signerDesignation),
      });
      const safeVersion = String(version).replace(/[^a-z0-9_-]+/gi, "-");
      const basePath = `nafed_pacs/${currentUser.uid}/dpr/${project.id}/${safeVersion}`;
      const signaturePath = `${basePath}/pacs-signature-${Date.now()}-${signatureFile.name.replace(/[^a-z0-9._-]+/gi, "-")}`;
      const finalPdfPath = `${basePath}/${output.fileName}`;
      const signatureRef = ref(storage, signaturePath);
      const finalPdfRef = ref(storage, finalPdfPath);
      await Promise.all([
        uploadBytes(signatureRef, signatureFile, {
          contentType: signatureFile.type,
          customMetadata: {
            projectId: project.id,
            applicationId: String(appId || ""),
            version,
            signerName: clean(signerName),
            signerDesignation: clean(signerDesignation),
          },
        }),
        uploadBytes(finalPdfRef, output.blob, {
          contentType: "application/pdf",
          customMetadata: {
            projectId: project.id,
            applicationId: String(appId || ""),
            version,
            pageCount: String(output.pageCount),
            executionType: "PMC_PACS_DPR",
          },
        }),
      ]);
      const [pacsSignatureUrl, signedUrl] = await Promise.all([
        getDownloadURL(signatureRef),
        getDownloadURL(finalPdfRef),
      ]);
      const signedAtIso = new Date().toISOString();
      const changes = {
        dprStatus: "PACS_SIGNED_FINAL",
        dprPacsSignatureUrl: pacsSignatureUrl,
        dprPacsSignatureStoragePath: signaturePath,
        dprPacsSignerName: clean(signerName),
        dprPacsSignerDesignation: clean(signerDesignation),
        dprPacsSignedAtIso: signedAtIso,
        dprPacsSignedAt: serverTimestamp(),
        finalDprUrl: signedUrl,
        finalSignedDprUrl: signedUrl,
        dprUrl: signedUrl,
        dprPdfUrl: signedUrl,
        finalSignedDprStoragePath: finalPdfPath,
        finalSignedDprFileName: output.fileName,
        currentStage: "Final DPR Signed by PACS",
        updatedAt: serverTimestamp(),
      };
      await updateDoc(doc(db, "godown_projects", project.id), changes);
      const localChanges = {
        ...changes,
        dprPacsSignedAt: new Date(),
        updatedAt: new Date(),
      };
      onSaved?.(localChanges);
      setSignatureFile(null);
      setConfirmed(false);
      setMessage({ type: "success", text: "Final DPR generated successfully. The original PMC-signed pages are preserved and the PACS signature is added on the last acceptance page." });
    } catch (error) {
      console.error("PACS DPR execution failed:", error);
      const permissionError =
        error?.code === "permission-denied" ||
        error?.code === "storage/unauthorized";
      setMessage({
        type: "error",
        text: permissionError
          ? "PACS DPR save permission was denied. Allow this PACS owner to upload inside its nafed_pacs folder and update its own godown_projects record."
          : error?.message || "Final signed DPR could not be generated.",
      });
    } finally {
      setSigning(false);
    }
  }

  return (
    <div className="module pacs-manual-dpr">
      <style>{PACS_DPR_STYLE}</style>
      <div className={`pacs-dpr-hero ${finalSignedDprUrl ? "final" : "published"}`}>
        <div><small>PMC MANUAL DPR · CONTROLLED VERSION</small><h2>{dpr.fileName || "Detailed Project Report"}</h2><p>{societyName} · {appId} · {version}</p></div>
        <span>{finalSignedDprUrl ? "PACS SIGNED FINAL" : "PACS SIGNATURE PENDING"}</span>
      </div>

      {message && <div className={`pacs-dpr-message ${message.type}`}><span>{message.text}</span><button type="button" onClick={() => setMessage(null)}>×</button></div>}

      <div className="pacs-dpr-status-grid">
        <Info label="PMC DPR" value="Manual PDF Published" />
        <Info label="DPR Version" value={version} />
        <Info label="PMC Signature" value="Included in PDF" />
        <Info label="PACS Signature" value={finalSignedDprUrl ? "Recorded" : "Pending"} />
      </div>

      <section className="pacs-dpr-document">
        <div className="pacs-dpr-pdf-icon">PDF</div>
        <div><small>PMC UPLOADED & SIGNED SOURCE DPR</small><h3>{dpr.fileName || "Manual Detailed Project Report.pdf"}</h3><p>The complete PMC-signed source pages remain unchanged. PACS execution adds only one professional PACS acceptance page.</p></div>
        <div>
          <button type="button" onClick={() => window.open(originalPdfUrl, "_blank", "noopener,noreferrer")}>Preview Source</button>
          <button type="button" onClick={() => downloadFile(originalPdfUrl, dpr.fileName || `DPR_${appId}_${version}.pdf`)}>Download Source</button>
        </div>
      </section>

      {!finalSignedDprUrl ? (
        <section className="pacs-dpr-signing">
          <header><div><small>FINAL PACS EXECUTION</small><h3>Apply PACS Signature & Create Final DPR</h3><p>The PMC signature already exists inside the uploaded DPR. Only your PACS acceptance signature is placed on the dedicated final page.</p></div><span>LAST PAGE</span></header>
          <div className="pacs-dpr-signing-fields">
            <label><span>PACS Signatory Name *</span><input value={signerName} onChange={(event) => setSignerName(event.target.value)} placeholder="Full legal name" /></label>
            <label><span>Designation *</span><input value={signerDesignation} onChange={(event) => setSignerDesignation(event.target.value)} placeholder="Chairperson / Authorised Signatory" /></label>
            <label className="wide"><span>PACS Signature PNG/JPG *</span><input type="file" accept="image/png,image/jpeg,.png,.jpg,.jpeg" onChange={(event) => setSignatureFile(event.target.files?.[0] || null)} />{signaturePreview && <div className="pacs-dpr-signature-preview"><img src={signaturePreview} alt="PACS signature preview" /><b>{signerName || "PACS Authorised Signatory"}</b><small>{signerDesignation}</small></div>}</label>
          </div>
          <label className="pacs-dpr-confirm"><input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} /><span>I have reviewed the complete PMC-uploaded DPR and authorise this PACS signature to be applied to the controlled final execution page.</span></label>
          <footer><div><b>Original PMC-signed DPR + PACS acceptance signature</b><span>The signed result is stored as a new final PDF; every PMC source page is preserved.</span></div><button type="button" disabled={signing} onClick={finaliseDpr}>{signing ? "Generating & Uploading…" : "Apply Signature & Finalise DPR"}</button></footer>
        </section>
      ) : (
        <section className="pacs-dpr-final-download">
          <div><small>FINAL EXECUTED DOCUMENT</small><h3>PMC-Signed DPR with PACS Acceptance</h3><p>The PMC signature remains in the source DPR and the PACS signature appears on the appended final page.</p></div>
          <button type="button" onClick={() => window.open(finalSignedDprUrl, "_blank", "noopener,noreferrer")}>Preview Final DPR</button>
          <button type="button" onClick={() => downloadFile(finalSignedDprUrl, project.finalSignedDprFileName || `FINAL_DPR_${appId}_${version}.pdf`)}>Download Final Signed DPR</button>
        </section>
      )}

      <div className="pacs-dpr-note"><b>Controlled document rule:</b> A new PMC replacement version clears the previous PACS execution and requires a fresh PACS signature. No other dashboard module or project record is deleted.</div>
    </div>
  );
}

const PACS_DPR_STYLE = `
.pacs-manual-dpr{--navy:#10375f;--green:#0a7544;--ink:#17324a;--muted:#65798a;--line:#d1dde7;color:var(--ink);font-family:Inter,"Segoe UI",Arial,sans-serif}.pacs-manual-dpr *{box-sizing:border-box}.pacs-dpr-hero{display:flex;justify-content:space-between;align-items:flex-start;gap:20px;padding:22px;border:1px solid #b9d4c5;border-top:5px solid var(--green);border-radius:11px;background:linear-gradient(120deg,#fff,#eef8f3)}.pacs-dpr-hero small,.pacs-dpr-document small,.pacs-dpr-signing small,.pacs-dpr-final-download small{color:var(--green);font-size:8.5px;font-weight:900;letter-spacing:.09em}.pacs-dpr-hero h2{margin:5px 0;color:var(--navy);font-size:25px}.pacs-dpr-hero p{margin:0;color:var(--muted);font-size:10px}.pacs-dpr-hero>span{padding:9px 12px;border-radius:20px;background:#fff1d5;color:#8a5a00;font-size:8.5px;font-weight:900;white-space:nowrap}.pacs-dpr-hero.final>span{background:#e4f6ea;color:#08703d}.pacs-dpr-hero.waiting>span{background:#eef2f5;color:#56697a}.pacs-dpr-waiting{margin-top:13px;padding:17px;border:1px solid #e5c57c;border-left:5px solid #d58a1c;border-radius:8px;background:#fff8e9}.pacs-dpr-waiting b{color:#81520b;font-size:13px}.pacs-dpr-waiting p{margin:6px 0 0;color:#7a623c;font-size:10px;line-height:1.55}.pacs-dpr-message{display:flex;justify-content:space-between;align-items:center;gap:15px;margin:12px 0;padding:11px 13px;border-radius:6px;font-size:9.5px;font-weight:800}.pacs-dpr-message.success{border:1px solid #9dc9ac;background:#ecf8f0;color:#126337}.pacs-dpr-message.error{border:1px solid #e0a2a2;background:#fff0f0;color:#a22929}.pacs-dpr-message button{border:0;background:transparent;color:inherit;font-size:16px;cursor:pointer}.pacs-dpr-status-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin:13px 0}.pacs-dpr-status-grid>.info{min-height:75px;padding:13px;border:1px solid var(--line);border-radius:8px;background:#fff}.pacs-dpr-status-grid .info span,.pacs-dpr-status-grid .info b{display:block}.pacs-dpr-status-grid .info span{font-size:8.5px;color:var(--muted)}.pacs-dpr-status-grid .info b{margin-top:7px;color:var(--navy);font-size:11px}.pacs-dpr-document{display:grid;grid-template-columns:auto 1fr auto;align-items:center;gap:14px;padding:16px;border:1px solid var(--line);border-radius:9px;background:#fff}.pacs-dpr-pdf-icon{width:52px;height:52px;display:grid;place-items:center;border-radius:8px;background:#c72f39;color:#fff;font-size:10px;font-weight:950}.pacs-dpr-document h3,.pacs-dpr-signing h3,.pacs-dpr-final-download h3{margin:5px 0;color:var(--navy);font-size:15px}.pacs-dpr-document p,.pacs-dpr-signing p,.pacs-dpr-final-download p{margin:0;color:var(--muted);font-size:9px;line-height:1.5}.pacs-dpr-document>div:last-child{display:flex;gap:7px}.pacs-dpr-document button,.pacs-dpr-final-download button{padding:9px 11px;border:1px solid #9db1c1;border-radius:6px;background:#fff;color:var(--navy);font-size:8.5px;font-weight:900;cursor:pointer}.pacs-dpr-document button+button,.pacs-dpr-final-download button:last-child{border-color:var(--green);background:var(--green);color:#fff}.pacs-dpr-signing{overflow:hidden;margin-top:12px;border:1px solid var(--line);border-radius:9px;background:#fff}.pacs-dpr-signing>header{display:flex;justify-content:space-between;gap:14px;padding:15px 17px;border-bottom:1px solid var(--line);background:#f7f9fb}.pacs-dpr-signing>header>span{height:max-content;padding:7px 9px;border-radius:14px;background:#eaf2fa;color:var(--navy);font-size:8px;font-weight:900}.pacs-dpr-signing-fields{display:grid;grid-template-columns:1fr 1fr;gap:13px;padding:17px}.pacs-dpr-signing-fields label.wide{grid-column:1/-1}.pacs-dpr-signing-fields label>span{display:block;margin-bottom:6px;color:#405a70;font-size:8.5px;font-weight:850}.pacs-dpr-signing-fields input{width:100%;min-height:41px;padding:9px 10px;border:1px solid #b8c7d4;border-radius:6px;color:var(--ink);font:inherit;font-size:10px;outline:0}.pacs-dpr-signing-fields input:focus{border-color:var(--green);box-shadow:0 0 0 3px #e2f3e9}.pacs-dpr-signature-preview{min-height:126px;margin-top:9px;padding:11px;border:1px dashed #aabcc9;border-radius:7px;background:#fbfcfd;text-align:center}.pacs-dpr-signature-preview img{display:block;width:210px;height:70px;margin:auto;object-fit:contain}.pacs-dpr-signature-preview b,.pacs-dpr-signature-preview small{display:block}.pacs-dpr-signature-preview b{margin-top:5px;color:var(--navy);font-size:10px}.pacs-dpr-signature-preview small{margin-top:2px;color:var(--muted);letter-spacing:0}.pacs-dpr-confirm{display:flex;align-items:flex-start;gap:9px;margin:0 17px 15px;padding:11px;border:1px solid #cbd6df;border-radius:6px;background:#f6f8fa;color:#4b6174;font-size:8.5px;line-height:1.5}.pacs-dpr-confirm input{margin-top:2px}.pacs-dpr-signing footer{display:flex;align-items:center;gap:14px;padding:13px 17px;border-top:1px solid var(--line);background:#f7f9fb}.pacs-dpr-signing footer>div{margin-right:auto}.pacs-dpr-signing footer b,.pacs-dpr-signing footer span{display:block}.pacs-dpr-signing footer b{font-size:9px;color:var(--navy)}.pacs-dpr-signing footer span{margin-top:3px;color:var(--muted);font-size:8px}.pacs-dpr-signing footer button{min-width:210px;padding:11px 14px;border:0;border-radius:6px;background:var(--green);color:#fff;font-size:9px;font-weight:900;cursor:pointer}.pacs-dpr-signing footer button:disabled{cursor:not-allowed;opacity:.45}.pacs-dpr-final-download{display:grid;grid-template-columns:1fr auto auto;align-items:center;gap:10px;margin-top:12px;padding:18px;border:1px solid #99c8a9;border-top:4px solid var(--green);border-radius:9px;background:#eff9f3}.pacs-dpr-note{margin-top:12px;padding:11px;border:1px solid #cbd7df;border-radius:7px;background:#f8fafb;color:#5d7181;font-size:8.5px;line-height:1.5}@media(max-width:760px){.pacs-dpr-hero{display:block}.pacs-dpr-hero>span{display:inline-block;margin-top:10px}.pacs-dpr-status-grid{grid-template-columns:1fr 1fr}.pacs-dpr-document{grid-template-columns:auto 1fr}.pacs-dpr-document>div:last-child{grid-column:1/-1}.pacs-dpr-signing-fields{grid-template-columns:1fr}.pacs-dpr-signing-fields label.wide{grid-column:auto}.pacs-dpr-signing footer,.pacs-dpr-final-download{align-items:stretch;grid-template-columns:1fr;flex-direction:column}.pacs-dpr-signing footer button{width:100%}}
`;

function FileList({ files, openFile, locked = false }) {
  return (
    <div className="files">
      {files.map((item, index) => (
        <div className="file" key={`${item.url}-${index}`}>
          <div className="file-type">PDF</div>
          <div>
            <b>{item.name}</b>
            <span>
              {item.category} | {item.code || "DOCUMENT"} |{" "}
              {formatDate(item.date)}
            </span>
          </div>
          <em>{item.status}</em>
          <button
            disabled={locked}
            onClick={() => !locked && openFile(item.url)}
          >
            {locked ? "Locked Until Tender Award" : "View / Download"}
          </button>
        </div>
      ))}
    </div>
  );
}
function Kpi({ title, value, note }) {
  return (
    <div className="kpi">
      <span>{title}</span>
      <h2>{value}</h2>
      <p>{note}</p>
    </div>
  );
}
function Info({ label, value }) {
  return (
    <div className="info">
      <span>{label}</span>
      <b>{value || "Not available"}</b>
    </div>
  );
}
function Panel({ title, action, click, children }) {
  return (
    <section className="panel">
      <header>
        <h2>{title}</h2>
        {action && <button onClick={click}>{action}</button>}
      </header>
      {children}
    </section>
  );
}
function Empty({ text }) {
  return <div className="empty">{text}</div>;
}
function HelpCenterPage({
  societyName,
  pmc,
  pmcName,
  pmcPhone,
  pmcEmail,
  pmcAllocated,
}) {
  const address = clean(
    pmc?.officeAddress ||
      pmc?.address ||
      pmc?.registeredOfficeAddress ||
      pmc?.city,
  );

  if (!pmcAllocated) {
    return (
      <section className="help-center locked-help">
        <div className="help-shield">?</div>
        <small>SECURE ALLOCATION-BASED SUPPORT</small>
        <h2>PMC Help Center is not assigned yet</h2>
        <p>
          The State Branch has not allocated a PMC to {societyName}. No generic
          or unrelated PMC number is displayed. This screen will unlock in real
          time immediately after allocation.
        </p>
      </section>
    );
  }

  return (
    <section className="help-center">
      <div className="help-title">
        <div className="help-shield">24×7</div>
        <div>
          <small>YOUR AUTHORISED PMC SUPPORT DESK</small>
          <h2>{pmcName}</h2>
          <p>Support access mapped exclusively to {societyName}.</p>
        </div>
        <b>Verified Allocation</b>
      </div>
      <div className="help-contact-grid">
        <div>
          <span>Official Help Number</span>
          <strong>{pmcPhone || "Number not provided by PMC"}</strong>
          {pmcPhone && (
            <a className="call-action" href={`tel:${safePhoneHref(pmcPhone)}`}>
              Call Allocated PMC
            </a>
          )}
        </div>
        <div>
          <span>Official Email</span>
          <strong>{pmcEmail || "Email not provided by PMC"}</strong>
          {pmcEmail && (
            <a className="mail-action" href={`mailto:${pmcEmail}`}>
              Email Support
            </a>
          )}
        </div>
        <div>
          <span>PMC Office</span>
          <strong>{address || "Address not provided"}</strong>
          <small>
            Contact details update automatically from the allocated PMC profile.
          </small>
        </div>
      </div>
      <div className="privacy-note">
        For security, contacts of other PMCs are never shown to this PACS.
      </div>
    </section>
  );
}
function OperationalPage({
  title,
  project,
  documents,
  openFile,
  pmcAllocated,
  workStarted,
}) {
  if (title === "Tender")
    return (
      <TenderPage project={project} documents={documents} openFile={openFile} />
    );
  if (title === "Construction")
    return (
      <ConstructionPage
        project={project}
        pmcAllocated={pmcAllocated}
        workStarted={workStarted}
      />
    );
  if (title === "Bills")
    return (
      <RegisterPage
        title="RA Bill Register"
        rows={project.raBills || project.bills || []}
        type="bill"
      />
    );
  if (title === "Payments")
    return (
      <RegisterPage
        title="Payment Register"
        rows={project.payments || project.paymentRecords || []}
        type="payment"
      />
    );
  return null;
}

function TenderPage({ project, documents, openFile }) {
  const tenderFiles = documents.filter((item) =>
    /tender|boq|award|work order|loa/i.test(item.name),
  );
  return (
    <div className="operation-page">
      <div className="operation-hero">
        <small>PROCUREMENT WORKSPACE</small>
        <h2>Tender and Award Status</h2>
        <p>
          Live tender information, contractor award and official procurement
          records.
        </p>
      </div>
      <div className="operation-kpis">
        <Info
          label="Tender Number"
          value={project.tenderNumber || project.tenderId}
        />
        <Info
          label="Tender Status"
          value={project.tenderStatus || "Not initiated"}
        />
        <Info
          label="Awarded Contractor"
          value={project.contractorName || project.awardedContractorName}
        />
        <Info
          label="Award Value"
          value={
            project.l1Amount
              ? `INR ${numberText(project.l1Amount)}`
              : "Not available"
          }
        />
      </div>
      <Panel title="Tender Milestones">
        <div className="milestones">
          <Milestone
            label="Tender Published"
            done={project.tenderPublishedAt}
            date={project.tenderPublishedAt}
          />
          <Milestone
            label="Bid Opening"
            done={project.bidOpeningAt}
            date={project.bidOpeningAt}
          />
          <Milestone
            label="L1 Approved"
            done={project.l1ApprovedAt || project.contractorId}
            date={project.l1ApprovedAt}
          />
          <Milestone
            label="LOA Issued"
            done={project.loaPdfUrl || project.loaGenerateDate}
            date={project.loaGenerateDate}
          />
        </div>
      </Panel>
      <Panel title="Tender and Award Documents">
        <FileList files={tenderFiles} openFile={openFile} />
        {!tenderFiles.length && (
          <Empty text="Tender, BOQ, comparative statement and LOA documents will appear automatically." />
        )}
      </Panel>
    </div>
  );
}

function ConstructionPage({ project, pmcAllocated, workStarted }) {
  const milestones = Array.isArray(project.milestones)
    ? project.milestones
    : [];
  const progress = Math.max(
    0,
    Math.min(
      100,
      Number(project.progress || project.constructionProgress || 0),
    ),
  );
  return (
    <div className="operation-page">
      <div className="operation-hero">
        <small>PROJECT EXECUTION CONTROL</small>
        <h2>Construction Monitoring</h2>
        <p>
          Track work start, physical progress, milestones and PMC verification.
        </p>
      </div>
      <div className="construction-board">
        <div className="progress-display">
          <div style={{ "--progress": `${progress * 3.6}deg` }}>
            <b>{progress}%</b>
          </div>
          <span>Physical Progress</span>
        </div>
        <div className="operation-kpis">
          <Info
            label="PMC Allocation"
            value={pmcAllocated ? "Completed" : "Pending"}
          />
          <Info
            label="Work Status"
            value={workStarted ? "Work Started" : "Not started"}
          />
          <Info
            label="Start Date"
            value={formatDate(project.workStartDate || project.workStartedAt)}
          />
          <Info
            label="Contract Duration"
            value={
              project.workDurationDays
                ? `${project.workDurationDays} Days`
                : "Not available"
            }
          />
        </div>
      </div>
      <Panel title="Execution Milestones">
        <div className="milestone-list">
          {milestones.map((item, index) => (
            <div key={index}>
              <span>{index + 1}</span>
              <div>
                <b>{item.name || item.title}</b>
                <small>
                  {item.status || "Pending"} |{" "}
                  {formatDate(item.completedAt || item.updatedAt)}
                </small>
              </div>
              <em>{item.progress || 0}%</em>
            </div>
          ))}
          {!milestones.length && (
            <Empty text="Construction milestones will be published by the PMC after work starts." />
          )}
        </div>
      </Panel>
    </div>
  );
}

function RegisterPage({ title, rows, type }) {
  const list = Array.isArray(rows) ? rows : [];
  const total = list.reduce(
    (sum, item) =>
      sum + Number(item.amount || item.approvedAmount || item.grossAmount || 0),
    0,
  );
  return (
    <div className="operation-page">
      <div className="operation-hero">
        <small>FINANCIAL TRACKING</small>
        <h2>{title}</h2>
        <p>
          Secure project-wise financial records with verification and approval
          status.
        </p>
      </div>
      <div className="operation-kpis">
        <Info label="Total Records" value={list.length} />
        <Info label="Total Amount" value={`INR ${numberText(total)}`} />
        <Info
          label="Approved"
          value={
            list.filter((item) => /approved|paid/i.test(item.status)).length
          }
        />
        <Info
          label="Pending"
          value={
            list.filter((item) => !/approved|paid/i.test(item.status)).length
          }
        />
      </div>
      <Panel title={title}>
        <div className="register">
          <div className="register-head">
            <span>Reference</span>
            <span>Date</span>
            <span>Amount</span>
            <span>Status</span>
          </div>
          {list.map((item, index) => (
            <div className="register-row" key={item.id || index}>
              <b>
                {item.billNumber ||
                  item.paymentNumber ||
                  item.reference ||
                  `${type.toUpperCase()}-${index + 1}`}
              </b>
              <span>
                {formatDate(item.createdAt || item.date || item.paidAt)}
              </span>
              <strong>
                INR{" "}
                {numberText(
                  item.amount || item.approvedAmount || item.grossAmount,
                )}
              </strong>
              <em>{item.status || "Pending"}</em>
            </div>
          ))}
          {!list.length && (
            <Empty
              text={`No ${type} records are available for this project yet.`}
            />
          )}
        </div>
      </Panel>
    </div>
  );
}

function Milestone({ label, done, date }) {
  return (
    <div className={done ? "milestone done" : "milestone"}>
      <i>{done ? "OK" : "--"}</i>
      <span>
        <b>{label}</b>
        <small>{done ? formatDate(date) : "Pending"}</small>
      </span>
    </div>
  );
}

const CSS = `
.technical-lock{margin:10px 0;background:#fff7e8;border:1px solid #e3b777;border-left:5px solid #d0831b;border-radius:7px;padding:13px;color:#7b430d}.technical-lock b{display:block}.technical-lock p{margin:5px 0 0;font-size:10px;line-height:1.5}.file button:disabled{background:#eef1f4!important;border-color:#aeb8c2!important;color:#77838f!important;cursor:not-allowed!important}
*{box-sizing:border-box}body{margin:0}.portal,.loading{font-family:Inter,"Segoe UI",Arial,sans-serif;color:#0b2144}.portal{min-height:100vh;background:#f3f7fc}.header{height:72px;background:#fff;border-bottom:1px solid #d8e2ee;padding:0 3.5vw;display:flex;align-items:center;justify-content:space-between;position:relative;z-index:5}.brand,.identity,.header-right{display:flex;align-items:center;gap:11px}.logo{width:45px;height:45px;border-radius:50%;background:#168c42;color:#fff;display:grid;place-items:center;font-size:23px;font-weight:900}.brand b{display:block;color:#168c42;font-size:24px}.brand span,.identity span,.live span{display:block;color:#718096;font-size:11px}.live{text-align:right;border-right:1px solid #dbe3ed;padding-right:13px}.live b{display:block;color:#0a5b9e}.avatar{width:39px;height:39px;border-radius:50%;background:#0a5fb6;color:#fff;display:grid;place-items:center;font-weight:900}.identity button{border:1px solid #ccd8e5;background:#fff;color:#12375f;border-radius:7px;padding:9px 12px}.content{max-width:1550px;margin:auto;padding:22px}.home-content{max-width:none;padding:0}.launcher{min-height:calc(100vh - 72px);padding:25px 5vw 34px;background:radial-gradient(circle at 15% 15%,#1fb29075,transparent 28%),radial-gradient(circle at 85% 35%,#3ba8d95c,transparent 30%),linear-gradient(120deg,#062d55ed,#087264e6),url('/assets/nafed/pacs-dashboard-bg.jpg') center/cover fixed;color:#fff;position:relative;overflow:hidden}.launcher:before,.launcher:after{content:"";position:absolute;border:1px solid #ffffff13;border-radius:50%;animation:float 9s ease-in-out infinite}.launcher:before{width:420px;height:420px;left:-180px;top:80px}.launcher:after{width:520px;height:520px;right:-230px;bottom:-170px;animation-delay:-3s}.launcher>*{position:relative;z-index:1}.launcher-heading{text-align:center}.nafed-seal{width:105px;height:68px;border-radius:17px;background:#fff;color:#158342;display:grid;place-items:center;margin:auto;font-size:25px;font-weight:950;box-shadow:0 10px 30px #001f3330}.launcher-heading small{display:block;margin-top:13px;color:#b8f1c7;font-weight:900;letter-spacing:.12em}.launcher-heading h1{font-size:34px;margin:6px 0}.launcher-heading p{margin:0;color:#e2f1f5}.launcher-summary{max-width:1220px;margin:17px auto 14px;display:grid;grid-template-columns:repeat(4,1fr);background:#ffffff17;border:1px solid #ffffff35;border-radius:11px;backdrop-filter:blur(10px)}.launcher-summary span{padding:11px 16px;border-right:1px solid #ffffff2b;font-size:10px;color:#cce4ed}.launcher-summary span:last-child{border:0}.launcher-summary b{display:block;color:#fff;font-size:12px;margin-top:5px}.module-grid{max-width:1380px;margin:auto;display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:17px}.module-grid button{min-height:205px;border:1px solid #d8e8d6;background:linear-gradient(145deg,#fff,#f7fbff);border-radius:20px;padding:22px;color:#153358;cursor:pointer;text-align:left;display:flex;flex-direction:column;align-items:flex-start;position:relative;overflow:hidden;box-shadow:0 14px 34px #001d3136;transition:.25s;animation:rise .45s both;animation-delay:var(--delay)}.module-grid button:before{content:"";position:absolute;width:120px;height:120px;border-radius:50%;right:-54px;top:-54px;background:linear-gradient(135deg,#dff7e7,#e1efff);opacity:.75;transition:.25s}.module-grid button:hover{transform:translateY(-7px);border-color:#63c681;box-shadow:0 22px 50px #001b354d}.module-grid button:hover:before{transform:scale(1.35)}.module-icon{width:58px;height:58px;border-radius:17px;background:linear-gradient(135deg,#e3f7e9,#e3efff);color:#118045;display:grid;place-items:center;margin-bottom:16px;box-shadow:inset 0 0 0 1px #fff}.module-icon svg{width:30px;height:30px}.module-grid b{font-size:18px;color:#174b37;line-height:1.2}.module-grid span{font-size:11px;color:#657389;line-height:1.55;margin-top:8px;min-height:35px}.module-grid footer{width:100%;display:flex;align-items:center;justify-content:space-between;margin-top:auto;padding-top:13px;border-top:1px solid #e7edf3}.module-grid em{font-style:normal;color:#075db7;font-size:10px;font-weight:900}.module-grid strong{background:#edf8ef;color:#188142;border-radius:12px;padding:6px 9px;font-size:8px;text-transform:uppercase}.launcher-note{text-align:center;margin-top:16px;color:#d8edf2;font-size:10px}.module-bar{display:grid;grid-template-columns:auto 1fr auto;align-items:center;gap:18px;margin-bottom:16px}.module-bar>button{border:0;background:#092f62;color:#fff;border-radius:8px;padding:11px 14px;font-weight:850;cursor:pointer}.module-bar small,.welcome small,.intro small,.operation-hero small{color:#168c42;font-weight:900;letter-spacing:.08em}.module-bar h1{margin:4px 0;font-size:27px}.module-bar p{margin:0;color:#68768a;font-size:11px}.module-bar i{border-left:1px solid #abb7c7;margin:0 13px}.module-bar strong{color:#075db7}.allocation-status{background:#fff5e6;color:#9b6204;border:1px solid #efd29d;border-radius:20px;padding:9px 14px;font-size:11px;font-weight:900}.allocation-status.assigned{background:#eaf8ee;color:#0b7e34;border-color:#b9dfc4}.notice{background:#fff8e9;border:1px solid #efd49c;color:#845606;border-radius:8px;padding:11px 14px;margin-bottom:14px}.notice button{float:right;border:0;background:transparent;color:inherit}.welcome,.operation-hero{background:linear-gradient(110deg,#fff,#eff7ff);border:1px solid #d5e0ec;border-radius:11px;padding:20px;margin-bottom:14px}.welcome{display:flex;justify-content:space-between;align-items:center}.welcome h2,.operation-hero h2{margin:5px 0}.welcome p,.operation-hero p{margin:0;color:#6b788c}.welcome>b{background:#eaf3ff;color:#075db7;border-radius:20px;padding:9px 14px;font-size:11px}.kpis,.operation-kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:13px;margin-bottom:14px}.kpi,.operation-kpis .info{background:#fff;border:1px solid #d5dfeb;border-left:4px solid #1768bf;border-radius:9px;padding:16px}.kpi span,.operation-kpis .info span{color:#718096;font-size:11px}.kpi h2{margin:6px 0;font-size:20px}.kpi p{margin:0;color:#7c899a;font-size:10px}.operation-kpis .info b{font-size:14px;margin-top:7px}.pmc-card{background:linear-gradient(110deg,#092f62,#07599d);color:#fff;border-radius:11px;padding:20px;display:grid;grid-template-columns:1.1fr 1.4fr;gap:25px;margin-bottom:14px}.pmc-card small{color:#a8cce9;font-weight:800}.pmc-card h2{margin:5px 0}.pmc-card p{margin:0;color:#d3e5f4;font-size:11px}.pmc-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:8px}.info{min-width:0}.info span,.info b{display:block}.info span{font-size:9px;color:#78869a;margin-bottom:4px}.info b{font-size:11px;overflow-wrap:anywhere}.pmc-grid .info{background:#ffffff13;border:1px solid #ffffff20;border-radius:7px;padding:10px}.pmc-grid .info span{color:#afd0e9}.steps{background:#fff;border:1px solid #d5dfeb;border-radius:10px;padding:18px 24px;display:flex;align-items:center;overflow:auto;margin-bottom:14px}.step{width:105px;flex:0 0 auto;text-align:center}.step>span{width:35px;height:35px;border-radius:50%;margin:auto;display:grid;place-items:center;background:#aab3c0;color:#fff;font-size:11px;font-weight:900}.step.done>span{background:#168c42}.step.current>span{background:#1768bf}.step b,.step small{display:block;font-size:10px;margin-top:6px}.step small{color:#7b889a}.steps>i{height:3px;background:#c7ced8;flex:1;min-width:30px;margin-top:-28px}.steps>i.done{background:#168c42}.dashboard-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px}.panel{background:#fff;border:1px solid #d5dfeb;border-radius:10px;padding:14px;margin-bottom:14px}.panel>header{display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid #e2e8f0;padding-bottom:10px;margin-bottom:10px}.panel h2{font-size:16px;margin:0}.panel header button{border:0;background:#eaf3ff;color:#075db7;border-radius:6px;padding:8px 10px;font-weight:800}.files{display:grid;gap:8px}.file{display:grid;grid-template-columns:42px 1fr auto auto;gap:10px;align-items:center;border:1px solid #dce4ed;border-radius:8px;padding:10px}.file-type{width:38px;height:38px;border-radius:7px;background:#fff0f0;color:#cc2936;display:grid;place-items:center;font-size:9px;font-weight:900}.file b,.file span{display:block}.file b{font-size:11px}.file span{font-size:9px;color:#7b899b;margin-top:4px}.file em{font-style:normal;color:#0b7e34;background:#eaf8ee;border-radius:5px;padding:5px;font-size:9px}.file button{border:1px solid #9bbadd;background:#f4f9ff;color:#075db7;border-radius:6px;padding:7px;font-size:9px;font-weight:800}.meeting-summary{display:grid;grid-template-columns:1fr 1fr;gap:12px}.meeting-photo{height:155px;background:#e9eef5;border-radius:8px;display:grid;place-items:center;color:#718096;font-size:11px;overflow:hidden}.meeting-photo img{width:100%;height:100%;object-fit:cover}.meeting-summary>div:last-child{display:grid;gap:9px}.meeting-summary .info{border-bottom:1px solid #e2e8f0;padding-bottom:7px}.module{margin-top:5px}.intro{background:linear-gradient(110deg,#fff,#f1f8ff);border:1px solid #d5dfeb;border-radius:11px;padding:20px;margin-bottom:14px}.intro h2{margin:5px 0}.intro p{margin:0;color:#6e7b8e}.document-stats{display:grid;grid-template-columns:repeat(3,1fr);gap:11px;margin-bottom:14px}.document-stats .info{background:#fff;border:1px solid #d5dfeb;border-radius:8px;padding:13px}.empty{background:#f5f8fc;border:1px dashed #cbd6e3;border-radius:7px;padding:13px;color:#718096;font-size:11px}.construction-board{display:grid;grid-template-columns:200px 1fr;gap:15px;margin-bottom:14px}.progress-display{background:#fff;border:1px solid #d5dfeb;border-radius:10px;display:grid;place-items:center;padding:20px}.progress-display>div{--progress:0deg;width:105px;height:105px;border-radius:50%;display:grid;place-items:center;background:conic-gradient(#168c42 var(--progress),#dfe8e2 0);position:relative}.progress-display>div:after{content:"";position:absolute;inset:10px;background:#fff;border-radius:50%}.progress-display b{z-index:1;font-size:20px}.progress-display span{font-size:11px;color:#718096}.construction-board .operation-kpis{margin:0}.milestones{display:grid;grid-template-columns:repeat(4,1fr);gap:10px}.milestone{border:1px solid #e1d09d;background:#fff9ed;border-radius:8px;padding:13px;display:flex;gap:9px}.milestone.done{border-color:#afd8ba;background:#edf9f0}.milestone i{font-style:normal;font-size:10px}.milestone span,.milestone b,.milestone small{display:block}.milestone small{color:#718096;margin-top:5px}.milestone-list{display:grid;gap:8px}.milestone-list>div{display:grid;grid-template-columns:35px 1fr auto;gap:11px;align-items:center;border:1px solid #dce4ed;border-radius:8px;padding:11px}.milestone-list>div>span{width:32px;height:32px;border-radius:50%;background:#eaf3ff;color:#075db7;display:grid;place-items:center;font-weight:900}.milestone-list b,.milestone-list small{display:block}.milestone-list small{color:#718096;margin-top:4px}.milestone-list em{font-style:normal;color:#168c42;font-weight:900}.register-head,.register-row{display:grid;grid-template-columns:1.2fr 1fr 1fr .8fr;gap:10px;padding:10px;align-items:center}.register-head{background:#f1f5f9;color:#718096;font-size:10px}.register-row{border:1px solid #e0e7ef;border-top:0;font-size:11px}.register-row strong{color:#075db7}.register-row em{font-style:normal;background:#eef7f0;color:#168c42;border-radius:5px;padding:5px;width:max-content}.loading{min-height:100vh;background:#f3f7fc;display:grid;place-content:center;text-align:center}.loading>span{width:50px;height:50px;border:5px solid #d7e2ee;border-top-color:#168c42;border-radius:50%;animation:spin 1s linear infinite;margin:auto}.loading h2{margin:15px 0 5px}.loading p{color:#718096}@keyframes spin{to{transform:rotate(360deg)}}@keyframes rise{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}@keyframes float{50%{transform:translateY(-18px) scale(1.03)}}@media(max-width:1050px){.module-grid{grid-template-columns:repeat(2,1fr)}.launcher-summary{grid-template-columns:repeat(2,1fr)}.kpis,.operation-kpis{grid-template-columns:repeat(2,1fr)}.pmc-card{grid-template-columns:1fr}.dashboard-grid{grid-template-columns:1fr}.construction-board{grid-template-columns:1fr}.milestones{grid-template-columns:repeat(2,1fr)}}@media(max-width:700px){.header{padding:0 12px}.brand span,.identity>div:nth-child(2),.live{display:none}.content{padding:13px}.home-content{padding:0}.launcher{padding:20px 12px}.launcher-heading h1{font-size:26px}.module-grid{grid-template-columns:1fr}.launcher-summary{grid-template-columns:1fr}.launcher-summary span{border-right:0;border-bottom:1px solid #ffffff2b}.module-bar{grid-template-columns:1fr}.module-bar>button{width:max-content}.allocation-status{width:max-content}.welcome{display:block}.welcome>b{display:inline-block;margin-top:12px}.kpis,.operation-kpis{grid-template-columns:1fr}.pmc-grid,.document-stats{grid-template-columns:1fr}.dashboard-grid{grid-template-columns:1fr}.file{grid-template-columns:38px 1fr}.file em{display:none}.file button{grid-column:2}.meeting-summary{grid-template-columns:1fr}.milestones{grid-template-columns:1fr}.register-head{display:none}.register-row{grid-template-columns:1fr}.module-grid button{grid-template-columns:52px 1fr auto}}
/* Premium NAFED dashboard visual overrides */
.header{height:82px;border-color:#cbd7e3;box-shadow:0 4px 18px #0a294a0d}.official-logo{display:block;width:92px;height:98px;object-fit:contain;mix-blend-mode:multiply}.official-logo.compact{width:55px;height:64px}.nafed-seal{width:112px;height:116px;border-radius:16px;border:1px solid #d8e5dc;padding:7px;background:#fff;box-shadow:0 12px 32px #001f3345}.brand b{font-size:24px}.avatar{background:linear-gradient(135deg,#0a5fb6,#168c42)}
.launcher{min-height:calc(100vh - 82px);padding-top:20px;background:radial-gradient(circle at 12% 10%,#1eaa8060,transparent 26%),radial-gradient(circle at 86% 30%,#49a9c84d,transparent 30%),linear-gradient(122deg,#062b50f5 0%,#075c62ef 56%,#277951ed 100%),url('/assets/nafed/pacs-dashboard-bg.jpg') center/cover fixed}.launcher-heading small{color:#d7f2d5}.launcher-heading h1{font-size:35px;text-shadow:0 2px 15px #001b2d45}.launcher-summary{border-color:#ffffff54;backdrop-filter:blur(12px);box-shadow:0 12px 28px #001e3024}.launcher-summary b{overflow-wrap:anywhere}
.module-grid button{--accent:#168c42;min-height:208px;border:1px solid #cad8e5;border-top:4px solid var(--accent);border-radius:16px;padding:20px}.module-grid button:nth-child(4n+2){--accent:#1768bf}.module-grid button:nth-child(4n+3){--accent:#7b4bc3}.module-grid button:nth-child(4n){--accent:#d0831b}.module-grid button:before{background:linear-gradient(135deg,#edf8f0,#e8f1fb)}.module-grid button:hover{border-color:var(--accent)}.module-icon{width:56px;height:56px;border-radius:14px;color:var(--accent);border:1px solid #d6e4df}.module-grid strong{border:1px solid #c9e8d2}.identity button{font-weight:750;cursor:pointer}
/* State Branch dashboard style shell */
.portal-home{min-height:100vh;background:radial-gradient(circle at 8% 10%,rgba(34,197,94,.22),transparent 26%),radial-gradient(circle at 92% 20%,rgba(14,165,233,.18),transparent 26%),linear-gradient(135deg,#041329 0%,#062b55 48%,#064e3b 100%);padding:22px 0 34px;position:relative;overflow-x:hidden}.portal-home:before{content:"";position:fixed;inset:0;pointer-events:none;background-image:linear-gradient(rgba(255,255,255,.045) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.045) 1px,transparent 1px);background-size:48px 48px;mask-image:linear-gradient(to bottom,rgba(0,0,0,.9),rgba(0,0,0,.18))}.portal-home .header{width:min(1380px,calc(100% - 44px));height:auto;min-height:88px;margin:0 auto;border-radius:22px;background:rgba(255,255,255,.12);color:#fff;border:1px solid rgba(255,255,255,.18);box-shadow:0 22px 60px rgba(2,6,23,.34);backdrop-filter:blur(18px);padding:12px 20px}.portal-home .brand{gap:16px}.portal-home .brand>.official-logo{width:112px;height:72px;background:#fff;border-radius:16px;padding:5px;mix-blend-mode:normal;box-shadow:0 10px 25px rgba(0,0,0,.18)}.portal-home .brand b{color:#fff;font-size:23px;font-weight:950}.portal-home .brand span{color:#d1fae5;font-weight:750;margin-top:4px}.portal-home .live{border-color:#ffffff38}.portal-home .live b,.portal-home .live span,.portal-home .identity b,.portal-home .identity span{color:#fff}.portal-home .avatar{background:#fff;color:#064e3b}.portal-home .identity button{background:rgba(255,255,255,.16);border:1px solid rgba(255,255,255,.22);color:#fff;border-radius:11px;font-weight:900}.portal-home .home-content{position:relative;z-index:1}.portal-home .launcher{min-height:auto;background:transparent;padding:24px max(22px,calc((100vw - 1380px)/2)) 0;overflow:visible}.portal-home .launcher:before,.portal-home .launcher:after{display:none}.portal-home .launcher-heading{display:grid;grid-template-columns:auto 1fr;grid-template-rows:auto auto auto;column-gap:18px;text-align:left;align-items:center;margin-bottom:18px}.portal-home .nafed-seal{grid-row:1/4;width:112px;height:116px;margin:0;border-radius:18px}.portal-home .launcher-heading small{margin:0;color:#bbf7d0}.portal-home .launcher-heading h1{font-size:31px;margin:4px 0;color:#fff}.portal-home .launcher-heading p{color:#ecfdf5;font-weight:800}.portal-home .launcher-summary{max-width:none;margin:0 0 20px;background:rgba(255,255,255,.96);border:1px solid rgba(255,255,255,.6);border-radius:18px;box-shadow:0 22px 60px rgba(2,6,23,.22);overflow:hidden;backdrop-filter:blur(16px)}.portal-home .launcher-summary span{color:#64748b;padding:15px 18px;border-right:1px solid #dbe4ef;font-weight:800}.portal-home .launcher-summary b{color:#071f45;font-size:13px}.portal-home .module-grid{max-width:none;background:rgba(255,255,255,.96);border:1px solid rgba(255,255,255,.6);border-radius:24px;padding:22px;box-shadow:0 28px 78px rgba(2,6,23,.26);grid-template-columns:repeat(3,minmax(260px,1fr));gap:14px}.portal-home .module-grid button{min-height:118px;border:1px solid #dbe4ef;border-top:1px solid #dbe4ef;border-radius:18px;padding:15px;display:grid;grid-template-columns:56px 1fr;grid-template-rows:auto auto 1fr;column-gap:13px;box-shadow:0 10px 24px rgba(15,23,42,.07)}.portal-home .module-grid button:hover{transform:translateY(-4px);box-shadow:0 22px 46px rgba(15,23,42,.18);border-color:#86efac}.portal-home .module-grid button:before{display:none}.portal-home .module-icon{grid-row:1/4;margin:0;width:54px;height:54px}.portal-home .module-grid b{font-size:15px;font-weight:950;color:#166534}.portal-home .module-grid span{min-height:0;margin:5px 0 0;color:#334155;font-size:11px;font-weight:700}.portal-home .module-grid footer{grid-column:2;margin-top:9px;padding-top:8px}.portal-home .launcher-note{color:#d1fae5;font-weight:700;margin:16px 0 0}
@media(max-width:1050px){.portal-home .header{flex-direction:column;align-items:flex-start;gap:12px}.portal-home .header-right{width:100%;flex-wrap:wrap}.portal-home .module-grid{grid-template-columns:repeat(2,minmax(240px,1fr))}}@media(max-width:700px){.portal-home{padding-top:12px}.portal-home .header{width:calc(100% - 24px);padding:12px}.portal-home .launcher{padding:18px 12px 0}.portal-home .launcher-heading{grid-template-columns:80px 1fr;column-gap:12px}.portal-home .nafed-seal{width:80px;height:86px}.portal-home .launcher-heading h1{font-size:22px}.portal-home .module-grid{grid-template-columns:1fr;padding:13px}.portal-home .module-grid button{grid-template-columns:54px 1fr}.portal-home .launcher-summary{grid-template-columns:1fr 1fr}.portal-home .launcher-summary span{border-bottom:1px solid #dbe4ef}.portal-home .brand>.official-logo{width:88px;height:62px}}
/* Premium inner module workspace */
.portal:not(.portal-home){min-height:100vh;background:radial-gradient(circle at 7% 8%,rgba(34,197,94,.17),transparent 24%),radial-gradient(circle at 93% 18%,rgba(14,165,233,.14),transparent 25%),linear-gradient(135deg,#041329 0%,#062b55 49%,#064e3b 100%);padding:20px 0 34px;position:relative}.portal:not(.portal-home):before{content:"";position:fixed;inset:0;pointer-events:none;background-image:linear-gradient(rgba(255,255,255,.035) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.035) 1px,transparent 1px);background-size:48px 48px}.portal:not(.portal-home) .header{width:min(1380px,calc(100% - 44px));height:auto;min-height:84px;margin:0 auto;border-radius:21px;background:rgba(255,255,255,.12);border:1px solid rgba(255,255,255,.18);box-shadow:0 22px 60px rgba(2,6,23,.32);backdrop-filter:blur(18px);padding:11px 19px;color:#fff}.portal:not(.portal-home) .brand>.official-logo{width:105px;height:68px;border-radius:15px;background:#fff;padding:5px;mix-blend-mode:normal}.portal:not(.portal-home) .brand b,.portal:not(.portal-home) .live b,.portal:not(.portal-home) .identity b{color:#fff}.portal:not(.portal-home) .brand span,.portal:not(.portal-home) .live span,.portal:not(.portal-home) .identity span{color:#d1fae5}.portal:not(.portal-home) .live{border-color:#ffffff38}.portal:not(.portal-home) .avatar{background:#fff;color:#064e3b}.portal:not(.portal-home) .identity button{background:rgba(255,255,255,.14);border-color:rgba(255,255,255,.24);color:#fff;font-weight:900}.portal:not(.portal-home) .content{width:min(1380px,calc(100% - 44px));max-width:none;padding:22px 0 0;position:relative;z-index:2}.portal:not(.portal-home) .module-bar{background:rgba(255,255,255,.96);border:1px solid rgba(255,255,255,.6);border-radius:20px;padding:15px 18px;box-shadow:0 22px 60px rgba(2,6,23,.24);margin-bottom:16px}.portal:not(.portal-home) .module-bar>button{background:linear-gradient(135deg,#062b55,#064e3b);border-radius:11px;box-shadow:0 9px 20px rgba(6,43,85,.18)}.portal:not(.portal-home) .module-bar h1{font-weight:950;color:#071f45}.portal:not(.portal-home) .module-bar small{color:#168c42}.portal:not(.portal-home) .allocation-status{box-shadow:inset 0 0 0 1px #fff}.portal:not(.portal-home) .notice{border-radius:12px;box-shadow:0 10px 24px rgba(2,6,23,.16)}
.portal:not(.portal-home) .welcome{background:linear-gradient(115deg,#062b55,#0a5f63 68%,#168c42);border:1px solid #ffffff35;border-radius:18px;color:#fff;padding:22px;box-shadow:0 20px 48px rgba(2,6,23,.24)}.portal:not(.portal-home) .welcome small{color:#bbf7d0}.portal:not(.portal-home) .welcome h2{font-size:24px;font-weight:950}.portal:not(.portal-home) .welcome p{color:#e6f7ef}.portal:not(.portal-home) .welcome>b{background:#fff;color:#075f39;border:1px solid #d1fae5;padding:10px 14px}.portal:not(.portal-home) .kpis{gap:14px}.portal:not(.portal-home) .kpi{position:relative;overflow:hidden;border:1px solid #d6e0eb;border-left:0;border-top:4px solid #168c42;border-radius:15px;padding:17px;background:#fff;box-shadow:0 13px 30px rgba(2,6,23,.16)}.portal:not(.portal-home) .kpi:nth-child(2){border-top-color:#1768bf}.portal:not(.portal-home) .kpi:nth-child(3){border-top-color:#7c3fc5}.portal:not(.portal-home) .kpi:nth-child(4){border-top-color:#d17b16}.portal:not(.portal-home) .kpi:after{content:"";position:absolute;width:80px;height:80px;border-radius:50%;right:-34px;top:-38px;background:#eef7f0}.portal:not(.portal-home) .kpi h2{font-size:20px;color:#071f45;font-weight:950;overflow-wrap:anywhere}.portal:not(.portal-home) .pmc-card{background:linear-gradient(112deg,#062b55 0%,#0a4d73 55%,#08744f 100%);border:1px solid #ffffff30;border-radius:18px;box-shadow:0 22px 56px rgba(2,6,23,.28);padding:22px}.portal:not(.portal-home) .pmc-card small{color:#bbf7d0;letter-spacing:.08em}.portal:not(.portal-home) .pmc-card h2{font-size:22px;font-weight:950}.portal:not(.portal-home) .pmc-grid .info{background:rgba(255,255,255,.12);border:1px solid rgba(255,255,255,.18);border-radius:11px;padding:12px}.portal:not(.portal-home) .steps{border:1px solid #cbd8e5;border-radius:17px;padding:21px 25px;box-shadow:0 16px 38px rgba(2,6,23,.18)}.portal:not(.portal-home) .step>span{width:39px;height:39px;box-shadow:0 0 0 5px #eef2f7}.portal:not(.portal-home) .step.done>span{background:#168c42;box-shadow:0 0 0 5px #dcfce7}.portal:not(.portal-home) .step.current>span{background:#1768bf;box-shadow:0 0 0 5px #dbeafe}.portal:not(.portal-home) .step b{font-weight:950;color:#071f45}.portal:not(.portal-home) .dashboard-grid{gap:16px}.portal:not(.portal-home) .panel{border:1px solid #cbd8e5;border-radius:17px;padding:16px;box-shadow:0 16px 40px rgba(2,6,23,.17)}.portal:not(.portal-home) .panel>header{padding-bottom:12px;border-bottom:1.5px solid #ccd6e2}.portal:not(.portal-home) .panel h2{font-weight:950;color:#071f45}.portal:not(.portal-home) .panel header button{border:1px solid #9bbadd;border-radius:9px;font-weight:900;cursor:pointer}.portal:not(.portal-home) .file{border:1px solid #bdcbd9;border-radius:11px;background:#fbfdff}.portal:not(.portal-home) .file button{border:1px solid #5f87b5;border-radius:8px;cursor:pointer}.portal:not(.portal-home) .meeting-photo{border:1px dashed #93a4b8;border-radius:11px;background:linear-gradient(135deg,#eef3f8,#e2e9f1)}
.portal:not(.portal-home) .intro,.portal:not(.portal-home) .operation-hero{background:linear-gradient(115deg,#062b55,#0a5f63 68%,#168c42);border:1px solid #ffffff35;border-radius:18px;color:#fff;box-shadow:0 20px 48px rgba(2,6,23,.24)}.portal:not(.portal-home) .intro small,.portal:not(.portal-home) .operation-hero small{color:#bbf7d0}.portal:not(.portal-home) .intro p,.portal:not(.portal-home) .operation-hero p{color:#e5f6ee}.portal:not(.portal-home) .document-stats .info,.portal:not(.portal-home) .operation-kpis .info{border:1px solid #cbd8e5;border-left:4px solid #168c42;border-radius:13px;box-shadow:0 12px 28px rgba(2,6,23,.14)}.portal:not(.portal-home) .operation-kpis .info:nth-child(2){border-left-color:#1768bf}.portal:not(.portal-home) .operation-kpis .info:nth-child(3){border-left-color:#7c3fc5}.portal:not(.portal-home) .operation-kpis .info:nth-child(4){border-left-color:#d17b16}.portal:not(.portal-home) .progress-display{border-radius:17px;box-shadow:0 16px 38px rgba(2,6,23,.16)}.portal:not(.portal-home) .register-head{background:#dfe8f1;color:#071f45;font-weight:900;border:1.5px solid #111827}.portal:not(.portal-home) .register-row{border:1.5px solid #111827;background:#fff}.portal:not(.portal-home) .milestone,.portal:not(.portal-home) .milestone-list>div{border-width:1.5px;border-radius:11px}
@media(max-width:1050px){.portal:not(.portal-home) .header{flex-direction:column;align-items:flex-start;gap:10px}.portal:not(.portal-home) .header-right{width:100%;flex-wrap:wrap}}@media(max-width:700px){.portal:not(.portal-home){padding-top:12px}.portal:not(.portal-home) .header,.portal:not(.portal-home) .content{width:calc(100% - 24px)}.portal:not(.portal-home) .module-bar{grid-template-columns:1fr;padding:13px}.portal:not(.portal-home) .brand>.official-logo{width:86px;height:60px}.portal:not(.portal-home) .pmc-card{padding:16px}}
/* Final Government MIS design system */
.portal,.portal-home,.portal:not(.portal-home){background:#eef2f5!important;color:#1f2937;padding:0 0 32px;min-height:100vh}.portal:before,.portal-home:before,.portal:not(.portal-home):before{display:none!important}.portal .header,.portal-home .header,.portal:not(.portal-home) .header{width:100%;max-width:none;margin:0;height:84px;min-height:84px;border:0;border-radius:0;background:#fff;color:#1f2937;padding:8px max(28px,calc((100vw - 1420px)/2));box-shadow:0 1px 0 #b8c4d0;backdrop-filter:none;position:relative}.portal .header:before{content:"";position:absolute;left:0;right:0;top:0;height:4px;background:linear-gradient(90deg,#e68a21 0 33.33%,#f8fafc 33.33% 66.66%,#168447 66.66%)}.portal .brand>.official-logo,.portal-home .brand>.official-logo,.portal:not(.portal-home) .brand>.official-logo{width:70px;height:68px;border:0;border-radius:0;background:#fff;padding:2px;box-shadow:none;mix-blend-mode:multiply}.portal .brand b,.portal-home .brand b,.portal:not(.portal-home) .brand b{color:#12345b;font-size:21px;font-weight:800}.portal .brand span,.portal-home .brand span,.portal:not(.portal-home) .brand span{color:#526273;font-size:11px;font-weight:600}.portal .live,.portal-home .live,.portal:not(.portal-home) .live{border-color:#cdd6df}.portal .live b,.portal .identity b,.portal-home .live b,.portal-home .identity b,.portal:not(.portal-home) .live b,.portal:not(.portal-home) .identity b{color:#12345b}.portal .live span,.portal .identity span,.portal-home .live span,.portal-home .identity span,.portal:not(.portal-home) .live span,.portal:not(.portal-home) .identity span{color:#607083}.portal .avatar,.portal-home .avatar,.portal:not(.portal-home) .avatar{background:#e7f2eb;color:#166534;border:1px solid #9fc7ad;box-shadow:none}.portal .identity button,.portal-home .identity button,.portal:not(.portal-home) .identity button{background:#fff;color:#12345b;border:1px solid #8798aa;border-radius:5px;padding:9px 13px;box-shadow:none}
.portal-home .home-content{padding:0;background:#eef2f5}.portal-home .launcher{background:#eef2f5!important;padding:22px max(28px,calc((100vw - 1420px)/2)) 0;min-height:auto;color:#1f2937}.portal-home .launcher-heading{display:grid;grid-template-columns:82px 1fr;grid-template-rows:auto auto auto;column-gap:16px;text-align:left;align-items:center;background:#fff;border:1px solid #c6d0da;border-top:4px solid #168447;border-radius:7px;padding:17px 20px;margin:0 0 14px;box-shadow:none}.portal-home .nafed-seal{grid-row:1/4;width:80px;height:85px;margin:0;border:1px solid #cbd5df;border-radius:5px;padding:4px;box-shadow:none}.portal-home .launcher-heading small{margin:0;color:#168447;font-size:10px}.portal-home .launcher-heading h1{font-size:26px;color:#12345b;text-shadow:none;margin:3px 0;font-weight:800}.portal-home .launcher-heading p{color:#4d5d6c;font-weight:600}.portal-home .launcher-summary{margin:0 0 14px;background:#fff;border:1px solid #b9c5d0;border-radius:6px;box-shadow:none;backdrop-filter:none}.portal-home .launcher-summary span{color:#5f6d7a;padding:13px 16px;border-right:1px solid #cbd4dd;font-weight:600}.portal-home .launcher-summary b{color:#172f4f;font-size:12px}.portal-home .module-grid{background:#fff;border:1px solid #b9c5d0;border-radius:7px;padding:15px;box-shadow:none;grid-template-columns:repeat(3,minmax(260px,1fr));gap:11px}.portal-home .module-grid button{min-height:112px;border:1px solid #c5d0da!important;border-radius:6px;padding:14px;background:#fff;box-shadow:none;display:grid;grid-template-columns:50px 1fr;grid-template-rows:auto auto 1fr;column-gap:12px}.portal-home .module-grid button:hover{transform:none;border-color:#168447!important;background:#f5faf7;box-shadow:0 0 0 1px #168447}.portal-home .module-icon{grid-row:1/4;width:48px;height:48px;border-radius:5px;margin:0;background:#edf4ef;color:#166534;border:1px solid #b9d1c1}.portal-home .module-grid b{font-size:14px;color:#12345b}.portal-home .module-grid span{font-size:10px;color:#536273}.portal-home .module-grid footer{margin-top:7px;padding-top:7px;border-color:#d5dde5}.portal-home .module-grid em{color:#174f87}.portal-home .module-grid strong{border-radius:4px;padding:4px 7px}.portal-home .launcher-note{color:#607083;margin:13px 0 0}
.portal:not(.portal-home) .content{width:min(1420px,calc(100% - 56px));padding:20px 0 0}.portal:not(.portal-home) .module-bar{background:#fff;border:1px solid #b9c5d0;border-top:4px solid #168447;border-radius:7px;padding:14px 17px;box-shadow:none;margin-bottom:13px}.portal:not(.portal-home) .module-bar>button{background:#12345b;border:1px solid #0c2745;border-radius:5px;box-shadow:none;padding:10px 13px}.portal:not(.portal-home) .module-bar h1{font-size:24px;font-weight:800}.portal:not(.portal-home) .module-bar small{color:#168447}.portal:not(.portal-home) .allocation-status{border-radius:4px;box-shadow:none}.portal:not(.portal-home) .welcome{background:#fff;border:1px solid #b9c5d0;border-left:5px solid #168447;border-radius:6px;color:#1f2937;padding:18px 20px;box-shadow:none}.portal:not(.portal-home) .welcome small{color:#168447}.portal:not(.portal-home) .welcome h2{font-size:21px;color:#12345b;font-weight:800}.portal:not(.portal-home) .welcome p{color:#526273}.portal:not(.portal-home) .welcome>b{background:#edf6f0;color:#166534;border:1px solid #a9cdb5;border-radius:4px;padding:8px 11px}.portal:not(.portal-home) .kpis{gap:10px}.portal:not(.portal-home) .kpi{border:1px solid #b9c5d0;border-top:3px solid #168447;border-radius:6px;padding:15px;background:#fff;box-shadow:none;min-height:112px}.portal:not(.portal-home) .kpi:nth-child(2),.portal:not(.portal-home) .kpi:nth-child(3),.portal:not(.portal-home) .kpi:nth-child(4){border-top-color:#12345b}.portal:not(.portal-home) .kpi:after{display:none}.portal:not(.portal-home) .kpi h2{font-size:17px;font-weight:800;line-height:1.3}.portal:not(.portal-home) .pmc-card{background:#fff;border:1px solid #aebbc8;border-radius:6px;box-shadow:none;padding:0;display:grid;grid-template-columns:.85fr 1.15fr;gap:0;overflow:hidden}.portal:not(.portal-home) .pmc-card>div:first-child{background:#12345b;color:#fff;padding:18px}.portal:not(.portal-home) .pmc-card small{color:#bfe3c9}.portal:not(.portal-home) .pmc-card h2{font-size:19px;font-weight:800}.portal:not(.portal-home) .pmc-card p{color:#dbe7f1}.portal:not(.portal-home) .pmc-grid{padding:14px;background:#f8fafc}.portal:not(.portal-home) .pmc-grid .info{background:#fff;border:1px solid #b9c5d0;border-radius:4px;padding:10px}.portal:not(.portal-home) .pmc-grid .info span{color:#607083}.portal:not(.portal-home) .pmc-grid .info b{color:#1f2937}.portal:not(.portal-home) .steps{border:1px solid #b9c5d0;border-radius:6px;padding:19px 22px;box-shadow:none}.portal:not(.portal-home) .step>span{width:34px;height:34px;box-shadow:none}.portal:not(.portal-home) .step.done>span{box-shadow:none}.portal:not(.portal-home) .step.current>span{box-shadow:none;background:#174f87}.portal:not(.portal-home) .step b{font-weight:700}.portal:not(.portal-home) .dashboard-grid{gap:12px}.portal:not(.portal-home) .panel{border:1px solid #b9c5d0;border-radius:6px;padding:14px;box-shadow:none}.portal:not(.portal-home) .panel>header{border-bottom:2px solid #d1d9e1;padding-bottom:10px}.portal:not(.portal-home) .panel h2{font-size:15px;font-weight:800;color:#12345b}.portal:not(.portal-home) .panel header button{background:#f2f6fa;border:1px solid #879aad;border-radius:4px;color:#174f87}.portal:not(.portal-home) .file{border:1px solid #c1ccd6;border-radius:4px;background:#fff}.portal:not(.portal-home) .file-type{border-radius:3px}.portal:not(.portal-home) .file button{border-radius:4px}.portal:not(.portal-home) .meeting-photo{border:1px dashed #9eacba;border-radius:4px;background:#eef2f6}
.portal:not(.portal-home) .intro,.portal:not(.portal-home) .operation-hero{background:#fff;border:1px solid #b9c5d0;border-left:5px solid #168447;border-radius:6px;color:#1f2937;box-shadow:none}.portal:not(.portal-home) .intro small,.portal:not(.portal-home) .operation-hero small{color:#168447}.portal:not(.portal-home) .intro h2,.portal:not(.portal-home) .operation-hero h2{color:#12345b}.portal:not(.portal-home) .intro p,.portal:not(.portal-home) .operation-hero p{color:#526273}.portal:not(.portal-home) .document-stats .info,.portal:not(.portal-home) .operation-kpis .info{border:1px solid #b9c5d0;border-left:3px solid #168447;border-radius:5px;box-shadow:none}.portal:not(.portal-home) .progress-display{border:1px solid #b9c5d0;border-radius:6px;box-shadow:none}.portal:not(.portal-home) .milestone,.portal:not(.portal-home) .milestone-list>div{border-width:1px;border-radius:5px}.portal:not(.portal-home) .register-head,.portal:not(.portal-home) .register-row{border:1px solid #5c6670}.portal:not(.portal-home) .register-head{background:#dfe6ed}.portal:not(.portal-home) .empty{border-radius:4px}
@media(max-width:1050px){.portal-home .module-grid{grid-template-columns:repeat(2,1fr)}.portal:not(.portal-home) .pmc-card{grid-template-columns:1fr}}@media(max-width:700px){.portal .header,.portal-home .header,.portal:not(.portal-home) .header{padding:8px 12px;height:auto}.portal-home .launcher{padding:14px 12px 0}.portal-home .launcher-heading{grid-template-columns:64px 1fr;padding:13px}.portal-home .nafed-seal{width:62px;height:66px}.portal-home .module-grid{grid-template-columns:1fr;padding:10px}.portal:not(.portal-home) .content{width:calc(100% - 24px)}.portal:not(.portal-home) .kpis{grid-template-columns:1fr}.portal:not(.portal-home) .module-bar{padding:12px}}
.sync-state{display:flex;align-items:center;gap:6px;color:#166534;font-size:10px;font-weight:800;padding:7px 9px;border:1px solid #b9d8c3;background:#eff9f2;border-radius:5px}.sync-state i{width:8px;height:8px;border-radius:50%;background:#16a34a;box-shadow:0 0 0 4px #dcfce7;animation:syncPulse 1.8s ease-in-out infinite}.sync-state span{display:block;color:#607083;font-size:8px;font-weight:600;margin-left:3px}.help-preview{display:grid;gap:8px}.help-preview strong{color:#12345b}.help-preview p{margin:0;color:#607083;font-size:11px;line-height:1.5}.help-preview a{width:max-content;text-decoration:none;background:#166534;color:#fff;border-radius:5px;padding:8px 11px;font-size:11px;font-weight:800}.help-center{background:#fff;border:1px solid #b9c5d0;border-top:4px solid #168447;border-radius:7px;padding:20px}.help-title{display:grid;grid-template-columns:auto 1fr auto;align-items:center;gap:15px;border-bottom:1px solid #d5dde5;padding-bottom:17px}.help-title h2{margin:4px 0;color:#12345b}.help-title p{margin:0;color:#607083}.help-title>small,.help-title small{color:#168447;font-weight:900}.help-title>b{background:#edf8f0;color:#166534;border:1px solid #b9d8c3;border-radius:4px;padding:8px 10px;font-size:10px}.help-shield{width:58px;height:58px;border-radius:50%;display:grid;place-items:center;background:#12345b;color:#fff;border:5px solid #e3efe7;font-weight:900}.help-contact-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-top:17px}.help-contact-grid>div{border:1px solid #c4ced8;border-radius:6px;padding:16px;display:flex;flex-direction:column;gap:8px;min-height:150px}.help-contact-grid span{color:#607083;font-size:10px}.help-contact-grid strong{color:#12345b;font-size:15px;overflow-wrap:anywhere}.help-contact-grid small{color:#607083;line-height:1.5}.help-contact-grid a{margin-top:auto;width:max-content;text-decoration:none;color:#fff;border-radius:5px;padding:9px 12px;font-size:11px;font-weight:800}.call-action{background:#168447}.mail-action{background:#174f87}.privacy-note{margin-top:14px;background:#f1f5f9;border-left:4px solid #12345b;padding:11px;color:#526273;font-size:10px}.locked-help{text-align:center;padding:45px max(20px,15%)}.locked-help .help-shield{margin:0 auto 15px}.locked-help h2{color:#12345b}.locked-help p{color:#607083;line-height:1.7}.locked-help>small{color:#168447;font-weight:900}.portal-home .sync-state{background:#eff9f2}.portal:not(.portal-home) .sync-state{background:rgba(255,255,255,.12);border-color:rgba(255,255,255,.25);color:#d1fae5}.portal:not(.portal-home) .sync-state span{color:#dbeafe}@keyframes syncPulse{50%{opacity:.45;transform:scale(.8)}}@media(max-width:800px){.sync-state span{display:none}.help-title{grid-template-columns:auto 1fr}.help-title>b{grid-column:1/-1;width:max-content}.help-contact-grid{grid-template-columns:1fr}}
/* PACS master signatures and seal */
.signature-workspace{background:#fff;border:1px solid #b9c5d0;border-top:4px solid #168447;border-radius:7px;padding:18px;color:#1f2937}.signature-hero{display:flex;align-items:center;justify-content:space-between;gap:20px;padding:18px;background:linear-gradient(110deg,#f8fbff,#edf8f1);border:1px solid #c4d1dc;border-radius:7px}.signature-hero small{color:#168447;font-size:9px;font-weight:900;letter-spacing:.08em}.signature-hero h2{margin:5px 0;color:#12345b;font-size:23px}.signature-hero p{max-width:760px;margin:0;color:#526273;font-size:11px;line-height:1.6}.signature-score{min-width:135px;padding:13px;border:1px solid #dfb55e;border-radius:6px;background:#fff8e8;text-align:center}.signature-score.ready{border-color:#82bd96;background:#edf8f1}.signature-score b,.signature-score span{display:block}.signature-score b{color:#12345b;font-size:23px}.signature-score span{margin-top:4px;color:#607083;font-size:9px;font-weight:800}.signature-message{margin-top:12px;padding:11px 13px;border-radius:6px;font-size:10px;font-weight:800}.signature-message.success{border:1px solid #9fcbb0;background:#edf8f1;color:#166534}.signature-message.error{border:1px solid #e2a0a0;background:#fff1f1;color:#9f2323}.signatory-name-panel{display:grid;grid-template-columns:1fr 1fr auto;gap:11px;align-items:end;margin-top:13px;padding:13px;border:1px solid #c8d2dc;border-radius:6px;background:#f8fafc}.signatory-name-panel label>span{display:block;margin-bottom:6px;color:#42566b;font-size:9px;font-weight:900}.signatory-name-panel input{width:100%;height:42px;padding:0 11px;border:1px solid #aebdcb;border-radius:5px;background:#fff;color:#173451}.signatory-name-panel button{height:42px;padding:0 15px;border:1px solid #0d5b37;border-radius:5px;background:#168447;color:#fff;font-size:10px;font-weight:900;cursor:pointer}.signatory-name-panel button:disabled{cursor:not-allowed;opacity:.55}.signature-upload-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:13px;margin-top:13px}.signature-upload-card{overflow:hidden;border:1px solid #aebdcb;border-radius:7px;background:#fff}.signature-upload-card>header{display:grid;grid-template-columns:34px 1fr auto;gap:9px;align-items:center;padding:12px;border-bottom:1px solid #d4dde5;background:#f4f7fa}.signature-upload-card>header>span{width:31px;height:31px;display:grid;place-items:center;border-radius:50%;background:#12345b;color:#fff;font-size:9px;font-weight:900}.signature-upload-card h3{margin:0;color:#12345b;font-size:12px}.signature-upload-card header p{margin:3px 0 0;color:#607083;font-size:9px}.signature-upload-card header>b{padding:5px 7px;border:1px solid #dfb55e;border-radius:4px;background:#fff8e8;color:#8a5b05;font-size:7px}.signature-upload-card header>b.uploaded{border-color:#8dc5a1;background:#edf8f1;color:#166534}.signature-preview{height:180px;display:grid;place-items:center;padding:15px;background:linear-gradient(135deg,#f7fafc,#edf2f7)}.signature-preview img{display:block;width:100%;height:100%;object-fit:contain}.seal-card .signature-preview img{max-width:165px;border-radius:50%}.signature-preview>div{text-align:center;color:#718096}.signature-preview strong{display:grid;place-items:center;width:72px;height:72px;margin:0 auto 8px;border:2px dashed #9aabba;border-radius:50%;color:#526273}.signature-preview span{font-size:8px}.signature-owner{padding:10px 12px;border-top:1px solid #d7dfe7}.signature-owner span,.signature-owner b{display:block}.signature-owner span{color:#718096;font-size:8px}.signature-owner b{margin-top:4px;color:#173451;font-size:10px}.signature-upload-button{display:block;margin:0 12px 12px;padding:11px;border:1px solid #174f87;border-radius:5px;background:#174f87;color:#fff;text-align:center;font-size:9px;font-weight:900;cursor:pointer}.signature-upload-button input{display:none}.signature-guidance{margin-top:13px;padding:11px 13px;border-left:4px solid #174f87;background:#eef4fa;color:#526273;font-size:9px;line-height:1.55}@media(max-width:980px){.signature-upload-grid{grid-template-columns:1fr}.signatory-name-panel{grid-template-columns:1fr 1fr}.signatory-name-panel button{grid-column:1/-1}}@media(max-width:650px){.signature-workspace{padding:11px}.signature-hero{align-items:flex-start;flex-direction:column}.signature-score{width:100%}.signatory-name-panel{grid-template-columns:1fr}.signatory-name-panel button{grid-column:auto}}
/* Final submitted PACS application */
.application-workspace{display:grid;gap:13px;color:#1f2937}.application-hero{display:flex;align-items:center;justify-content:space-between;gap:22px;padding:20px;border:1px solid #aebbc8;border-top:4px solid #168447;border-radius:7px;background:linear-gradient(110deg,#fff,#f0f8f3)}.application-hero small{color:#168447;font-size:9px;font-weight:900;letter-spacing:.09em}.application-hero h2{margin:5px 0;color:#12345b;font-size:24px}.application-hero p{max-width:820px;margin:0;color:#526273;font-size:11px;line-height:1.65}.application-hero-actions{display:flex;flex-direction:column;align-items:stretch;gap:8px;min-width:160px}.submitted-badge,.editing-badge{display:block;padding:8px 10px;border-radius:4px;text-align:center;font-size:9px;font-weight:900}.submitted-badge{border:1px solid #8fc3a1;background:#edf8f1;color:#166534}.editing-badge{border:1px solid #e1bd77;background:#fff8e8;color:#8a5b05}.application-edit-btn,.application-save-btn,.application-cancel-btn,.application-empty button{border-radius:5px;padding:11px 14px;font-size:10px;font-weight:900;cursor:pointer}.application-edit-btn,.application-save-btn,.application-empty button{border:1px solid #0d5b37;background:#168447;color:#fff}.application-edit-btn:hover,.application-save-btn:hover,.application-empty button:hover{background:#0f713a}.application-message{display:flex;justify-content:space-between;align-items:center;gap:15px;padding:11px 13px;border-radius:5px;font-size:10px;font-weight:800}.application-message.success{border:1px solid #9fcbb0;background:#edf8f1;color:#166534}.application-message.error{border:1px solid #e2a0a0;background:#fff1f1;color:#9f2323}.application-message.info{border:1px solid #9bbadd;background:#eef5fc;color:#174f87}.application-message button{border:0;background:transparent;color:inherit;font-size:9px;font-weight:900;cursor:pointer}.application-summary{display:grid;grid-template-columns:repeat(6,minmax(0,1fr));border:1px solid #b9c5d0;border-radius:6px;background:#fff;overflow:hidden}.application-summary .info{min-height:78px;padding:13px;border-right:1px solid #d5dde5}.application-summary .info:last-child{border-right:0}.application-summary .info span{color:#607083;font-size:9px}.application-summary .info b{margin-top:7px;color:#12345b;font-size:11px}.application-edit-notice{display:flex;gap:13px;align-items:center;padding:12px 14px;border:1px solid #dfbd79;border-left:4px solid #d0831b;border-radius:5px;background:#fff9ea;color:#74450c}.application-edit-notice b{font-size:10px;white-space:nowrap}.application-edit-notice span{font-size:9px;line-height:1.55}.application-form{display:grid;grid-template-columns:1fr 1fr;gap:13px}.application-section{overflow:hidden;border:1px solid #b9c5d0;border-radius:7px;background:#fff}.application-section>header{display:flex;align-items:center;gap:10px;padding:13px 15px;border-bottom:1px solid #d5dde5;background:#f5f8fa}.application-section>header>span{width:32px;height:32px;display:grid;place-items:center;border-radius:50%;background:#12345b;color:#fff;font-size:9px;font-weight:900}.application-section h3{margin:0;color:#12345b;font-size:13px}.application-section header p{margin:3px 0 0;color:#607083;font-size:8px}.application-fields{display:grid;grid-template-columns:1fr 1fr;gap:0}.application-field{min-width:0;min-height:72px;padding:12px 14px;border-right:1px solid #e1e7ed;border-bottom:1px solid #e1e7ed}.application-field:nth-child(even){border-right:0}.application-field.wide{grid-column:1/-1;border-right:0}.application-field>span,.application-field>b{display:block}.application-field>span{margin-bottom:7px;color:#607083;font-size:8px;font-weight:800;text-transform:uppercase;letter-spacing:.025em}.application-field>span em{margin-left:3px;color:#b42318;font-style:normal}.application-field>b{color:#173451;font-size:11px;line-height:1.45;overflow-wrap:anywhere}.application-field input,.application-field textarea{width:100%;border:1px solid #9eafbf;border-radius:5px;background:#fff;color:#173451;padding:9px 10px;font:inherit;font-size:11px;outline:0}.application-field input{height:39px}.application-field textarea{resize:vertical;min-height:68px}.application-field input:focus,.application-field textarea:focus{border-color:#168447;box-shadow:0 0 0 3px #dff2e6}.application-form.editing .application-section{border-color:#9dafc1;box-shadow:0 5px 16px rgba(18,52,91,.06)}.application-save-bar{position:sticky;bottom:10px;z-index:3;display:grid;grid-template-columns:1fr auto auto;gap:10px;align-items:center;padding:13px 15px;border:1px solid #92a3b4;border-left:4px solid #168447;border-radius:7px;background:#fff;box-shadow:0 14px 35px rgba(18,52,91,.2)}.application-save-bar b,.application-save-bar span{display:block}.application-save-bar b{color:#12345b;font-size:10px}.application-save-bar span{margin-top:3px;color:#607083;font-size:8px;line-height:1.4}.application-cancel-btn{border:1px solid #8798aa;background:#fff;color:#12345b}.application-save-btn:disabled,.application-cancel-btn:disabled{cursor:not-allowed;opacity:.5}.application-audit{display:grid;grid-template-columns:1fr 220px 300px;align-items:center;gap:15px;padding:13px 15px;border:1px solid #b9c5d0;border-radius:6px;background:#f8fafc}.application-audit small,.application-audit>div>b{display:block}.application-audit small{color:#168447;font-size:8px;font-weight:900}.application-audit>div>b{margin-top:4px;color:#12345b;font-size:10px}.application-audit .info{padding-left:14px;border-left:1px solid #d2dbe4}.application-empty{max-width:720px;margin:35px auto;padding:45px;text-align:center;border:1px solid #b9c5d0;border-top:4px solid #168447;border-radius:7px;background:#fff}.application-empty-icon{width:64px;height:64px;display:grid;place-items:center;margin:0 auto 13px;border-radius:50%;background:#edf8f1;color:#166534;font-weight:900}.application-empty small{color:#168447;font-size:9px;font-weight:900}.application-empty h2{margin:7px 0;color:#12345b}.application-empty p{margin:0 auto 18px;color:#607083;font-size:11px;line-height:1.65}.application-empty button{min-width:190px}@media(max-width:1150px){.application-summary{grid-template-columns:repeat(3,1fr)}.application-summary .info:nth-child(3){border-right:0}.application-summary .info:nth-child(-n+3){border-bottom:1px solid #d5dde5}.application-form{grid-template-columns:1fr}.application-audit{grid-template-columns:1fr 1fr}.application-audit>div:first-child{grid-column:1/-1}}@media(max-width:700px){.application-hero{align-items:flex-start;flex-direction:column;padding:15px}.application-hero-actions{width:100%}.application-summary{grid-template-columns:1fr 1fr}.application-summary .info{border-bottom:1px solid #d5dde5}.application-summary .info:nth-child(2n){border-right:0}.application-summary .info:nth-last-child(-n+2){border-bottom:0}.application-fields{grid-template-columns:1fr}.application-field,.application-field:nth-child(even){grid-column:auto;border-right:0}.application-save-bar{position:static;grid-template-columns:1fr 1fr}.application-save-bar>div{grid-column:1/-1}.application-save-btn,.application-cancel-btn{width:100%}.application-audit{grid-template-columns:1fr}.application-audit>div:first-child{grid-column:auto}.application-audit .info{padding:10px 0 0;border-left:0;border-top:1px solid #d2dbe4}}
.application-field select{width:100%;height:39px;border:1px solid #9eafbf;border-radius:5px;background:#fff;color:#173451;padding:8px 10px;font:inherit;font-size:11px;outline:0}.application-field select:focus{border-color:#168447;box-shadow:0 0 0 3px #dff2e6}
.pacs-application-report-center{overflow:hidden;border:1px solid #9fb0c1;border-top:4px solid #12345b;border-radius:7px;background:#fff;box-shadow:0 8px 24px rgba(18,52,91,.07)}.pacs-application-report-center>header{display:flex;justify-content:space-between;align-items:center;gap:18px;padding:16px 18px;border-bottom:1px solid #cbd5df;background:linear-gradient(110deg,#f7fafc,#eef5fb)}.pacs-application-report-center>header small{color:#168447;font-size:8px;font-weight:900;letter-spacing:.08em}.pacs-application-report-center>header h3{margin:5px 0 3px;color:#12345b;font-size:17px}.pacs-application-report-center>header p{max-width:850px;margin:0;color:#607083;font-size:9px;line-height:1.55}.pacs-application-report-center>header>b{flex:0 0 auto;padding:8px 11px;border:1px solid #8fc3a1;border-radius:4px;background:#edf8f1;color:#166534;font-size:9px}.pacs-application-report-center>header>b.incomplete{border-color:#dfbd79;background:#fff8e8;color:#8a5b05}.pacs-report-download-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;padding:15px}.pacs-report-download-grid>article{padding:13px;border:1px solid #cbd5df;border-radius:6px;background:#fff}.pacs-report-language{display:flex;align-items:center;gap:10px;padding-bottom:11px;border-bottom:1px solid #e0e6ec}.pacs-report-language>i{width:34px;height:34px;display:grid;place-items:center;border-radius:50%;background:#12345b;color:#fff;font-size:9px;font-style:normal;font-weight:900}.pacs-report-language h4,.pacs-report-language span{display:block}.pacs-report-language h4{margin:0;color:#12345b;font-size:12px}.pacs-report-language span{margin-top:3px;color:#667588;font-size:8px;line-height:1.4}.pacs-report-actions{display:grid;grid-template-columns:1fr 1fr;gap:7px;margin-top:11px}.pacs-report-actions button{min-height:36px;border-radius:4px;padding:8px;font-size:8px;font-weight:900;cursor:pointer}.pacs-report-actions button:disabled{cursor:not-allowed;opacity:.45}.pacs-report-actions .report-download-word{grid-column:1/-1;border:1px solid #0d5b37;background:#168447;color:#fff}.pacs-report-actions .report-print-pdf{border:1px solid #12345b;background:#12345b;color:#fff}.pacs-report-actions .report-preview{border:1px solid #91a2b4;background:#fff;color:#12345b}.pacs-report-warning,.pacs-report-complete{margin:0 15px 15px;padding:10px 12px;border-radius:5px;font-size:9px;line-height:1.55}.pacs-report-warning{border:1px solid #dfbd79;border-left:4px solid #d0831b;background:#fff9ea;color:#74450c}.pacs-report-warning b,.pacs-report-warning span{display:block}.pacs-report-warning span{margin-top:3px}.pacs-report-complete{border:1px solid #9fcbb0;border-left:4px solid #168447;background:#edf8f1;color:#166534;font-weight:800}@media(max-width:1050px){.pacs-report-download-grid{grid-template-columns:1fr}.pacs-report-actions{grid-template-columns:1fr 1fr}}@media(max-width:700px){.pacs-application-report-center>header{align-items:flex-start;flex-direction:column}.pacs-report-download-grid{padding:11px}.pacs-report-actions{grid-template-columns:1fr}.pacs-report-actions .report-download-word{grid-column:auto}.pacs-report-warning,.pacs-report-complete{margin:0 11px 11px}}
.application-document-register{padding:15px;border:1px solid #b9c5d0;border-radius:7px;background:#fff}.application-document-register>header{display:flex;justify-content:space-between;align-items:center;gap:15px;margin-bottom:12px;padding-bottom:12px;border-bottom:1px solid #d5dde5}.application-document-register small{color:#168447;font-size:8px;font-weight:900;letter-spacing:.06em}.application-document-register h3{margin:4px 0;color:#12345b;font-size:14px}.application-document-register p{margin:0;color:#607083;font-size:9px}.application-document-register>header>b{padding:7px 10px;border:1px solid #a9cdb5;border-radius:4px;background:#edf6f0;color:#166534;font-size:9px;white-space:nowrap}@media(max-width:700px){.application-document-register>header{align-items:flex-start;flex-direction:column}}
`;
